/**
 * 🗺️ MAPA PAGE - Editor VSM
 * 
 * Renderiza apenas o conteúdo do editor VSM:
 * - MapaRibbon (Ribbon) - Toolbar com ferramentas VSM
 * - MapaCanvas (VsmCanvas) - Área de desenho do mapa
 * - LeanOfficeStatusBar - Sobreposta no canvas (métricas)
 * 
 * IMPORTANTE:
 * - Usa o WorkspaceLayout global (sidebar + topbar únicos)
 * - Ribbon fica dentro do shell compartilhado
 * - Renderiza apenas o conteúdo do canvas no main content
 */

import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { VsmCanvas } from '../components/VsmCanvas';
import { MiniMapHUD } from '../components/MiniMapHUD';
import type { V186EngineeringTool } from '../components/VsmCanvas/types';
import { Ribbon } from '../components/Ribbon';
import { FactorySettingsModal } from '../components/FactorySettingsModal';
import { LeanOfficeStatusBar } from '../components/LeanOfficeStatusBar';
import { JourneyPanel } from '../components/JourneyPanel';

import { ViewModeSwitcher, ViewMode } from '../components/ViewModeSwitcher';
import OfficeSidebar from '../components/OfficeSidebar';
import { CanvasRuler } from '../components/CanvasRuler';
import { useTheme } from '../context/ThemeContext';
import { RightPanel } from '../components/RightPanel';
import { YamazumiSimulator, buildInitialYamazumiAssignments, buildInitialYamazumiFteByRole, buildYamazumiColumns, UNASSIGNED_ROLE_ID, getCycleTimeMinutes, simularDemanda, calcularLeadTime, detectarGargalo } from '../components/YamazumiSimulator';
import { WorkspaceModeProvider, useWorkspaceMode } from '../context/WorkspaceModeContext';
import { SimulationProvider } from '../context/SimulationContext';
import { useProjectStore } from '../store/useProjectStore';
import { ModuleLayout } from '../components/layout/ModuleLayout';
import { Action, CardData, PostIt, PostItType, Position, PageConnector, ProjectStage, Connection, Project } from '../types';
import { recalculateFlow, generateId, generateNextSequentialNumber } from '../utils/vsmGeometry';
import { getCardDimensions } from '../components/Card/cardUtils';
import { useEdgeCalculations } from '../hooks/useEdgeCalculations';
import { useHistory } from '../hooks/useHistory';
import { VisualSettingsModal } from '../components/VisualSettingsModal';
import { ProjectSettingsModal } from '../components/ProjectSettingsModal';
import OrgManager from '../components/OrgManager';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../hooks/usePermission';
import { useProjectContext } from '../context/ProjectContext';
import { supabase } from '../lib/supabaseClient';
import { Loader2, Building2, Factory, X, ChevronDown } from 'lucide-react';
import { MetricsSummary } from '../components/MetricsSummary';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { saveProjectToDisk, loadProjectFromDisk } from '../utils/fileSystem';
import { exportService } from '../services/exportService';
import { buildSyncActivities, syncYamazumiToTasks, calcularKPIs } from '../services/yamazumiSyncService';
import { ActionService, type EnterpriseAction } from '../services/actionService';
import { ProjectPlanService } from '../services/projectPlanService';
import { ProjectService } from '../services/projectService';
import { useYamazumiReal } from '../hooks/useYamazumiReal';
import { KPIBar } from '../components/KPIBar';
import { downloadTemplate } from '../utils/downloadTemplate';
import { importFromExcel } from '../utils/importExcel';
import { exportCanvasToA3 } from '../utils/exportA3';
import { generateLeanReport } from '../utils/leanReport';
import SimulationSidebar from '../components/SimulationSidebar';
import { useAppToast } from '../components/AppToast';
import { mapStrategyEngine } from './mapStrategyEngine';
import { snapToNearestLane, SwimLaneInfo } from '../utils/autoLayoutEngine';
import { useWorkflowLiveMetrics } from '../hooks/useWorkflowLiveMetrics';
import { MapWorkflowComparePanel } from '../components/MapWorkflowComparePanel';
import { useReportStudio } from '../context/ReportContext';
import { LeadTimeLadderFooter } from '../components/LeadTimeLadder';
import { CANCELLED_PROGRAM_INFO_BOX_IDS, isCancelledProgramInfoCard, shouldRemoveCancelledProgramInfoConnection } from '../utils/vsmCommunicationCleanup';

const MAP_SESSION_SNAPSHOT_KEY = 'wf_active_map_session_snapshot';

const VSM_CONNECTOR_DRAG_TYPES = new Set([
  'vsm_empurrado',
  'vsm_puxado',
  'vsm_info_manual',
  'vsm_info_eletro',
]);

const VSM_VERTICAL_FLOW_WIDTH = 32;  // bbox tight match com o desenho da seta
const VSM_VERTICAL_FLOW_DEFAULT_HEIGHT = 162; // 108 * 1.5
const VSM_VERTICAL_FLOW_DOCK_GAP = 40;        // breathing room top/bottom
const VSM_TRANSPORT_DOCK_GAP_X = -12;          // negative: truck overlaps arrow edge slightly
const isVsmVerticalFlowCard = (card: CardData) => (card as any).vsmIcon === 'vertical_flow';
const isVsmTransportDrag = (type: string) => ['vsm_caminhao', 'vsm_transporte', 'vsm_transport'].includes(type);
const isVsmInventoryDrag = (type: string) => ['vsm_estoque', 'vsm_pulmao', 'vsm_supermercado', 'vsm_fifo', 'vsm_emergencia'].includes(type);
const isVerticalLogisticsAnchor = (card: CardData) => isVsmVerticalFlowCard(card) || ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '');
const isFactoryProcessCard = (card: CardData) => {
  const icon = String((card as any).vsmIcon ?? '');
  const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
  return card.tipo === 'atividade' && (icon === 'processo' || /solda\s*(i|ii)\b|estamparia|montagem|expedi[cç][aã]o/i.test(label));
};
const isFactoryPcpmCard = (card: CardData) => {
  const icon = String((card as any).vsmIcon ?? '');
  const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
  return card.tipo === 'atividade' && (['pcpm', 'nivelamento', 'info_eletro'].includes(icon) || /pcpm|mrp/i.test(label));
};
// V224: identifica o card de Expedição (alvo lógico da retirada kanban quando MRP está bloqueado)
const isFactoryExpeditionCard = (card: CardData) => {
  if ((card as any).vsmSubtype === 'expedition') return true;
  const label = String((card as any).atividade ?? (card as any).vsmNome ?? (card as any).titulo ?? (card as any).title ?? '');
  return /expedi[cç][aã]o/i.test(label);
};
const isFactoryExternalCard = (card: CardData) => {
  const icon = (card as any).vsmIcon ?? '';
  return ['externo', 'fornecedor', 'cliente'].includes(icon) || ['card_inicial', 'card_final'].includes(card.tipo);
};

const VSM_ENTITY_VISUAL_CENTER_NUDGE_X = 6;
const getEntityRenderOffsetX = (card: CardData) => {
  const isEntity = ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '');
  if (!isEntity) return 0;
  return card.tipo === 'card_inicial' && card.layoutType === 'classic' ? 30 : 0;
};
const getEntityRenderedWidth = (card: CardData) => {
  const isEntity = ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '');
  if (!isEntity) return getVsmCardBounds(card).width;
  if (card.tipo === 'card_inicial') return 220;
  if (card.tipo === 'card_final') return card.layoutType === 'classic' ? 220 : 127;
  return 127;
};
const getLogisticsCenterX = (card: CardData) => {
  const isEntityCard = ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '')
    && (card.tipo === 'card_inicial' || card.tipo === 'card_final');
  if (isEntityCard) {
    return card.posicao.x + getEntityRenderOffsetX(card) + getEntityRenderedWidth(card) / 2 + VSM_ENTITY_VISUAL_CENTER_NUDGE_X;
  }
  const bounds = getVsmCardBounds(card);
  return bounds.centerX;
};

const getVsmCardBounds = (card: CardData) => {
  const dims = getCardDimensions(card, card.tipo === 'decisao', false);
  const width = card.tipo === 'decisao' ? dims.decisionSize : dims.width;
  const height = card.tipo === 'decisao' ? dims.decisionSize : dims.height;
  return {
    x: card.posicao.x,
    y: card.posicao.y,
    width,
    height,
    centerX: card.posicao.x + width / 2,
    centerY: card.posicao.y + height / 2,
    top: card.posicao.y,
    bottom: card.posicao.y + height,
  };
};

const findNearestConnectionPoint = (
  connections: Array<{
    sourceId?: string;
    targetId?: string;
    connectionId?: string;
    sourcePoint?: Position;
    targetPoint?: Position;
  }>,
  x: number,
  y: number,
  threshold = 34,
) => {
  let best: { sourceId: string; targetId: string; connectionId: string; x: number; y: number; distance: number } | null = null;

  for (const conn of connections) {
    if (!conn.sourceId || !conn.targetId || !conn.connectionId || !conn.sourcePoint || !conn.targetPoint) continue;
    const dx = conn.targetPoint.x - conn.sourcePoint.x;
    const dy = conn.targetPoint.y - conn.sourcePoint.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) continue;
    const t = Math.max(0, Math.min(1, ((x - conn.sourcePoint.x) * dx + (y - conn.sourcePoint.y) * dy) / lenSq));
    const closestX = conn.sourcePoint.x + t * dx;
    const closestY = conn.sourcePoint.y + t * dy;
    const distance = Math.hypot(x - closestX, y - closestY);
    if (distance <= threshold && (!best || distance < best.distance)) {
      best = { sourceId: conn.sourceId, targetId: conn.targetId, connectionId: conn.connectionId, x: closestX, y: closestY, distance };
    }
  }

  return best;
};

const findConnectionPointForBounds = (
  connections: Array<{
    sourceId?: string;
    targetId?: string;
    connectionId?: string;
    sourcePoint?: Position;
    targetPoint?: Position;
  }>,
  bounds: ReturnType<typeof getVsmCardBounds>,
  threshold = 18,
) => {
  let best: { sourceId: string; targetId: string; connectionId: string; x: number; y: number; distance: number } | null = null;
  const centerX = bounds.centerX;
  const centerY = bounds.centerY;

  for (const conn of connections) {
    if (!conn.sourceId || !conn.targetId || !conn.connectionId || !conn.sourcePoint || !conn.targetPoint) continue;
    const minX = Math.min(conn.sourcePoint.x, conn.targetPoint.x);
    const maxX = Math.max(conn.sourcePoint.x, conn.targetPoint.x);
    const minY = Math.min(conn.sourcePoint.y, conn.targetPoint.y);
    const maxY = Math.max(conn.sourcePoint.y, conn.targetPoint.y);
    const crossesX = centerX >= minX - threshold && centerX <= maxX + threshold;
    const crossesY = maxY >= bounds.top - threshold && minY <= bounds.bottom + threshold;
    if (!crossesX || !crossesY) continue;

    const dx = conn.targetPoint.x - conn.sourcePoint.x;
    const dy = conn.targetPoint.y - conn.sourcePoint.y;
    const lenSq = dx * dx + dy * dy;
    if (lenSq === 0) continue;
    const t = Math.max(0, Math.min(1, ((centerX - conn.sourcePoint.x) * dx + (centerY - conn.sourcePoint.y) * dy) / lenSq));
    const closestX = conn.sourcePoint.x + t * dx;
    const closestY = conn.sourcePoint.y + t * dy;
    const verticalDistance = closestY < bounds.top ? bounds.top - closestY : closestY > bounds.bottom ? closestY - bounds.bottom : 0;
    const centerDistance = Math.hypot(centerX - closestX, centerY - closestY);
    const distance = verticalDistance * 10 + centerDistance;
    if (!best || distance < best.distance) {
      best = { sourceId: conn.sourceId, targetId: conn.targetId, connectionId: conn.connectionId, x: closestX, y: closestY, distance };
    }
  }

  return best;
};

const findConnectionPoint = (
  connections: Array<{
    sourceId?: string;
    targetId?: string;
    connectionId?: string;
    sourcePoint?: Position;
    targetPoint?: Position;
  }>,
  sourceId: string,
  targetId: string,
  connectionId: string,
  x: number,
) => {
  const conn = connections.find(item =>
    item.sourceId === sourceId &&
    item.targetId === targetId &&
    item.connectionId === connectionId &&
    item.sourcePoint &&
    item.targetPoint
  );
  if (!conn?.sourcePoint || !conn.targetPoint) return null;
  const minX = Math.min(conn.sourcePoint.x, conn.targetPoint.x);
  const maxX = Math.max(conn.sourcePoint.x, conn.targetPoint.x);
  const clampedX = Math.max(minX, Math.min(maxX, x));
  const dx = conn.targetPoint.x - conn.sourcePoint.x;
  const dy = conn.targetPoint.y - conn.sourcePoint.y;
  const t = dx === 0 ? 0 : (clampedX - conn.sourcePoint.x) / dx;
  return {
    sourceId: conn.sourceId!,
    targetId: conn.targetId!,
    connectionId: conn.connectionId!,
    x: clampedX,
    y: conn.sourcePoint.y + dy * t,
    distance: 0,
  };
};

const findVerticalFlowAnchors = (cards: CardData[], x: number, y: number) => {
  const source = cards
    .filter(card => ['card_inicial', 'card_final'].includes(card.tipo) || ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? ''))
    .filter(card => getVsmCardBounds(card).bottom <= y + 40)
    .sort((a, b) => {
      const ab = getVsmCardBounds(a);
      const bb = getVsmCardBounds(b);
      return Math.abs(ab.centerX - x) + Math.abs(ab.bottom - y) - (Math.abs(bb.centerX - x) + Math.abs(bb.bottom - y));
    })[0];

  const target = cards
    .filter(card => card.tipo === 'inventory' || ['estoque', 'pulmao', 'supermercado'].includes((card as any).vsmIcon ?? ''))
    .filter(card => getVsmCardBounds(card).top >= y - 40)
    .sort((a, b) => {
      const ab = getVsmCardBounds(a);
      const bb = getVsmCardBounds(b);
      return Math.abs(ab.centerX - x) + Math.abs(ab.top - y) - (Math.abs(bb.centerX - x) + Math.abs(bb.top - y));
    })[0];

  return { source, target };
};

const buildVerticalFlowGeometry = (flow: CardData, cards: CardData[]) => {
  const source = (flow as any).vsmFlowSourceId ? cards.find(card => card.id === (flow as any).vsmFlowSourceId) : undefined;
  const target = (flow as any).vsmFlowTargetId ? cards.find(card => card.id === (flow as any).vsmFlowTargetId) : undefined;
  const sourceBounds = source ? getVsmCardBounds(source) : null;
  const targetBounds = target ? getVsmCardBounds(target) : null;
  const manualX = typeof (flow as any).vsmFlowManualX === 'number' ? Math.round((flow as any).vsmFlowManualX) : null;
  const manualY = typeof (flow as any).vsmFlowManualY === 'number' ? Math.round((flow as any).vsmFlowManualY) : null;

  if (!sourceBounds || !targetBounds) {
    const height = Math.max(80, Number((flow as any).vsmFlowHeight) || VSM_VERTICAL_FLOW_DEFAULT_HEIGHT);
    return { x: manualX ?? flow.posicao.x, y: manualY ?? flow.posicao.y, height };
  }

  const centerX = Math.round(getLogisticsCenterX(source!));
  const autoY = Math.round(sourceBounds.bottom + VSM_VERTICAL_FLOW_DOCK_GAP);
  const targetTop = Math.round(targetBounds.top - VSM_VERTICAL_FLOW_DOCK_GAP);
  const autoHeight = Math.max(80, targetTop - autoY);

  // Centralizar a combinação seta + caminhão sob a entidade quando houver dock de transporte.
  // VERTICAL_FLOW_WIDTH (32) + DOCK_GAP_X (-12) + dock card (110) = 130 wide. Centro = flow.x + 65.
  const hasDockedTransport = cards.some(c =>
    (c as any).transportDockParentId === flow.id &&
    ['caminhao', 'transporte'].includes((c as any).vsmIcon ?? '')
  );
  const autoX = hasDockedTransport
    ? Math.round(centerX - 65)
    : Math.round(centerX - VSM_VERTICAL_FLOW_WIDTH / 2);

  // Quando manualY está definido, manter altura preservada (não recalcular pela geometria entidade→target).
  const height = manualY !== null
    ? Math.max(80, Number((flow as any).vsmFlowHeight) || autoHeight)
    : autoHeight;

  return { x: manualX ?? autoX, y: manualY ?? autoY, height };
};

const buildTransportDockPosition = (flow: CardData) => {
  const height = Math.max(80, Number((flow as any).vsmFlowHeight) || VSM_VERTICAL_FLOW_DEFAULT_HEIGHT);
  return {
    x: Math.round(flow.posicao.x + VSM_VERTICAL_FLOW_WIDTH + VSM_TRANSPORT_DOCK_GAP_X),
    y: Math.round(flow.posicao.y + Math.max(16, height * 0.28)),
  };
};

const buildCenteredBelowPosition = (anchor: CardData, child: CardData, fallbackY?: number) => {
  const anchorBounds = getVsmCardBounds(anchor);
  const childBounds = getVsmCardBounds(child);
  return {
    x: Math.round(getLogisticsCenterX(anchor) - childBounds.width / 2),
    y: Math.round(fallbackY ?? anchorBounds.bottom + VSM_VERTICAL_FLOW_DOCK_GAP),
  };
};

// Returns the nearest entity (externo/fornecedor/cliente) to the drop X axis.
// No Y-filtering: if there is any entity on the canvas, inventory should center under it.
const findEntityAnchorAbove = (cards: CardData[], x: number, _y: number): CardData | undefined => {
  const entities = cards.filter(card =>
    ['externo', 'fornecedor', 'cliente'].includes((card as any).vsmIcon ?? '')
  );
  if (entities.length === 0) return undefined;
  return entities.sort((a, b) =>
    Math.abs(getLogisticsCenterX(a) - x) - Math.abs(getLogisticsCenterX(b) - x)
  )[0];
};

const findVerticalLogisticsAnchorAbove = (cards: CardData[], x: number, y: number) => {
  // Entity cards get absolute priority over vertical flow cards.
  const entity = findEntityAnchorAbove(cards, x, y);
  if (entity) return entity;
  return cards
    .filter(isVsmVerticalFlowCard)
    .filter(card => getVsmCardBounds(card).bottom <= y + 20)
    .filter(card => Math.abs(getLogisticsCenterX(card) - x) <= 200)
    .sort((a, b) => {
      const ab = getVsmCardBounds(a);
      const bb = getVsmCardBounds(b);
      return Math.abs(getLogisticsCenterX(a) - x) + Math.abs(ab.bottom - y) - (Math.abs(getLogisticsCenterX(b) - x) + Math.abs(bb.bottom - y));
    })[0];
};

// ============================================================================
// SYNC — propagate card executionConfig to workflow stages on save
// ============================================================================

async function syncCardConfigToStages(
  cards: CardData[],
  blueprintId: string,
  organizationId: string,
): Promise<void> {
  console.log('[syncCardConfigToStages] Starting, blueprintId:', blueprintId);
  // 1. Find the template linked to this blueprint (two paths)
  let templateId: string | null = null;

  // Path A: process_blueprints.generated_template_id
  const { data: bp } = await supabase
    .from('process_blueprints')
    .select('generated_template_id')
    .eq('id', blueprintId)
    .eq('organization_id', organizationId)
    .maybeSingle();
  templateId = bp?.generated_template_id ?? null;

  // Path B: workflow_templates_v9.blueprint_id (reverse link)
  if (!templateId) {
    const { data: tpl } = await supabase
      .from('workflow_templates_v9')
      .select('id')
      .eq('blueprint_id', blueprintId)
      .eq('organization_id', organizationId)
      .limit(1)
      .maybeSingle();
    templateId = tpl?.id ?? null;
  }

  // Path C: process_blueprints.source_id (blueprint generated from template)
  if (!templateId) {
    const { data: bp2 } = await supabase
      .from('process_blueprints')
      .select('source_id, source_type')
      .eq('id', blueprintId)
      .eq('organization_id', organizationId)
      .maybeSingle();
    if (bp2?.source_type === 'workflow' && bp2.source_id) {
      templateId = bp2.source_id;
    }
  }

  // 2. Build card map by name (stage.name matches card.atividade)
  const cardByName = new Map<string, CardData>();
  for (const card of cards) {
    if (card.tipo === 'atividade' && card.status === 'ativo') {
      const key = (card.atividade || card.nome_fluxo || '').trim().toLowerCase();
      if (key) cardByName.set(key, card);
    }
  }
  const cardKeys = Array.from(cardByName.keys());
  console.log('[syncCardConfigToStages] cardByName keys:', cardKeys);
  console.log('[syncCardConfigToStages] cards with executionConfig:', Array.from(cardByName.entries()).filter(([,c]) => c.executionConfig).map(([k,c]) => ({ key: k, instruction: c.executionConfig?.instruction?.substring(0, 50) })));

  // 2b. Load stages — first try from templateId, if no name match try finding the right template
  let stages: { id: string; name: string; metadata: unknown }[] | null = null;

  if (templateId) {
    const { data } = await supabase
      .from('workflow_stages_v9')
      .select('id, name, metadata')
      .eq('template_id', templateId)
      .eq('organization_id', organizationId);
    stages = data;
  }

  // Check if stages actually match any cards
  const stagesMatch = stages?.some(s => cardByName.has((s.name || '').trim().toLowerCase()));

  // Path D: If no templateId or stages don't match card names, find template by matching stage names
  if (!stages || stages.length === 0 || !stagesMatch) {
    console.log('[syncCardConfigToStages] Template stages do not match cards, searching by stage names...');
    const { data: allStages } = await supabase
      .from('workflow_stages_v9')
      .select('id, name, metadata, template_id')
      .eq('organization_id', organizationId);
    if (allStages) {
      // Group by template_id, find the template with most card name matches
      const byTemplate = new Map<string, typeof allStages>();
      for (const s of allStages) {
        if (!byTemplate.has(s.template_id)) byTemplate.set(s.template_id, []);
        byTemplate.get(s.template_id)!.push(s);
      }
      let bestTemplateId = '';
      let bestCount = 0;
      for (const [tplId, tplStages] of byTemplate) {
        const matchCount = tplStages.filter(s => cardByName.has((s.name || '').trim().toLowerCase())).length;
        if (matchCount > bestCount) {
          bestCount = matchCount;
          bestTemplateId = tplId;
        }
      }
      if (bestCount > 0) {
        stages = byTemplate.get(bestTemplateId)!;
        console.log('[syncCardConfigToStages] Found matching template via stage names, matches:', bestCount);
      }
    }
  }

  if (!stages || stages.length === 0) return;
  console.log('[syncCardConfigToStages] stages:', stages.map(s => s.name));

  // 4. Update stages that have matching cards with executionConfig
  for (const stage of stages) {
    const stageKey = (stage.name || '').trim().toLowerCase();
    const card = cardByName.get(stageKey);
    if (!card?.executionConfig) {
      console.log('[syncCardConfigToStages] Skipping stage (no executionConfig):', stageKey, 'card found:', !!card);
      continue;
    }
    console.log('[syncCardConfigToStages] Updating stage:', stageKey, 'with instruction:', card.executionConfig.instruction?.substring(0, 50));

    const meta = (stage.metadata as Record<string, unknown>) ?? {};
    const ec = card.executionConfig;

    await supabase
      .from('workflow_stages_v9')
      .update({
        department_id: (card as any).department_id ?? meta.department_id ?? null,
        metadata: {
          ...meta,
          instruction: ec.instruction ?? null,
          executionConfig: ec,
          department_id: (card as any).department_id ?? meta.department_id ?? null,
          department_name: (card as any).setor || (card as any).sector || meta.department_name || null,
        },
      })
      .eq('id', stage.id)
      .eq('organization_id', organizationId);
  }
}

// 📐 Constantes de dimensão do layout
const RULER_SIZE = 20; // px - dimensão uniforme das réguas
const RULER_ALIGNMENT_OFFSET = 0;
const TOOL_RAIL_WIDTH = 40;
const CANVAS_CONTROLS_GAP_PX = 8;
const CANVAS_CONTROLS_INSET_PX = 16;

/**
 * MapaPage - Página do Editor VSM
 */

// Constante para evitar nova referência de array vazio a cada render
const EMPTY_CARDS_ARRAY: CardData[] = [];

type RightPanelMode = 'none' | 'properties' | 'dashboard' | 'kaizen' | 'yamazumi' | 'simulation' | 'compare';

interface DashboardSimulationSuggestion {
  id: number;
  focusResource?: string;
  focusCardId?: string;
  suggestedExtraFte?: number;
  suggestedWaitReductionPercent?: number;
}

/* ════════════════════════════════════════════════════════════════
 * VSM LIFECYCLE — status vocabulary aligned to Lean/TPS
 * ════════════════════════════════════════════════════════════════ */
export type VsmStatusValue =
  | 'draft' | 'current'           // Estado Atual
  | 'kaizen' | 'transition'       // Em Kaizen
  | 'future'                      // Estado Futuro
  | 'published' | 'standard'      // Padrão Homologado
  | 'archived';

interface VsmStatusMeta {
  label: string;
  tone: string;
}

function getVsmStatusMeta(status?: string | null, isStandard?: boolean): VsmStatusMeta {
  if (isStandard || status === 'standard' || status === 'published') {
    return {
      label: '⭐ Padrão Homologado',
      tone: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-700/70 dark:bg-violet-900/25 dark:text-violet-300',
    };
  }
  if (status === 'future') {
    return {
      label: '🎯 Estado Futuro',
      tone: 'border-emerald-300 bg-emerald-50 text-emerald-700 dark:border-emerald-700/70 dark:bg-emerald-900/25 dark:text-emerald-300',
    };
  }
  if (status === 'kaizen' || status === 'transition') {
    return {
      label: '⚡ Cenário Kaizen',
      tone: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-700/70 dark:bg-amber-900/25 dark:text-amber-300',
    };
  }
  // draft | current | null | unknown → Estado Atual
  return {
    label: '📊 Estado Atual',
    tone: 'border-blue-200 bg-blue-50/60 text-blue-700 dark:border-blue-800/70 dark:bg-blue-900/20 dark:text-blue-300',
  };
}

const VSM_STATUS_OPTIONS: Array<{ value: VsmStatusValue; description: string }> = [
  { value: 'current',  description: 'Mapa do fluxo como ele opera hoje' },
  { value: 'kaizen',   description: 'Mapa em processo de melhoria ativa' },
  { value: 'future',   description: 'Fluxo ideal projetado após as melhorias' },
  { value: 'standard', description: 'Padrão corporativo aprovado e homologado' },
];

interface VsmStatusBadgeProps {
  status: string;
  isStandard?: boolean;
  canEdit: boolean;
  onChangeStatus: (s: VsmStatusValue) => Promise<void>;
}

const VsmStatusBadge: React.FC<VsmStatusBadgeProps> = ({ status, isStandard, canEdit, onChangeStatus }) => {
  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const wrapRef = React.useRef<HTMLDivElement>(null);
  const meta = getVsmStatusMeta(status, isStandard);

  React.useEffect(() => {
    if (!open) return;
    const onOutside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [open]);

  const handleSelect = async (value: VsmStatusValue) => {
    if (busy) return;
    setBusy(true);
    setOpen(false);
    try { await onChangeStatus(value); } finally { setBusy(false); }
  };

  return (
    <div ref={wrapRef} className="relative shrink-0">
      <button
        type="button"
        onClick={() => canEdit && setOpen(v => !v)}
        disabled={busy}
        title={canEdit ? 'Clique para alterar o ciclo de vida do mapa' : meta.label}
        className={`inline-flex items-center gap-1 rounded-[3px] border px-1.5 py-[2px] text-[10px] font-medium tracking-[0.03em] transition-opacity ${meta.tone} ${canEdit ? 'cursor-pointer hover:opacity-80' : 'cursor-default'} ${busy ? 'opacity-50' : ''}`}
      >
        {meta.label}
        {canEdit && <ChevronDown size={9} className="opacity-50" />}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-[200] min-w-[220px] rounded-md border border-[var(--border-default)] bg-[var(--bg-elevated)] shadow-xl py-1">
          {VSM_STATUS_OPTIONS.map(opt => {
            const optMeta = getVsmStatusMeta(opt.value);
            const isActive =
              status === opt.value ||
              (opt.value === 'standard' && isStandard) ||
              (opt.value === 'current' && (status === 'draft' || !status));
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => void handleSelect(opt.value)}
                className={`w-full flex flex-col items-start gap-0.5 px-3 py-2 text-left transition-colors hover:bg-[var(--button-hover)] ${isActive ? 'bg-[var(--bg-surface-3)]' : ''}`}
              >
                <span className={`text-[11px] font-semibold ${optMeta.tone.split(' ').find(c => c.startsWith('text-')) ?? ''}`}>
                  {optMeta.label}
                </span>
                <span className="text-[10px] text-[var(--text-muted)]">{opt.description}</span>
                {isActive && <span className="text-[9px] text-[var(--text-muted)]">&#x2022; status atual</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

interface BlueprintHeaderSnapshot {
  name: string;
  status: VsmStatusValue;
  versionSemver: string;
  updatedAt: string;
  isCorporateStandard: boolean;
}

function formatTechnicalDate(value?: string | null): string {
  if (!value) return 'Sem registro';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Sem registro';
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(parsed);
}


function formatStageHeaderLabel(value: string): string {
  return value.replace(': ', ' - ');
}

function formatCompactDuration(minutes: number): string {
  const safe = Math.max(0, minutes || 0);
  if (safe >= 1440) return `${(safe / 1440).toFixed(1)} d`;
  if (safe >= 60) return `${(safe / 60).toFixed(1)} h`;
  return `${safe.toFixed(0)} min`;
}

function formatCompactCurrency(value: number): string {
  return value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
}

const CANVAS_EMPTY_MODES = {
  ATUAL: {
    badge: 'Estado Atual',
    title: 'Adicione um card inicial',
    sub: 'ou importe um padrão existente.',
    color: 'rgba(15,23,42,0.30)',
    badgeColor: 'rgba(15,23,42,0.15)',
  },
  KAIZEN: {
    badge: 'Modo Oportunidades',
    title: 'Mapeie melhorias aqui',
    sub: 'Adicione um card inicial ou importe um padrão existente.',
    color: 'rgba(180,120,0,0.55)',
    badgeColor: 'rgba(245,158,11,0.18)',
  },
  FUTURO: {
    badge: 'Estado Futuro',
    title: 'Projete o processo ideal',
    sub: 'Adicione um card inicial ou importe um padrão existente.',
    color: 'rgba(16,120,80,0.55)',
    badgeColor: 'rgba(52,211,153,0.18)',
  },
} as const;

const CanvasEmptyState: React.FC = () => {
  const { mode } = useWorkspaceMode();
  const { isDark } = useTheme();
  const cfg = CANVAS_EMPTY_MODES[mode];

  // ATUAL uses neutral tones that must adapt to light/dark/black canvas
  const atualColor = isDark ? 'rgba(248,250,252,0.38)' : 'rgba(15,23,42,0.32)';
  const atualBadge = isDark ? 'rgba(248,250,252,0.10)' : 'rgba(15,23,42,0.08)';

  const color      = mode === 'ATUAL' ? atualColor : cfg.color;
  const badgeColor = mode === 'ATUAL' ? atualBadge : cfg.badgeColor;

  return (
    <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center">
      <div className="flex flex-col items-center gap-2 text-center" style={{ color }}>
        <div
          style={{
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            padding: '2px 8px',
            background: badgeColor,
            borderRadius: '2px',
            marginBottom: '2px',
          }}
        >
          {cfg.badge}
        </div>
        <div style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.04em' }}>
          {cfg.title}
        </div>
        <div style={{ fontSize: '11px', fontWeight: 400, opacity: 0.78 }}>
          {cfg.sub}
        </div>
      </div>
    </div>
  );
};

const CanvasModeBorderOverlay: React.FC<{ inset: number }> = ({ inset }) => {
  const { mode } = useWorkspaceMode();
  const isKaizen = mode === 'KAIZEN';
  const isFuturo = mode === 'FUTURO';
  const isActive = isKaizen || isFuturo;
  // Amber for KAIZEN (Oportunidades), emerald for FUTURO
  const rgb = isKaizen ? '245,158,11' : isFuturo ? '52,211,153' : '0,0,0';
  return (
    <div
      style={{
        position: 'absolute',
        top: inset,
        left: inset,
        right: 0,
        bottom: 0,
        pointerEvents: 'none',
        zIndex: 45,
        opacity: isActive ? 1 : 0,
        transition: 'opacity 0.25s ease, box-shadow 0.25s ease',
        boxShadow: isActive
          ? [
              `inset 0 0 0 1px rgba(${rgb},0.40)`,
              `inset 0 0 48px rgba(${rgb},0.055)`,
            ].join(',')
          : 'none',
      }}
    />
  );
};

const MapaPageInner: React.FC = () => {
  const { mode } = useWorkspaceMode();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast, ToastContainer } = useAppToast();
  const { openReport } = useReportStudio();
  const { isDark, theme } = useTheme();
  const rulerBg = 'var(--map-ruler-bg)';
  const fileInputRef = useRef<HTMLInputElement>(null);
  // ============================================================================
  // ZUSTAND STORE — seletores atômicos (evita re-render em mutações alheias)
  // ============================================================================
  const setCards = useProjectStore((s) => s.setCards);
  const addCard = useProjectStore((s) => s.addCard);
  const postIts = useProjectStore((s) => s.postIts);
  const setPostIts = useProjectStore((s) => s.setPostIts);
  const actions = useProjectStore((s) => s.actions);
  const setActions = useProjectStore((s) => s.setActions);
  const selectedCardId = useProjectStore((s) => s.selectedCardId);
  const setSelectedCardId = useProjectStore((s) => s.setSelectedCardId);
  const updateCardData = useProjectStore((s) => s.updateCardData);
  const updatePostIt = useProjectStore((s) => s.updatePostIt);
  const metrics = useProjectStore((s) => s.metrics);
  const heatmapMode = useProjectStore((s) => s.heatmapMode);
  const setHeatmapMode = useProjectStore((s) => s.setHeatmapMode);
  const recalculateFlowVolumes = useProjectStore((s) => s.recalculateFlowVolumes);
  const getCardsWithSimulation = useProjectStore((s) => s.getCardsWithSimulation);
  const createSimulationBaseline = useProjectStore((s) => s.createSimulationBaseline);
  const resetToBaseline = useProjectStore((s) => s.resetToBaseline);
  const hasFutureMap = useProjectStore((s) => s.hasFutureMap);
  const simulateFutureState = useProjectStore((s) => s.simulateFutureState);
  const optimizationSession = useProjectStore((s) => s.optimizationSession);
  const optimizationLayer = useProjectStore((s) => s.optimizationLayer);
  const setOptimizationLayer = useProjectStore((s) => s.setOptimizationLayer);
  
  // Usar seletores Zustand para obter dados reativos
  const getCards = useProjectStore((state) => state.getCards);
  const isKaizenMode = useProjectStore((state) => state.isKaizenMode);
  const organization = useProjectStore((state) => state.organization);
  const mapType = useProjectStore((state) => state.mapType);
  const consultingMode = useProjectStore((state) => state.consultingMode);
  const setConsultingMode = useProjectStore((state) => state.setConsultingMode);
  const journeyDeviationAlert = useProjectStore((state) => state.journeyDeviationAlert);
  const setMapType = useProjectStore((state) => state.setMapType);
  const vsmDesignStyle = useProjectStore((state) => state.vsmDesignStyle);
  const setVsmDesignStyle = useProjectStore((state) => state.setVsmDesignStyle);
  const vsmCardColor = useProjectStore((state) => state.vsmCardColor);
  const setVsmCardColor = useProjectStore((state) => state.setVsmCardColor);
  
  // Seletor com referência estável para array vazio
  const cards = useProjectStore((state) => {
    const activeSheet = state.sheets.find(s => s.id === state.activeSheetId);
    return activeSheet?.cards ?? EMPTY_CARDS_ARRAY;
  });

  // ISOLAMENTO DE ESTADO - Mudanca de contexto
  useEffect(() => {
    setSelectedCardId(null);
    setSelectedCardIds(new Set());
    setSelectedConnection(null);
    setBoxSelection(null);
    setHighlightedPath(null);
    setSelectedPostItId(null);
    setSelectedPostItIds(new Set());
    setManualArrowMode(false);
    setManualArrowSource(null);
    setManualArrowWaypoints([]);
    setConnectingSource(null);
  }, [mapType, setSelectedCardId]);

  // ============================================================================
  // AUTO-NUMBERING: assign numeroSequencial to cards loaded without it
  // ============================================================================
  useEffect(() => {
    if (!cards || cards.length === 0) return;
    const missing = cards.filter(
      (c) => c.numeroSequencial === undefined || c.numeroSequencial === null
    );
    if (missing.length === 0) return;
    let nextNum = generateNextSequentialNumber(
      cards.filter((c) => c.numeroSequencial !== undefined && c.numeroSequencial !== null)
    );
    const updated = cards.map((c) => {
      if (c.numeroSequencial !== undefined && c.numeroSequencial !== null) return c;
      const assigned = nextNum;
      nextNum += 10;
      return { ...c, numeroSequencial: assigned };
    });
    setCards(updated);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cards]);

  // ============================================================================
  // HISTORY (UNDO/REDO)
  // ============================================================================
  const {
    undo,
    redo,
    canUndo,
    canRedo
  } = useHistory(cards, actions, postIts, setCards, setActions, setPostIts);

  // ============================================================================
  // LOCAL STATE
  // ============================================================================
  const [scale, setScale] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [selectedPostItIds, setSelectedPostItIds] = useState<Set<string>>(new Set());
  const [selectedCardIds, setSelectedCardIds] = useState<Set<string>>(new Set());
  const [highlightedPath, setHighlightedPath] = useState<string[] | null>(null);
  const [analysisMode, setAnalysisMode] = useState(false);
  const [isCreatingPostIt, setIsCreatingPostIt] = useState(false);
  const [creatingPostItType, setCreatingPostItType] = useState<PostItType>('idea');
  const [manualArrowMode, setManualArrowMode] = useState(false);
  const [manualArrowSource, setManualArrowSource] = useState<{ id: string; handle: 'right' | 'bottom' | 'top' | 'left' } | null>(null);
  const [manualArrowWaypoints, setManualArrowWaypoints] = useState<Position[]>([]);
  const [connectingSource, setConnectingSource] = useState<{ id: string; handle: 'right' | 'bottom' | 'top' | 'left'; tool?: V186EngineeringTool } | null>(null);
  const connectingSourceRef = useRef<{ id: string; handle: 'right' | 'bottom' | 'top' | 'left'; tool?: V186EngineeringTool } | null>(null);
  const kanbanConnectionStartedRef = useRef(false);
  connectingSourceRef.current = connectingSource;
  const [selectedConnection, setSelectedConnection] = useState<{ sourceId: string; connectionId: string } | null>(null);
  const [boxSelection, setBoxSelection] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
  const [draggingPostIt, setDraggingPostIt] = useState<{ id: string; offsetX: number; offsetY: number } | null>(null);
  const [postItSnapTargetId, setPostItSnapTargetId] = useState<string | null>(null);
  const [editingPostItId, setEditingPostItId] = useState<string | null>(null);
  const [linkingPostItId, setLinkingPostItId] = useState<string | null>(null);
  const [postItContextMenu, setPostItContextMenu] = useState<{ id: string; x: number; y: number } | null>(null);
  const [showGrid, setShowGrid] = useState(true);
  // Default OFF: movimento livre/fino para todos os cards. Usuário pode ligar via toolbar.
  const [snapToGridEnabled, setSnapToGridEnabled] = useState(false);
  const [pageMode, setPageMode] = useState<{ enabled: boolean; format: 'a4-portrait' | 'a4-landscape' | 'a3-portrait' | 'a3-landscape' }>({ 
    enabled: false, 
    format: 'a4-landscape' 
  });
  const [pageConnectors, setPageConnectors] = useState<PageConnector[]>([]);
  const [contentScale, setContentScale] = useState(12);
  const [panModeActive, setPanModeActive] = useState(false);
  const [showMinimap, setShowMinimap] = useState(false);
  const [showLeadTimeLadder, setShowLeadTimeLadder] = useState(true);
  // Listener do evento disparado pelo menu de contexto do VsmCanvas
  useEffect(() => {
    const handler = () => setShowLeadTimeLadder((prev) => !prev);
    window.addEventListener('vsm:toggleLadder', handler);
    return () => window.removeEventListener('vsm:toggleLadder', handler);
  }, []);
  const [currentLayout, setCurrentLayout] = useState<'default' | 'postit' | 'minimal' | 'classic'>('default');
  const [currentColor, setCurrentColor] = useState('blue');
  // Refs para evitar stale closure nos handlers de inserção de cards
  const currentLayoutRef = useRef<'default' | 'postit' | 'minimal' | 'classic'>('default');
  const currentColorRef = useRef('blue');
  currentLayoutRef.current = currentLayout;
  currentColorRef.current = currentColor;
  const [gridType, setGridType] = useState<'lines' | 'dots' | 'none'>('lines');
  const [gridColor, setGridColor] = useState(isDark ? (theme === 'black' ? 'rgba(255,255,255,0.022)' : 'rgba(255,255,255,0.04)') : 'rgba(71,85,105,0.062)');
  const [canvasBackgroundColor, setCanvasBackgroundColor] = useState(
    theme === 'black' ? '#131314' : isDark ? '#223244' : '#FDFEFF'
  );
  const [arrowColor, setArrowColor] = useState(isDark ? (theme === 'black' ? '#666666' : '#64748B') : 'rgba(0,0,0,0.6)');
  const [isLoading, setIsLoading] = useState(false);
  const [showJourneySidebar, setShowJourneySidebar] = useState(false);
  const [journeyPanelExpanded, setJourneyPanelExpanded] = useState(true);

  const [showRulers, setShowRulers] = useState(true);
  const [snapGuides, setSnapGuides] = useState<{ x?: number; y?: number }>({});
  const [canvasSize] = useState({ width: 1920, height: 1080 });
  const [viewportSize, setViewportSize] = useState({ width: 1920, height: 1080 });
  const alignedRulerInset = showRulers ? RULER_SIZE - RULER_ALIGNMENT_OFFSET : 0;

  // V229: Modal isolado para parâmetros globais da fábrica (turnos/min/dias).
  // Estado local — abertura/fechamento não re-renderiza o canvas ReactFlow.
  const [showFactorySettingsModal, setShowFactorySettingsModal] = useState(false);
  const [showVisualSettingsModal, setShowVisualSettingsModal] = useState(false);
  const [showProjectSettingsModal, setShowProjectSettingsModal] = useState(false);
  const [showOrgManagerModal, setShowOrgManagerModal] = useState(false);
  const [showNewMapTypeModal, setShowNewMapTypeModal] = useState(false);

  // V27.0: Atmosphere switch — background e grid por contexto
  useEffect(() => {
    if (mapType === 'FACTORY') {
      setShowGrid(false);
      // Não força snap em FACTORY — movimento fino é o padrão (Alt continua disponível como override).
      setShowRulers(false);
    }
    // Cores do canvas iguais para Factory e Office
    setCanvasBackgroundColor(theme === 'black' ? '#131314' : isDark ? '#223244' : '#FDFEFF');
    setGridType('lines');
    setGridColor(isDark ? (theme === 'black' ? 'rgba(255,255,255,0.022)' : 'rgba(255,255,255,0.04)') : 'rgba(71,85,105,0.062)');
  }, [mapType, setShowGrid, setSnapToGridEnabled, setShowRulers, theme, isDark]);

  const [rightPanelMode, setRightPanelMode] = useState<RightPanelMode>('none');
  const [rightPanelExpanded, setRightPanelExpanded] = useState(false);
  const [yamazumiFocusMode, setYamazumiFocusMode] = useState(false);
  const yamazumiUiSnapshotRef = useRef<{ showRulers: boolean; showJourneySidebar: boolean } | null>(null);
  const [yamazumiShiftMinutes, setYamazumiShiftMinutes] = useState(480);
  const [yamazumiAssignments, setYamazumiAssignments] = useState<Record<string, string>>({});
  const [yamazumiBaseAssignments, setYamazumiBaseAssignments] = useState<Record<string, string>>({});
  const [yamazumiFteByRole, setYamazumiFteByRole] = useState<Record<string, number>>({});
  const [yamazumiBaseFteByRole, setYamazumiBaseFteByRole] = useState<Record<string, number>>({});
  const [dashboardMode, setDashboardMode] = useState<'sidebar' | 'fullscreen'>('sidebar');
  const [isSimulationMode, setIsSimulationMode] = useState(false);
  const [dashboardFilterResetToken, setDashboardFilterResetToken] = useState(0);
  const [simulationSuggestion, setSimulationSuggestion] = useState<DashboardSimulationSuggestion | null>(null);
  const [selectedPostItId, setSelectedPostItId] = useState<string | null>(null);
  const [kaizenFilter, setKaizenFilter] = useState<'all' | PostItType>('all');
  const [isKaizenCollapsed, setIsKaizenCollapsed] = useState(false);
  const [engineeringTool, setEngineeringTool] = useState<V186EngineeringTool>(null);
  const previousEngineeringToolRef = useRef<V186EngineeringTool>(null);
  const [consolidateFutureNonce, setConsolidateFutureNonce] = useState(0);
  const [linkedBlueprintId, setLinkedBlueprintId] = useState<string | null>(null);
  const [blueprintHeaderSnapshot, setBlueprintHeaderSnapshot] = useState<BlueprintHeaderSnapshot | null>(null);
  const [executiveMode, setExecutiveMode] = useState(false);
  const [showExecutiveComparison, setShowExecutiveComparison] = useState(false);

  useEffect(() => {
    if (previousEngineeringToolRef.current !== engineeringTool) {
      connectingSourceRef.current = null;
      setConnectingSource(null);
      setManualArrowSource(null);
      setManualArrowWaypoints([]);
      kanbanConnectionStartedRef.current = false;
      previousEngineeringToolRef.current = engineeringTool;
      // pull_signal_connection usa o overlay de modo manual para clicar em qualquer ponto do card
      if (engineeringTool === 'pull_signal_connection') {
        setManualArrowMode(true);
      } else if (previousEngineeringToolRef.current === null) {
        // deixa manualArrowMode como está (controlado pelo toggle normal)
      }
    }
  }, [engineeringTool]);

  // Swimlanes (raias horizontais)
  const [swimLanes, setSwimLanes] = useState<SwimLaneInfo[]>([]);
  const [swimLanesVisible, setSwimLanesVisible] = useState(false);

  // Auth & Project context para blueprint
  const { user } = useAuth();
  const { canEdit } = usePermission();
  const { selectedProject: ctxProject, setSelectedProject, setProjects, updateProject, projects: ctxProjects } = useProjectContext();
  const [searchParams] = useSearchParams();
  const urlPlanId = searchParams.get('planId');

  // Derive selectedProject: prefer context selected, then context list, then Zustand, then cloud fetch
  const zustandCurrentProjectId = useProjectStore((s) => s.currentProjectId);
  const setCurrentProjectId = useProjectStore((s) => s.setCurrentProjectId);
  const zustandProjects = useProjectStore((s) => s.projects);
  const resolvedProjectId = ctxProject?.id ?? zustandCurrentProjectId;
  const [cloudProject, setCloudProject] = useState<any>(null);
  const selectedProject: any = ctxProject
    || (resolvedProjectId && ctxProjects.find(p => p.id === resolvedProjectId))
    || (resolvedProjectId && zustandProjects.find(p => p.id === resolvedProjectId))
    || cloudProject
    || null;

  useEffect(() => {
    const state = useProjectStore.getState();
    if (state.sheets.length > 0 || state.getCards().length > 0) return;

    try {
      const raw = localStorage.getItem(MAP_SESSION_SNAPSHOT_KEY);
      if (!raw) return;
      const snapshot = JSON.parse(raw) as {
        projectId?: string | null;
        mapName?: string | null;
        sheets?: Sheet[];
        activeSheetId?: string | null;
        postIts?: PostIt[];
        actions?: typeof actions;
        mapType?: 'OFFICE' | 'FACTORY';
        stage?: ProjectStage;
        completedStages?: ProjectStage[];
        viewMode?: 'current_clean' | 'kaizen_work' | 'future_simulated';
        isKaizenMode?: boolean;
        modeSnapshots?: typeof state._modeSnapshots;
        globalDemand?: number;
        workTime?: number;
        taktTime?: number;
        factoryWorkDays?: number;
      };
      if (!snapshot.sheets?.length) return;

      const activeSnapshotSheetId = snapshot.activeSheetId && snapshot.sheets.some(sheet => sheet.id === snapshot.activeSheetId)
        ? snapshot.activeSheetId
        : snapshot.sheets[0]?.id;
      state.setSheets(snapshot.sheets, activeSnapshotSheetId);
      if (snapshot.mapType === 'FACTORY' || snapshot.mapType === 'OFFICE') state.setMapType(snapshot.mapType);
      setPostIts(snapshot.postIts ?? []);
      setActions(snapshot.actions ?? []);
      useProjectStore.setState({
        currentProjectId: snapshot.projectId ?? state.currentProjectId,
        currentMapDisplayName: snapshot.mapName ?? state.currentMapDisplayName,
        stage: snapshot.stage ?? state.stage,
        completedStages: snapshot.completedStages ?? state.completedStages,
        viewMode: snapshot.viewMode ?? state.viewMode,
        isKaizenMode: Boolean(snapshot.isKaizenMode),
        _modeSnapshots: snapshot.modeSnapshots ?? state._modeSnapshots,
        globalDemand: Number(snapshot.globalDemand) || state.globalDemand,
        workTime: Number(snapshot.workTime) || state.workTime,
        taktTime: Number(snapshot.taktTime) || state.taktTime,
        factoryWorkDays: Number(snapshot.factoryWorkDays) || state.factoryWorkDays,
      } as any);
    } catch {
      // Snapshot inválido não deve bloquear abertura do mapa.
    }
  }, [setActions, setPostIts]);

  // Auto-define mapType sem sobrescrever mapas salvos/localizados.
  useEffect(() => {
    const savedMapType = selectedProject?.estado_atual?.mapType;
    if (savedMapType === 'FACTORY' || savedMapType === 'OFFICE') {
      setMapType(savedMapType);
    } else if (selectedProject?.enabledModules?.vsm === true) {
      setMapType('FACTORY');
    } else if (selectedProject?.enabledModules?.vsm === false) {
      setMapType('OFFICE');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id, selectedProject?.estado_atual?.mapType, selectedProject?.enabledModules?.vsm]);

  // V27.0: Modal automático ao abrir canvas vazio sem projeto selecionado
  useEffect(() => {
    if (!selectedProject && cards.length === 0) {
      setShowNewMapTypeModal(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject?.id]);

  const activeActivityCards = useMemo(
    () => cards.filter((card) => card.tipo === 'atividade' && card.status === 'ativo'),
    [cards]
  );
  const yamazumiColumns = useMemo(
    () => buildYamazumiColumns(activeActivityCards, organization?.roles ?? []),
    [activeActivityCards, organization?.roles]
  );
  const yamazumiSeedSignature = useMemo(
    () => JSON.stringify(activeActivityCards.map((card) => [card.id, card.responsavel ?? card.quem ?? '', card.resources?.[0]?.roleId ?? '', card.resources?.[0]?.quantity ?? card.pessoasSimultaneas ?? 1])),
    [activeActivityCards]
  );

  useEffect(() => {
    const nextAssignments = buildInitialYamazumiAssignments(activeActivityCards, organization?.roles ?? []);
    const nextFte = buildInitialYamazumiFteByRole(activeActivityCards, organization?.roles ?? []);
    setYamazumiAssignments(nextAssignments);
    setYamazumiBaseAssignments(nextAssignments);
    setYamazumiFteByRole(nextFte);
    setYamazumiBaseFteByRole(nextFte);
  }, [yamazumiSeedSignature, activeActivityCards, organization?.roles]);

  const yamazumiPendingChanges = useMemo(() => {
    const assignmentChanges = activeActivityCards.filter(
      (card) => (yamazumiAssignments[card.id] ?? UNASSIGNED_ROLE_ID) !== (yamazumiBaseAssignments[card.id] ?? UNASSIGNED_ROLE_ID)
    ).length;

    const fteChanges = Object.keys(yamazumiFteByRole).filter(
      (key) => Number(yamazumiFteByRole[key] ?? 1) !== Number(yamazumiBaseFteByRole[key] ?? 1)
    ).length;

    return assignmentChanges + fteChanges;
  }, [activeActivityCards, yamazumiAssignments, yamazumiBaseAssignments, yamazumiFteByRole, yamazumiBaseFteByRole]);

  // Auto-resolve: if no currentProjectId but ctxProjects has exactly 1, use it
  useEffect(() => {
    if (!resolvedProjectId && ctxProjects.length === 1 && ctxProjects[0]?.id) {
      setCurrentProjectId(ctxProjects[0].id);
    }
  }, [resolvedProjectId, ctxProjects, setCurrentProjectId]);

  // Resolve planId from URL → process_blueprints → process_blueprint_usage → project
  useEffect(() => {
    if (!urlPlanId || !user?.organizationId || selectedProject) return;
    let cancelled = false;
    (async () => {
      // 1. Find blueprint linked to this plan
      const { data: bp } = await supabase
        .from('process_blueprints')
        .select('id')
        .eq('plan_id', urlPlanId)
        .eq('organization_id', user.organizationId)
        .limit(1)
        .maybeSingle();
      if (cancelled || !bp) return;
      // 2. Find project linked to this blueprint
      const { data: usage } = await supabase
        .from('process_blueprint_usage')
        .select('project_id')
        .eq('blueprint_id', bp.id)
        .eq('organization_id', user.organizationId)
        .limit(1)
        .maybeSingle();
      if (cancelled || !usage?.project_id) return;
      setCurrentProjectId(usage.project_id);
    })();
    return () => { cancelled = true; };
  }, [urlPlanId, user?.organizationId, selectedProject, setCurrentProjectId]);

  // Auto-resolve: if no project found locally, fetch the most recent from Supabase
  useEffect(() => {
    if (selectedProject || !user?.organizationId) return;
    // If we have a resolvedProjectId, fetch that specific project
    if (resolvedProjectId) {
      let cancelled = false;
      supabase
        .from('projects')
        .select('*')
        .eq('id', resolvedProjectId)
        .eq('organization_id', user.organizationId)
        .maybeSingle()
        .then(({ data }) => {
          if (!cancelled && data) setCloudProject(data);
        });
      return () => { cancelled = true; };
    }
    // No resolvedProjectId at all — fetch the most recently saved project for this org
    let cancelled = false;
    supabase
      .from('projects')
      .select('*')
      .eq('organization_id', user.organizationId)
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) {
          setCloudProject(data);
          setCurrentProjectId(data.id);
        }
      });
    return () => { cancelled = true; };
  }, [selectedProject, resolvedProjectId, user?.organizationId, setCurrentProjectId]);

  // Mapa Vivo (Live Map)
  const [liveMode, setLiveMode] = useState(false);
  const [liveTemplateId, setLiveTemplateId] = useState<string | null>(null);
  const { metricsMap: liveMetricsMap } = useWorkflowLiveMetrics(
    liveMode ? liveTemplateId : null,
    user?.organizationId ?? null
  );

  useEffect(() => {
    setGridColor((current) =>
      current === 'rgba(255,255,255,0.05)' || current === 'rgba(71,85,105,0.065)' || current === 'rgba(255,255,255,0.022)' || current === 'rgba(71,85,105,0.04)' || current === 'rgba(71,85,105,0.055)' || current === 'rgba(255,255,255,0.04)' || current === 'rgba(71,85,105,0.062)' || current === 'rgba(255,255,255,0.035)' || current === 'rgba(255,255,255,0.03)' || current === 'rgba(255,255,255,0.045)' || current === 'rgba(71,85,105,0.07)'
        ? (isDark ? (theme === 'black' ? 'rgba(255,255,255,0.022)' : 'rgba(255,255,255,0.04)') : 'rgba(71,85,105,0.062)')
        : current,
    );
    setCanvasBackgroundColor((current) =>
      current === '#223244' || current === '#FDFEFF' || current === '#0d1117' || current === '#111111' || current === '#09090b' || current === '#131314'
        ? (theme === 'black' ? '#131314' : isDark ? '#223244' : '#FDFEFF')
        : current,
    );
    setArrowColor((current) =>
      current === '#64748B' || current === 'rgba(0,0,0,0.6)' || current === '#666666'
        ? (isDark ? (theme === 'black' ? '#666666' : '#64748B') : 'rgba(0,0,0,0.6)')
        : current,
    );
    setGridType((current) => (current === 'dots' || current === 'lines' || current === 'none' ? current : 'lines'));
  }, [isDark, theme]);

  // Detectar se o projeto atual possui blueprint publicado vinculado
  const zustandBlueprintId = useProjectStore((s) => s.currentBlueprintId);

  useEffect(() => {
    const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    const pid = resolvedProjectId;
    if (!pid || !UUID_RE.test(pid) || !user?.organizationId) {
      // No project — fallback to Zustand blueprintId (set by handleOpenMap)
      if (zustandBlueprintId) {
        setLinkedBlueprintId(zustandBlueprintId);
      } else {
        setLinkedBlueprintId(null);
      }
      return;
    }
    let cancelled = false;
    Promise.resolve(
      supabase
        .from('process_blueprint_usage')
        .select('blueprint_id')
        .eq('project_id', pid)
        .eq('organization_id', user.organizationId)
        .limit(1)
        .maybeSingle()
    )
      .then(({ data, error }) => {
        if (!cancelled) {
          const found = error ? null : (data?.blueprint_id ?? null);
          setLinkedBlueprintId(found ?? zustandBlueprintId ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setLinkedBlueprintId(zustandBlueprintId ?? null);
      });
    return () => { cancelled = true; };
  }, [resolvedProjectId, user?.organizationId, zustandBlueprintId]);

  useEffect(() => {
    if (!linkedBlueprintId || !user?.organizationId) {
      setBlueprintHeaderSnapshot(null);
      return;
    }

    let cancelled = false;

    Promise.resolve(
      supabase
        .from('process_blueprints')
        .select('name, status, updated_at, is_corporate_standard')
        .eq('id', linkedBlueprintId)
        .eq('organization_id', user.organizationId)
        .maybeSingle()
    )
      .then(({ data }) => {
        if (cancelled) return;
        if (!data) {
          setBlueprintHeaderSnapshot(null);
          return;
        }

        setBlueprintHeaderSnapshot({
          name: data.name ?? 'Blueprint vinculado',
          status: data.status ?? 'draft',
          versionSemver: '1.0.0',
          updatedAt: data.updated_at ?? new Date().toISOString(),
          isCorporateStandard: Boolean(data.is_corporate_standard),
        });
      })
      .catch(() => {
        if (!cancelled) setBlueprintHeaderSnapshot(null);
      });

    return () => {
      cancelled = true;
    };
  }, [linkedBlueprintId, user?.organizationId]);

  /** Altera o ciclo de vida do mapa via UPDATE em process_blueprints */
  const handleSetBlueprintStatus = useCallback(async (newStatus: VsmStatusValue) => {
    if (!linkedBlueprintId || !user?.organizationId) {
      showToast('warning', 'Nenhum blueprint vinculado. Salve o mapa primeiro.');
      return;
    }
    const { error } = await supabase
      .from('process_blueprints')
      .update({ status: newStatus })
      .eq('id', linkedBlueprintId)
      .eq('organization_id', user.organizationId);
    if (error) {
      showToast('error', 'Falha ao atualizar o status do mapa.');
      return;
    }
    setBlueprintHeaderSnapshot(prev =>
      prev
        ? { ...prev, status: newStatus, isCorporateStandard: newStatus === 'standard' }
        : null
    );
    showToast('success', 'Status do mapa atualizado.');
  }, [linkedBlueprintId, user?.organizationId, showToast]);

  const canvasRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  
  // Refs para drag de cards
  const draggingCardIdRef = useRef<string | null>(null);
  const isolatedDragCardIdRef = useRef<string | null>(null);
  const dragOffsetRef = useRef({ x: 0, y: 0 });
  const dragStartPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());
  const dragRafRef = useRef<number | null>(null);
  const pendingMoveRef = useRef<{ clientX: number; clientY: number } | null>(null);
  // Fast-path: marca se houve movimento real durante o gesto de drag.
  // Usado pelo handleCardDragEnd para disparar UMA UNICA recálculo de metricas no drop.
  const didDragMoveRef = useRef(false);
  // Alt segurado durante o drag → desativa snap-to-grid e magnetização (movimento fino livre).
  const altKeyHeldRef = useRef(false);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => { if (e.altKey) altKeyHeldRef.current = true; };
    const onKeyUp = (e: KeyboardEvent) => { if (!e.altKey) altKeyHeldRef.current = false; };
    const onBlur = () => { altKeyHeldRef.current = false; };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    window.addEventListener('blur', onBlur);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
      window.removeEventListener('blur', onBlur);
    };
  }, []);

  useEffect(() => {
    if (selectedPostItIds.size === 1) {
      const [onlyId] = Array.from(selectedPostItIds);
      if (onlyId !== selectedPostItId) {
        setSelectedPostItId(onlyId);
      }
      return;
    }

    if (selectedPostItIds.size === 0 && selectedPostItId) {
      setSelectedPostItId(null);
      return;
    }

    if (selectedPostItId && !selectedPostItIds.has(selectedPostItId)) {
      setSelectedPostItId(null);
    }
  }, [selectedPostItIds, selectedPostItId]);

  const clearMapFilters = useCallback(() => {
    setHighlightedPath(null);
    setSelectedCardId(null);
    setSelectedCardIds(new Set());
    setSelectedPostItIds(new Set());
    setSelectedPostItId(null);
    setLinkingPostItId(null);
    resetToBaseline();
    setDashboardFilterResetToken((prev) => prev + 1);
  }, [resetToBaseline, setSelectedCardId]);

  // ============================================================================
  // V26: AUTO-LAYOUT — Kaizen de Layout (atalho 'L')
  // ============================================================================
  const handleAutoLayout = useCallback(() => {
    if (mapType !== 'FACTORY') return;
    const currentCards = getCards();
    const flowCards = currentCards
      .filter(c => c.status === 'ativo' && (c.tipo === 'inventory' || isFactoryProcessCard(c)))
      .sort((a, b) => a.posicao.x - b.posicao.x);

    if (flowCards.length === 0) return;

    const GAP = 36;
    let cursorX = flowCards[0].posicao.x;
    const idToPos = new Map<string, { x: number; y: number }>();
    const centerY = Math.round(flowCards.reduce((sum, card) => {
      const bounds = getVsmCardBounds(card);
      return sum + card.posicao.y + bounds.height / 2;
    }, 0) / flowCards.length);

    for (const card of flowCards) {
      const bounds = getVsmCardBounds(card);
      idToPos.set(card.id, {
        x: Math.round(cursorX),
        y: Math.round(centerY - bounds.height / 2),
      });
      cursorX += bounds.width + GAP;
    }

    const updatedCards = currentCards.map(c => {
      const pos = idToPos.get(c.id);
      if (!pos) return c;
      return { ...c, posicao: pos };
    });
    setCards(updatedCards);
  }, [mapType, getCards, setCards]);

  // ============================================================================
  // KEYBOARD SHORTCUTS
  // ============================================================================
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC: Cancelar modo de criação de post-it
      if (e.key === 'Escape' && isCreatingPostIt) {
        e.preventDefault();
        setIsCreatingPostIt(false);
        return;
      }

      // Escape: Cancelar modo linking de post-it
      if (e.key === 'Escape' && linkingPostItId) {
        e.preventDefault();
        setLinkingPostItId(null);
        return;
      }

      if (e.key === 'Escape') {
        e.preventDefault();
        clearMapFilters();
        return;
      }

      // Ctrl+R: Toggle Réguas
      if (e.ctrlKey && e.key === 'r') {
        e.preventDefault();
        setShowRulers(prev => !prev);
        return;
      }
      
      // Arrow keys: Mover cards selecionados
      const isArrow = e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (isArrow) {
        // Não interceptar se estiver editando texto
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        
        // Determinar quais cards mover
        const cardsToMove = new Set<string>();
        if (selectedCardIds.size > 0) {
          selectedCardIds.forEach(id => cardsToMove.add(id));
        } else if (selectedCardId) {
          cardsToMove.add(selectedCardId);
        }
        
        if (cardsToMove.size === 0) return;
        
        e.preventDefault();
        const GRID_SIZE = 20;
        const step = e.shiftKey ? GRID_SIZE * 5 : GRID_SIZE; // Shift = passo maior
        
        let dx = 0, dy = 0;
        switch (e.key) {
          case 'ArrowUp':    dy = -step; break;
          case 'ArrowDown':  dy = step; break;
          case 'ArrowLeft':  dx = -step; break;
          case 'ArrowRight': dx = step; break;
        }
        
        const currentCards = getCards();
        const updatedCards = currentCards.map(c => {
          if (cardsToMove.has(c.id)) {
            return {
              ...c,
              posicao: {
                x: c.posicao.x + dx,
                y: c.posicao.y + dy
              }
            };
          }
          return c;
        });
        setCards(updatedCards);
      }

      // L: Kaizen de Layout — alinhar todos os nós Factory em trilho único com gap de 60px
      if (e.key === 'l' || e.key === 'L') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        handleAutoLayout();
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedCardIds, selectedCardId, getCards, setCards, isCreatingPostIt, linkingPostItId, clearMapFilters, handleAutoLayout]);

  // Ctrl+Z / Ctrl+Y (Desfazer / Refazer)
  useEffect(() => {
    const handleUndoRedo = (e: KeyboardEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === 'y' || (e.key === 'z' && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    };
    window.addEventListener('keydown', handleUndoRedo);
    return () => window.removeEventListener('keydown', handleUndoRedo);
  }, [undo, redo]);
  
  // Get current stage from store
  const stage = useProjectStore((state) => state.stage);
  const completedStages = useProjectStore((state) => state.completedStages);
  const viewMode = useProjectStore((state) => state.viewMode);
  const setViewMode = useProjectStore((state) => state.setViewMode);
  const ensureOptimizationSession = useProjectStore((state) => state.ensureOptimizationSession);

  useEffect(() => {
    if (viewMode === 'kaizen_work') {
      ensureOptimizationSession();
    }
  }, [ensureOptimizationSession, viewMode]);

  useEffect(() => {
    const state = useProjectStore.getState();
    if (state.sheets.length === 0 && cards.length === 0) return;

    // PASSO 5: debounce de 500ms no snapshot do localStorage. Edicoes rapidas
    // (drag, digitação, múltiplas mudanças) deixam de bloquear a thread principal
    // com JSON.stringify + setItem a cada keystroke/frame.
    const timeoutId = window.setTimeout(() => {
      try {
        const snapshotState = useProjectStore.getState();
        localStorage.setItem(MAP_SESSION_SNAPSHOT_KEY, JSON.stringify({
          projectId: snapshotState.currentProjectId,
          mapName: snapshotState.currentMapDisplayName ?? selectedProject?.nome_projeto ?? selectedProject?.name ?? null,
          sheets: snapshotState.sheets,
          activeSheetId: snapshotState.activeSheetId,
          postIts,
          actions,
          mapType: snapshotState.mapType,
          stage,
          completedStages,
          viewMode,
          isKaizenMode: snapshotState.isKaizenMode,
          modeSnapshots: snapshotState._modeSnapshots,
          globalDemand: snapshotState.globalDemand,
          workTime: snapshotState.workTime,
          taktTime: snapshotState.taktTime,
          factoryWorkDays: snapshotState.factoryWorkDays,
        }));
      } catch {
        // Sessão local é conveniência; não deve bloquear o editor.
      }
    }, 500);

    return () => window.clearTimeout(timeoutId);
  }, [actions, cards, completedStages, mapType, postIts, selectedProject?.name, selectedProject?.nome_projeto, stage, viewMode]);

  // Determinar modos disponíveis baseado no stage do projeto
  const availableModes = React.useMemo(() => {
    const modes: ViewMode[] = ['current_clean']; // Sempre mostrar "Atual"
    
    // Etapa 2 (Kaizen): adicionar Kaizen
    if (stage === 'kaizen' || stage === 'planning' || stage === 'execution' || stage === 'sustainment') {
      modes.push('kaizen_work');
    }
    
    // Etapa 3 (Planejamento): adicionar Futuro e Plano
    if (stage === 'planning' || stage === 'execution' || stage === 'sustainment') {
      modes.push('future_simulated', 'planning');
    }
    
    return modes;
  }, [stage]);

  const isPastCurrentMappingStage = stage === 'kaizen' || stage === 'planning' || stage === 'execution' || stage === 'sustainment';
  const isCurrentSnapshotReadOnly = viewMode === 'current_clean' && isPastCurrentMappingStage;

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================
  const activeCards = useMemo(() => {
    const baseCards = viewMode === 'kaizen_work' ? getCardsWithSimulation() : cards;
    const visibleModeCards = viewMode === 'current_clean'
      ? baseCards.filter(card => (card as any).vsmIcon !== 'kaizen' && card.tipo !== 'kaizen' && !(card as any).vsmOptimizationOnly)
      : baseCards;
    if (mapType !== 'FACTORY') return visibleModeCards;

    const cardsById = new Map(visibleModeCards.map(card => [card.id, card]));

    return visibleModeCards
      .filter(card => !isCancelledProgramInfoCard(card))
      .map(card => {
        if (!isFactoryPcpmCard(card) && !(card.conexoes_saida ?? []).some(conn => shouldRemoveCancelledProgramInfoConnection(conn, cardsById.get(conn.targetId)))) {
          return card;
        }

        // V224: quando MRP está bloqueado (vsmLocked) no PCPM, a conexão PCPM → Expedição
        // deixa de ser empurro de informação (vsm_info) e passa a ser sinal de retirada kanban
        // (vsm_pull_signal), preservando a ortodoxia Lean (TPS) sem exigir click manual.
        const mrpBlocked = isFactoryPcpmCard(card) && !!(card as any).vsmLocked;

        const cleanedConnections = (card.conexoes_saida ?? []).reduce<typeof card.conexoes_saida>((acc, conn) => {
          const target = cardsById.get(conn.targetId);
          if (shouldRemoveCancelledProgramInfoConnection(conn, target)) return acc;
          if (!isFactoryPcpmCard(card) || conn.connectionType !== 'vsm_info') { acc.push(conn); return acc; }
          // V224: PCPM → Expedição com MRP bloqueado → converte para vsm_pull_signal e preserva
          if (mrpBlocked && target && isFactoryExpeditionCard(target)) {
            acc.push({
              ...conn,
              connectionType: 'vsm_pull_signal',
              manualCommand: true,
              sourceHandle: (conn as any).sourceHandle ?? 'right',
              targetHandle: (conn as any).targetHandle ?? 'left',
            } as any);
            return acc;
          }
          if (target && isFactoryExternalCard(target) && (!(conn as any).manualCommand || conn.sourceHandle === 'bottom')) return acc;
          if (target && isFactoryProcessCard(target) && !(conn as any).manualCommand) return acc;
          acc.push(conn);
          return acc;
        }, []);

        return {
          ...card,
          conexoes_saida: cleanedConnections,
        };
      });
  }, [cards, getCardsWithSimulation, mapType, viewMode]);

  const visiblePostIts = useMemo(() => {
    if (viewMode === 'current_clean') {
      return postIts.filter(postIt => postIt.type !== 'action' && !postIt.optimizationOnly);
    }

    if (viewMode === 'future_simulated') {
      return postIts.filter(postIt => postIt.type === 'idea' || postIt.type === 'data');
    }

    return postIts;
  }, [postIts, viewMode]);

  useEffect(() => {
    if (mapType !== 'FACTORY') return;

    const currentCards = getCards();
    const cardsById = new Map(currentCards.map(card => [card.id, card]));
    let changed = false;
    const cleanedCards = currentCards
      .filter(card => {
        const keep = !isCancelledProgramInfoCard(card);
        if (!keep) changed = true;
        return keep;
      })
      .map(card => {
        const currentConnections = card.conexoes_saida ?? [];
        const mrpBlocked = isFactoryPcpmCard(card) && !!(card as any).vsmLocked;
        const cleanedConnections = currentConnections.reduce<typeof currentConnections>((acc, conn) => {
          const target = cardsById.get(conn.targetId);
          if (shouldRemoveCancelledProgramInfoConnection(conn, target)) return acc;
          if (!isFactoryPcpmCard(card) || conn.connectionType !== 'vsm_info') { acc.push(conn); return acc; }
          // V224: persiste a conversão PCPM → Expedição como vsm_pull_signal quando MRP bloqueado
          if (mrpBlocked && target && isFactoryExpeditionCard(target)) {
            acc.push({
              ...conn,
              connectionType: 'vsm_pull_signal',
              manualCommand: true,
              sourceHandle: (conn as any).sourceHandle ?? 'right',
              targetHandle: (conn as any).targetHandle ?? 'left',
            } as any);
            return acc;
          }
          if (target && isFactoryExternalCard(target) && (!(conn as any).manualCommand || conn.sourceHandle === 'bottom')) return acc;
          if (target && isFactoryProcessCard(target) && !(conn as any).manualCommand) return acc;
          acc.push(conn);
          return acc;
        }, []);
        // DEDUPE: para PCPM, manter apenas UMA conexão vsm_info por targetId de processo
        // Múltiplas conexões para o mesmo target ativam o trunk-merge e quebram o roteamento
        let dedupedConnections = cleanedConnections;
        if (isFactoryPcpmCard(card)) {
          const seenInfoTargets = new Set<string>();
          dedupedConnections = cleanedConnections.filter(conn => {
            if (conn.connectionType !== 'vsm_info') return true;
            const target = cardsById.get(conn.targetId);
            if (!target || !isFactoryProcessCard(target)) return true;
            if (seenInfoTargets.has(conn.targetId)) return false;
            seenInfoTargets.add(conn.targetId);
            return true;
          });
        }
        const bound = (card as any).vsmBoundConnection;
        const hasInvalidBound = !!bound && !currentCards.some(sourceCard =>
          sourceCard.id === bound.sourceId &&
          !isCancelledProgramInfoCard(sourceCard) &&
          (sourceCard.conexoes_saida ?? []).some(conn => conn.id === bound.connectionId && !shouldRemoveCancelledProgramInfoConnection(conn, cardsById.get(conn.targetId)))
        );

        if (dedupedConnections.length === currentConnections.length && !hasInvalidBound) return card;
        changed = true;
        return {
          ...card,
          conexoes_saida: dedupedConnections,
          ...(hasInvalidBound ? { vsmBoundConnection: undefined } : {}),
        };
      });

    if (changed) {
      setCards(cleanedCards);
      // PASSO 4: ler selectedConnection dinamicamente para evitar re-rodar todo
      // este effect a cada clique em conexão (era um dep do array).
      const currentSelectedConnection = useProjectStore.getState().selectedConnection;
      if (currentSelectedConnection && !cleanedCards.some(card => card.id === currentSelectedConnection.sourceId && (card.conexoes_saida ?? []).some(conn => conn.id === currentSelectedConnection.connectionId))) {
        setSelectedConnection(null);
      }
    }
  }, [getCards, mapType, setCards]);

  useEffect(() => {
    const handleAddCommunicationSketch = () => {
      if (useProjectStore.getState().mapType !== 'FACTORY') return;

      const currentCards = useProjectStore.getState().getCards();
      const active = currentCards.filter(card => card.status !== 'deletado');
      const pcpm = active.find(card =>
        isFactoryPcpmCard(card)
      );
      const supplier = active.find(card => {
        const extType = String((card as any).extType ?? '').toUpperCase();
        return card.id === 'ext-fornecedor' || extType === 'FORNECEDOR' || card.tipo === 'card_inicial' || (card as any).vsmIcon === 'fornecedor';
      });
      const client = active.find(card => {
        const extType = String((card as any).extType ?? '').toUpperCase();
        return card.id === 'ext-cliente' || extType === 'CLIENTE' || card.tipo === 'card_final' || (card as any).vsmIcon === 'cliente';
      });
      if (!pcpm || !supplier || !client) return;

      const topY = Math.round(Math.min(supplier.posicao.y, pcpm.posicao.y, client.posicao.y));
      const leftMidX = Math.round((supplier.posicao.x + pcpm.posicao.x) / 2 + 30);
      const rightMidX = Math.round((pcpm.posicao.x + client.posicao.x) / 2 + 10);
      const supplierForecastLaneY = Math.round(topY + 45);
      const communicationCards: CardData[] = [
        {
          id: 'vsm-comm-supplier-forecast', tipo: 'atividade', vsmIcon: 'comunicacao', atividade: 'Previsão 6\nSemanas', status: 'ativo',
          posicao: { x: leftMidX, y: supplierForecastLaneY - 20 },
          conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: 'classic', layoutColor: currentColorRef.current,
        },
        {
          id: 'vsm-comm-client-forecast', tipo: 'atividade', vsmIcon: 'comunicacao', atividade: 'Previsão 30\ne 60 dias', status: 'ativo',
          posicao: { x: rightMidX, y: topY + 2 }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: 'classic', layoutColor: currentColorRef.current,
        },
        {
          id: 'vsm-comm-client-confirm', tipo: 'atividade', vsmIcon: 'comunicacao', atividade: 'Confirmação\nDiária', status: 'ativo',
          posicao: { x: rightMidX, y: topY + 76 }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: 'classic', layoutColor: currentColorRef.current,
        },
      ];

      const byId = new Map(currentCards.map(card => [card.id, card]));
      communicationCards.forEach(card => {
        const previous = byId.get(card.id);
        byId.set(card.id, previous ? { ...previous, ...card, conexoes_saida: previous.conexoes_saida ?? [] } : card);
      });

      const addInfoConnection = (sourceId: string, targetId: string, sourceHandle: 'left' | 'right' | 'bottom' | 'top', targetHandle: 'left' | 'right' | 'bottom' | 'top') => {
        const source = byId.get(sourceId);
        if (!source || !byId.has(targetId)) return;
        const existing = (source.conexoes_saida ?? []).filter(conn => !(conn.targetId === targetId && conn.connectionType === 'vsm_info'));
        byId.set(sourceId, {
          ...source,
          conexoes_saida: [
            ...existing,
            { id: `conn-${sourceId}-${targetId}`, targetId, sourceHandle, targetHandle, connectionType: 'vsm_info' },
          ],
        });
      };

      const setSupplierInfoLanes = () => {
        const source = byId.get(pcpm.id);
        if (!source || !byId.has(supplier.id)) return;
        const existing = (source.conexoes_saida ?? []).filter(conn =>
          !(conn.targetId === supplier.id && conn.connectionType === 'vsm_info')
        );
        byId.set(pcpm.id, {
          ...source,
          conexoes_saida: existing,
        });
      };

      addInfoConnection(client.id, 'vsm-comm-client-forecast', 'left', 'right');
      addInfoConnection('vsm-comm-client-forecast', pcpm.id, 'left', 'right');
      setSupplierInfoLanes();

      CANCELLED_PROGRAM_INFO_BOX_IDS.forEach(id => byId.delete(id));

      const nextCards = currentCards
        .filter(card => !isCancelledProgramInfoCard(card))
        .map(card => {
          const nextCard = byId.get(card.id) ?? card;
          return {
            ...nextCard,
            conexoes_saida: (nextCard.conexoes_saida ?? []).filter(conn =>
              !shouldRemoveCancelledProgramInfoConnection(conn, byId.get(conn.targetId))
            ),
          };
        });
      communicationCards.forEach(card => {
        if (!currentCards.some(existing => existing.id === card.id)) nextCards.push(byId.get(card.id) ?? card);
      });

      useProjectStore.getState().setCards(nextCards);
    };

    window.addEventListener('workflow:add-vsm-communication-sketch', handleAddCommunicationSketch);
    return () => window.removeEventListener('workflow:add-vsm-communication-sketch', handleAddCommunicationSketch);
  }, []);

  useEffect(() => {
    if (mapType !== 'FACTORY') return;
    // PASSO 3: enquanto o usuário está ativamente arrastando um card,
    // nao recalcular a geometria do Vertical Flow. Evita disputa de frame com
    // o fast-path do handleMove e dezenas de re-execuções por gesto.
    if (draggingCardIdRef.current) return;
    let changed = false;
    const nextCards = cards.map(card => {
      if (!isVsmVerticalFlowCard(card) || !(card as any).vsmFlowSourceId || !(card as any).vsmFlowTargetId) {
        return card;
      }

      // Não re-sincronizar a seta enquanto está sendo arrastada manualmente — evita disputa de frame.
      if (draggingCardIdRef.current === card.id) {
        return card;
      }

      const geometry = buildVerticalFlowGeometry(card, cards);
      if (card.posicao.x === geometry.x && card.posicao.y === geometry.y && Number((card as any).vsmFlowHeight) === geometry.height) {
        return card;
      }

      changed = true;
      return {
        ...card,
        posicao: { x: geometry.x, y: geometry.y },
        vsmFlowHeight: geometry.height,
      };
    });

    const syncedCards = nextCards.map(card => {
      const parentId = (card as any).transportDockParentId;
      if (!parentId || !['caminhao', 'transporte'].includes((card as any).vsmIcon ?? '')) {
        return card;
      }

      const parentFlow = nextCards.find(candidate => candidate.id === parentId && isVsmVerticalFlowCard(candidate));
      if (!parentFlow) return card;

      const dock = buildTransportDockPosition(parentFlow);
      if (card.posicao.x === dock.x && card.posicao.y === dock.y) {
        return card;
      }

      changed = true;
      return { ...card, posicao: dock };
    });

    // Sync: inventory cards linked as target of a vertical flow → keep X centered, never lock Y.
    const inventorySynced = syncedCards.map(card => {
      if (card.tipo !== 'inventory') return card;
      const parentFlow = syncedCards.find(
        c => isVsmVerticalFlowCard(c) && (c as any).vsmFlowTargetId === card.id
      );
      if (!parentFlow) return card;
      const sourceEntityId = (parentFlow as any).vsmFlowSourceId;
      const sourceEntity = sourceEntityId ? syncedCards.find(c => c.id === sourceEntityId) : undefined;
      const axisCard = sourceEntity ?? parentFlow;
      const axisCenterX = getLogisticsCenterX(axisCard);
      const childBounds = getVsmCardBounds(card);
      const targetX = Math.round(axisCenterX - childBounds.width / 2);
      if (card.posicao.x === targetX) return card;
      changed = true;
      return { ...card, posicao: { ...card.posicao, x: targetX } };
    });

    if (changed) {
      setCards(inventorySynced);
    }
  }, [cards, setCards, mapType]);

  // Em modo FACTORY, forçar layout 'classic' para renderização correta das setas
  const effectiveLayout = mapType === 'FACTORY' ? 'classic' : currentLayout;

  // ✅ Usar useEdgeCalculations para calcular conexões com as regras corretas do vsmGeometry.ts
  const { processedConnections, mergeNodes } = useEdgeCalculations({
    activeCards,
    highlightedPath,
    currentLayout: effectiveLayout,
    isTimeScaleActive: false,
    viewMode
  });

  // ============================================================================
  // VIEWPORT SIZE TRACKING - Para réguas sincronizadas com canvas
  // ============================================================================
  useEffect(() => {
    if (!viewportRef.current) return;

    const updateViewportSize = () => {
      if (viewportRef.current) {
        const rect = viewportRef.current.getBoundingClientRect();
        setViewportSize({ width: rect.width, height: rect.height });
      }
    };

    // Atualizar tamanho inicial
    updateViewportSize();

    // Observer para mudanças de tamanho
    const resizeObserver = new ResizeObserver(updateViewportSize);
    resizeObserver.observe(viewportRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);


  // ============================================================================
  // CARD HANDLERS
  // ============================================================================

  // --- Move linkPostItToCard above handleCardSelect to fix ReferenceError ---
  const linkPostItToCard = useCallback((postItId: string, cardId: string) => {
    const card = cards.find(c => c.id === cardId);
    if (!card) return;
    const cw = card.calculatedWidth || 240;
    const ch = 180;
    // Contar quantos post-its já estão vinculados a este card
    const existingLinked = postIts.filter(p => p.fixedToNodeId === cardId && p.id !== postItId);
    const POST_IT_W = 80;
    const GAP = 5;
    const MAX_PER_ROW = 3;
    const row = Math.floor(existingLinked.length / MAX_PER_ROW);
    const col = existingLinked.length % MAX_PER_ROW;
    // Posicionar embaixo do card, centralizado
    const totalWidth = Math.min(existingLinked.length + 1, MAX_PER_ROW) * (POST_IT_W + GAP) - GAP;
    const startX = (cw - totalWidth) / 2;
    const relX = startX + col * (POST_IT_W + GAP);
    const relY = ch + 8 + row * 80; // 8px gap abaixo do card
    setPostIts(postIts.map(p =>
      p.id === postItId
        ? { ...p, fixedToNodeId: cardId, relativeOffset: { x: relX, y: relY }, x: card.posicao.x + relX, y: card.posicao.y + relY }
        : p
    ));
  }, [cards, postIts, setPostIts]);

  const handleCardSelect = useCallback((id: string, e?: React.MouseEvent) => {
    // Modo click-to-link: se há um post-it selecionado para linking, vincular ao card clicado
    if (linkingPostItId && !draggingPostIt) {
      const card = cards.find(c => c.id === id);
      if (card && card.tipo !== 'card_inicial' && card.tipo !== 'card_final') {
        linkPostItToCard(linkingPostItId, id);
        setLinkingPostItId(null);
        setSelectedPostItIds(new Set());
        return; // Não processar seleção normal do card
      }
    }
    if (e?.shiftKey) {
      // Multi-select
      setSelectedCardIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    } else {
      setSelectedCardId(id);
      setSelectedCardIds(new Set([id]));
      setRightPanelMode('properties');
      // Mutual exclusion: opening properties closes JourneyPanel
      setShowJourneySidebar(false);
    }
  }, [setSelectedCardId, setRightPanelMode, setShowJourneySidebar, linkingPostItId, draggingPostIt, cards, linkPostItToCard]);

  const handleClearSelection = useCallback(() => {
    setSelectedCardId(null);
    setSelectedCardIds(new Set());
    setSelectedPostItIds(new Set());
    setSelectedPostItId(null);
    setLinkingPostItId(null);
    // Fechar painel de propriedades ao limpar seleção
    setRightPanelMode(prev => prev === 'properties' ? 'none' : prev);
  }, [setSelectedCardId, setSelectedPostItId, setRightPanelMode]);

  const handleDeleteTotal = useCallback((id: string) => {
    const currentCards = getCards();
    const card = currentCards.find(c => c.id === id);
    if (!card) return;

    // Modo Kaizen: primeiro marca como removido (sombra), segundo delete remove fisicamente
    if (viewMode === 'kaizen_work' && card.status !== 'removido') {
      const updated = currentCards.map(c => {
        if (c.id === id) {
          return { ...c, status: 'removido' as const };
        }
        return c;
      });
      setCards(updated);
    } else {
      // Modo Mapa ou card já removido no Kaizen: remover fisicamente
      const updated = currentCards
        .filter(c => c.id !== id)
        .map(c => ({
          ...c,
          conexoes_saida: (c.conexoes_saida || []).filter(conn => conn.targetId !== id)
        }));
      setCards(updated);
    }

    // Limpar seleção local e global do card excluído
    setSelectedCardIds(prev => {
      if (!prev.has(id)) return prev;
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    const { setSelectedCardId: storeSetSelected } = useProjectStore.getState();
    storeSetSelected(null);
    recalculateFlowVolumes();
  }, [getCards, setCards, recalculateFlowVolumes, setSelectedCardIds, viewMode]);

  const handleMarkX = useCallback((id: string) => {
    updateCardData(id, { status: 'removido' });
    recalculateFlowVolumes();
  }, [updateCardData, recalculateFlowVolumes]);

  const handleDuplicateCard = useCallback((id: string) => {
    const currentCards = cards;
    const card = currentCards.find(c => c.id === id);
    if (!card) return;

    const newCard: CardData = {
      ...card,
      id: generateId(),
      posicao: { x: card.posicao.x + 50, y: card.posicao.y + 50 },
      conexoes_saida: [],
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };

    setCards([...cards, newCard]);
  }, [cards, setCards]);

  const handleAddNext = useCallback((sourceId: string, type: 'atividade' | 'decisao', isYesPath?: boolean) => {
    const currentCards = cards;
    const sourceCard = currentCards.find(c => c.id === sourceId);
    if (!sourceCard) return;

    const newCardId = generateId();
    
    // Dimensões dos cards (inventory usa largura real ~84px)
    const INV_W_A: Record<string, number> = { fifo: 240, puxado: 104, nivelamento: 192, entrega: 114 };
    const INV_H_A: Record<string, number> = { fifo: 80,  puxado: 80,  nivelamento: 144, entrega: 86  };
    const sourceWidth = sourceCard.tipo === 'decisao' ? 160
      : sourceCard.tipo === 'inventory' ? (INV_W_A[sourceCard.vsmIcon || ''] ?? 84)
      : 240;
    const sourceHeight = sourceCard.tipo === 'decisao' ? 160
      : sourceCard.tipo === 'inventory' ? (INV_H_A[sourceCard.vsmIcon || ''] ?? 56)
      : 180;
    const newCardWidth = type === 'decisao' ? 160 : 240;
    const newCardHeight = type === 'decisao' ? 160 : 180;
    
    // Constantes de espaçamento (mesmas do VsmCanvas ghost)
    const GRID_SIZE = 20;
    const HORIZONTAL_GAP = 36;  // VSM compacto
    const VERTICAL_GAP = 180;   // Espaçamento vertical quando há colisão (altura de 1 card atividade)
    const CARD_GAP = 180;       // Gap entre base de um card e topo do próximo (1 altura de card)
    const PARALLEL_VERTICAL_OFFSET = sourceHeight + CARD_GAP; // Offset dinâmico: sourceHeight + gap
    const ROW_SNAP_THRESHOLD = 100;
    
    // Função para verificar se posição está ocupada (mesma lógica do VsmCanvas)
    const checkPositionOccupied = (x: number, y: number, width: number, height: number): boolean => {
      const margin = 20;
      return currentCards.some(card => {
        if (card.id === sourceCard.id) return false;
        const cardW = card.tipo === 'decisao' ? 160 : 240;
        const cardH = card.tipo === 'decisao' ? 160 : 180;
        return !(x + width + margin < card.posicao.x || 
                 x - margin > card.posicao.x + cardW ||
                 y + height + margin < card.posicao.y || 
                 y - margin > card.posicao.y + cardH);
      });
    };
    
    let rawX: number, rawY: number;
    
    let isParallelPlacement = false;

    if (isYesPath === false) {
      // CAMINHO "NÃO": Direita + PARALLEL_VERTICAL_OFFSET abaixo da origem da decisão
      rawX = sourceCard.posicao.x + sourceWidth + HORIZONTAL_GAP;
      rawY = sourceCard.posicao.y + PARALLEL_VERTICAL_OFFSET;
    } else {
      // CAMINHO "SIM" ou PADRÃO
      // Verificar se já há cards conectados à direita desta origem
      const existingRightSuccessors = sourceCard.conexoes_saida
        .filter(conn => !conn.sourceHandle || conn.sourceHandle === 'right')
        .map(conn => currentCards.find(c => c.id === conn.targetId))
        .filter((c): c is CardData => !!c);

      if (existingRightSuccessors.length > 0) {
        isParallelPlacement = true;
        // Usar o mesmo X do primeiro sucessor (coluna paralela)
        const firstSuccessor = existingRightSuccessors[0];
        rawX = firstSuccessor.posicao.x + ((firstSuccessor.calculatedWidth || (firstSuccessor.tipo === 'decisao' ? 160 : 240)) - newCardWidth) / 2;
        // Colocar abaixo do último sucessor na coluna
        const lowestSuccessor = existingRightSuccessors.reduce((lowest, c) =>
          c.posicao.y > lowest.posicao.y ? c : lowest, existingRightSuccessors[0]);
        const lowestH = lowestSuccessor.tipo === 'decisao' ? 160 : 180;
        rawY = lowestSuccessor.posicao.y + lowestH + CARD_GAP;
      } else {
        // Primeiro card: à direita, centralizado verticalmente
        rawX = sourceCard.posicao.x + sourceWidth + HORIZONTAL_GAP;
        rawY = sourceCard.posicao.y + (sourceHeight - newCardHeight) / 2;
      }
    }
    
    // Verificar se posição está ocupada - continuar descendo até encontrar posição livre
    {
      let attempts = 0;
      const maxAttempts = 20;
      while (checkPositionOccupied(rawX, rawY, newCardWidth, newCardHeight) && attempts < maxAttempts) {
        rawY += newCardHeight + CARD_GAP;
        attempts++;
      }
    }
    
    // Aplicar snap to grid
    let snappedX = Math.round(rawX / GRID_SIZE) * GRID_SIZE;
    let snappedY = Math.round(rawY / GRID_SIZE) * GRID_SIZE;
    
    // REGRA DE ALINHAMENTO: Cards paralelos (isYesPath=false ou isParallelPlacement) NÃO devem alinhar com rows próximas
    // porque precisam manter o offset exato de 220px. Alinhamento só para fluxo principal.
    if (isYesPath !== false && !isParallelPlacement) {
      // Alinhar com row mais próxima se disponível (apenas para caminho "Sim" ou padrão)
      const nearbyRows = currentCards
        .map(c => c.posicao.y)
        .filter(cardY => Math.abs(cardY - snappedY) < ROW_SNAP_THRESHOLD);
      
      if (nearbyRows.length > 0) {
        const closestRow = nearbyRows.reduce((closest, rowY) =>
          Math.abs(rowY - snappedY) < Math.abs(closest - snappedY) ? rowY : closest
        );
        snappedY = closestRow;
      }
    }
    let swimLaneFields: Partial<CardData> = {};
    if (swimLanesVisible && swimLanes.length > 0) {
      const sourceCenter = sourceCard.posicao.y + sourceHeight / 2;
      const targetLane = swimLanes.reduce((best, lane) =>
        Math.abs(sourceCenter - lane.centerY) < Math.abs(sourceCenter - best.centerY) ? lane : best,
        swimLanes[0]
      );
      snappedY = targetLane.centerY - newCardHeight / 2;
      swimLaneFields = { quem: sourceCard.quem, sector: sourceCard.sector };
    }
    
    const newCard: CardData = {
      id: newCardId,
      tipo: type,
      atividade: type === 'atividade' ? 'Nova Atividade' : undefined,
      status: 'ativo',
      posicao: {
        x: snappedX,
        y: snappedY
      },
      conexoes_saida: [],
      p: type === 'atividade' ? 100 : undefined,
      tp: type === 'atividade' ? 0 : undefined,
      lt: type === 'atividade' ? 0 : undefined,
      tt: type === 'atividade' ? 0 : undefined,
      decisao_sim_pct: type === 'decisao' ? 50 : undefined,
      decisao_nao_pct: type === 'decisao' ? 50 : undefined,
      layoutType: currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      numeroSequencial: generateNextSequentialNumber(currentCards),
      ...swimLaneFields,
    };

    // Decisão NÃO: seta sai pelo bottom do losango. Decisão SIM / Padrão: sai pela direita.
    const sourceHandle = sourceCard.tipo === 'decisao' && isYesPath === false ? 'bottom' : 'right';
    
    const connection = {
      id: generateId(),
      targetId: newCardId,
      sourceHandle: sourceHandle as 'right' | 'bottom'
    };

    const updatedCards = cards.map(c =>
      c.id === sourceId
        ? { ...c, conexoes_saida: [...c.conexoes_saida, connection] }
        : c
    );

    setCards([...updatedCards, newCard]);
    recalculateFlowVolumes();
  }, [cards, setCards, recalculateFlowVolumes, swimLanesVisible, swimLanes]);

  // ============================================================================
  // SNAP GUIDES - Cálculo de linhas guias temporárias
  // ============================================================================
  const calculateSnapGuides = useCallback((draggingCard: CardData, newX: number, newY: number) => {
    const SNAP_THRESHOLD = 5; // pixels de tolerância para snap
    const FACTORY_Y_SNAP_THRESHOLD = 30; // V37: magnetismo de eixo Y controlado ±30px
    const FACTORY_GAP_SNAP_MIN = 18;     // VSM compacto
    const FACTORY_GAP_SNAP_MAX = 54;
    const FACTORY_GAP_TARGET  = 36;
    const cards = activeCards.filter(c => c.id !== draggingCard.id);
    
    let snapX: number | undefined;
    let snapY: number | undefined;

    const isFactory = mapType === 'FACTORY';
    const draggingDims = getCardDimensions(draggingCard, draggingCard.tipo === 'decisao', false);
    const draggingH = draggingDims.height;
    const draggingW = draggingDims.width;

    // V26: Trilho magnético — em Factory, travar no Y de qualquer vizinho dentro do threshold
    if (isFactory) {
      let bestDist = Infinity;
      for (const card of cards) {
        const cardDims = getCardDimensions(card, card.tipo === 'decisao', false);
        const cardH = cardDims.height;
        const cardCenterY = card.posicao.y + cardH / 2;
        const draggingCenterY = newY + draggingH / 2;
        const dist = Math.abs(draggingCenterY - cardCenterY);
        if (dist < FACTORY_Y_SNAP_THRESHOLD && dist < bestDist) {
          bestDist = dist;
          snapY = Math.round(cardCenterY - draggingH / 2);
        }
      }
      // V26: Poka-Yoke de espaçamento — gap entre 50–70px → forçar para 60px
      for (const card of cards) {
        const cardDims = getCardDimensions(card, card.tipo === 'decisao', false);
        const cardW = cardDims.width;
        // card está à esquerda do card arrastado
        const gapLeft = newX - (card.posicao.x + cardW);
        if (gapLeft >= FACTORY_GAP_SNAP_MIN && gapLeft <= FACTORY_GAP_SNAP_MAX) {
          snapX = card.posicao.x + cardW + FACTORY_GAP_TARGET;
          break;
        }
        // card está à direita do card arrastado
        const gapRight = card.posicao.x - (newX + draggingW);
        if (gapRight >= FACTORY_GAP_SNAP_MIN && gapRight <= FACTORY_GAP_SNAP_MAX) {
          snapX = card.posicao.x - draggingW - FACTORY_GAP_TARGET;
          break;
        }
      }
    }

    // Verificar alinhamento com outros cards (modo não-Factory)
    if (!isFactory) {
      for (const card of cards) {
        // Alinhamento horizontal (mesmo X)
        if (Math.abs(card.posicao.x - newX) < SNAP_THRESHOLD) {
          snapX = card.posicao.x;
        }
        
        // Alinhamento vertical (mesmo Y)
        if (Math.abs(card.posicao.y - newY) < SNAP_THRESHOLD) {
          snapY = card.posicao.y;
        }

        if (snapX !== undefined && snapY !== undefined) break;
      }
    }

    // Snap para grid (20px)
    if (snapToGridEnabled && !snapX) {
      const gridSize = 20;
      const snappedX = Math.round(newX / gridSize) * gridSize;
      if (Math.abs(newX - snappedX) < SNAP_THRESHOLD) {
        snapX = snappedX;
      }
    }

    if (snapToGridEnabled && !snapY) {
      const gridSize = 20;
      const snappedY = Math.round(newY / gridSize) * gridSize;
      if (Math.abs(newY - snappedY) < SNAP_THRESHOLD) {
        snapY = snappedY;
      }
    }

    setSnapGuides({ x: snapX, y: snapY });
    
    return {
      x: snapX !== undefined ? snapX : newX,
      y: snapY !== undefined ? snapY : newY
    };
  }, [activeCards, snapToGridEnabled, mapType]);

  const handleMove = useCallback((clientX: number, clientY: number) => {
    // Armazena a posição mais recente e agenda apenas um update por frame
    pendingMoveRef.current = { clientX, clientY };
    if (dragRafRef.current !== null) return;
    dragRafRef.current = requestAnimationFrame(() => {
      dragRafRef.current = null;
      const pending = pendingMoveRef.current;
      if (!pending) return;
      pendingMoveRef.current = null;
      const { clientX, clientY } = pending;

    const cardId = draggingCardIdRef.current;
    if (!cardId || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const totalScale = scale * (contentScale / 12);
    
    // Converter coordenadas de tela para coordenadas do canvas
    const canvasX = (clientX - rect.left - pan.x) / totalScale;
    const canvasY = (clientY - rect.top - pan.y) / totalScale;
    
    // Posição do card = posição do mouse no canvas - offset do clique dentro do card
    let newX = canvasX - dragOffsetRef.current.x;
    let newY = canvasY - dragOffsetRef.current.y;
    
    // Snap to grid (respeita toggle do usuário; Alt segurado = movimento livre/fino)
    // Padrão: toggle OFF → fineMove sempre ativo → comportamento livre tipo Alt para todos os cards.
    const fineMove = altKeyHeldRef.current || !snapToGridEnabled;
    if (snapToGridEnabled && !fineMove) {
      const GRID_SIZE = 20;
      newX = Math.round(newX / GRID_SIZE) * GRID_SIZE;
      newY = Math.round(newY / GRID_SIZE) * GRID_SIZE;
    }
    
    const card = getCards().find(c => c.id === cardId);
    if (!card) return;
    
    // Calcular snap guides (também respeita Alt para movimento fino)
    if (!fineMove) {
      const snapped = calculateSnapGuides(card, newX, newY);
      newX = snapped.x;
      newY = snapped.y;
    } else if (snapGuides.x !== undefined || snapGuides.y !== undefined) {
      setSnapGuides({ x: undefined, y: undefined });
    }
    
    // Snap vertical para centro da raia mais próxima (se raias ativas)
    if (swimLanesVisible && swimLanes.length > 0 && !fineMove) {
      const cardHeight = card.dimensions?.height ?? 160;
      newY = snapToNearestLane(newY, cardHeight, swimLanes);
    }

    // Nivelamento (OXOX): magnetiza no eixo da linha de informação tracejada em tempo real
    // → evita o "salto" ao soltar; o card já está exatamente onde o release vai gravar.
    // Alt desativa magnetização para permitir centralização manual em fluxos (Kanban etc).
    if (mapType === 'FACTORY' && (card as any).vsmIcon === 'nivelamento' && !fineMove) {
      const dims = card.dimensions ?? { width: 96, height: 40 };
      const centerX = newX + dims.width / 2;
      const centerY = newY + dims.height / 2;
      const infoConnections = processedConnections.filter(c => c.connectionType === 'vsm_info');
      const nearest = findNearestConnectionPoint(infoConnections, centerX, centerY, 60);
      if (nearest) {
        newX = Math.round(nearest.x - dims.width / 2);
        newY = Math.round(nearest.y - dims.height / 2);
      }
    }
    
    // Delta de movimento
    const deltaX = newX - card.posicao.x;
    const deltaY = newY - card.posicao.y;
    
    if (deltaX === 0 && deltaY === 0) return;

    // Caminhão atracado: o drag deve mover a SETA vertical pai (composite "seta de transporte").
    // Redirecionamos o update para o vsm_vertical_flow, gravando manualX/manualY equivalentes.
    const isDockedTransport = !!(card as any).transportDockParentId
      && ['caminhao','transporte'].includes((card as any).vsmIcon ?? '');
    if (isDockedTransport) {
      const parentId = (card as any).transportDockParentId as string;
      const currentCards = getCards();
      const parentFlow = currentCards.find(c => c.id === parentId);
      if (!parentFlow) return;
      // Geometria do dock: truckX = flowX + 20; truckY = flowY + max(16, flowHeight*0.28).
      const flowHeight = Math.max(80, Number((parentFlow as any).vsmFlowHeight) || VSM_VERTICAL_FLOW_DEFAULT_HEIGHT);
      const dockOffsetY = Math.max(16, flowHeight * 0.28);
      const flowX = newX - (VSM_VERTICAL_FLOW_WIDTH + VSM_TRANSPORT_DOCK_GAP_X);
      const flowY = newY - dockOffsetY;
      const updatedCards = currentCards.map(c => {
        if (c.id === parentId) {
          return {
            ...c,
            posicao: { x: flowX, y: flowY },
            vsmFlowManualX: flowX,
            vsmFlowManualY: flowY,
          } as CardData;
        }
        if (c.id === cardId) {
          return { ...c, posicao: { x: newX, y: newY } };
        }
        return c;
      });
      // FAST-PATH: durante o drag, escrever apenas em sheets sem disparar a
      // pipeline pesada (recalculateFlow + calculateMetrics + calculateProjectMetrics).
      // O flush completo ocorre uma única vez no handleCardDragEnd.
      useProjectStore.setState((state) => ({
        sheets: state.sheets.map(sheet =>
          sheet.id === state.activeSheetId
            ? { ...sheet, cards: updatedCards }
            : sheet
        ),
      }));
      didDragMoveRef.current = true;
      return;
    }
    
    // Mover card principal + cards selecionados (multi-select)
    const currentCards = getCards();
    const movedIds = new Set<string>();
    movedIds.add(cardId);
    if (!isolatedDragCardIdRef.current && selectedCardIds.size > 1 && selectedCardIds.has(cardId)) {
      selectedCardIds.forEach(id => movedIds.add(id));
    }
    
    const updatedCards = currentCards.map(c => {
      if (movedIds.has(c.id)) {
        const nextX = c.id === cardId ? newX : c.posicao.x + deltaX;
        const nextY = c.id === cardId ? newY : c.posicao.y + deltaY;
        const next: CardData = {
          ...c,
          posicao: { x: nextX, y: nextY },
        };
        // Vertical flow: ao arrastar manualmente, persistir X e Y como override do auto-centramento.
        if (isVsmVerticalFlowCard(c)) {
          (next as any).vsmFlowManualX = nextX;
          (next as any).vsmFlowManualY = nextY;
        }
        return next;
      }
      return c;
    });

    // FAST-PATH: durante o drag, escrever apenas em sheets sem disparar a
    // pipeline pesada (recalculateFlow + calculateMetrics + calculateProjectMetrics).
    // O flush completo ocorre uma única vez no handleCardDragEnd.
    useProjectStore.setState((state) => ({
      sheets: state.sheets.map(sheet =>
        sheet.id === state.activeSheetId
          ? { ...sheet, cards: updatedCards }
          : sheet
      ),
    }));
    didDragMoveRef.current = true;
    }); // end rAF
  }, [scale, contentScale, pan, selectedCardIds, getCards, setCards, calculateSnapGuides, swimLanesVisible, swimLanes, mapType, processedConnections]);

  const handleCardDragStart = useCallback((offsetX: number, offsetY: number, cardId: string) => {
    draggingCardIdRef.current = cardId;
    const draggedCard = getCards().find(c => c.id === cardId);
    const isCommunicationCard = mapType === 'FACTORY' && (draggedCard as any)?.vsmIcon === 'comunicacao';
    const effectiveSelectedCardIds = isCommunicationCard ? new Set<string>([cardId]) : selectedCardIds;
    isolatedDragCardIdRef.current = isCommunicationCard ? cardId : null;

    if (isCommunicationCard && (selectedCardIds.size !== 1 || !selectedCardIds.has(cardId))) {
      setSelectedCardId(cardId);
      setSelectedCardIds(new Set([cardId]));
    }
    
    // O offset vem em pixels de tela (posição do clique relativo ao canto do card no viewport)
    // Converter para coordenadas do canvas
    const totalScale = scale * (contentScale / 12);
    dragOffsetRef.current = {
      x: offsetX / totalScale,
      y: offsetY / totalScale
    };
    
    // Salvar posições iniciais de todos os cards selecionados (para undo futuro)
    const positions = new Map<string, { x: number; y: number }>();
    const currentCards = getCards();
    currentCards.forEach(c => {
      if (effectiveSelectedCardIds.has(c.id) || c.id === cardId) {
        positions.set(c.id, { ...c.posicao });
      }
    });
    dragStartPositionsRef.current = positions;
  }, [scale, contentScale, selectedCardIds, getCards, mapType, setSelectedCardId, setSelectedCardIds]);

  const handleCardDragEnd = useCallback(() => {
    const cardId = draggingCardIdRef.current;
    const card = cardId ? getCards().find(c => c.id === cardId) : null;
    // Os branches abaixo chamam setCards() (que dispara a pipeline completa de
    // métricas). Se nenhum deles for acionado, fazemos um flush único ao final
    // para reconciliar o fast-path do handleMove com recalculateFlow/calculateMetrics.
    let metricsFlushed = false;

    if (card && mapType === 'FACTORY' && (card as any).vsmIcon === 'comunicacao') {
      const bounds = getVsmCardBounds(card);
      const nearest = findConnectionPointForBounds(processedConnections, bounds) ??
        findNearestConnectionPoint(processedConnections, bounds.centerX, bounds.centerY, 58);
      if (nearest) {
        setCards(getCards().map(c => c.id === card.id ? {
          ...c,
          vsmBoundConnection: {
            sourceId: nearest.sourceId,
            targetId: nearest.targetId,
            connectionId: nearest.connectionId,
          },
        } as any : c));
        metricsFlushed = true;
      } else if ((card as any).vsmBoundConnection) {
        setCards(getCards().map(c => c.id === card.id ? {
          ...c,
          vsmBoundConnection: undefined,
        } as any : c));
        metricsFlushed = true;
      }
    }

    // Nivelamento (OXOX / Heijunka): apenas registra/limpa o binding.
    // A posição já foi magnetizada em tempo real pelo handleMove → sem salto ao soltar.
    if (card && mapType === 'FACTORY' && (card as any).vsmIcon === 'nivelamento') {
      const bounds = getVsmCardBounds(card);
      const infoConnections = processedConnections.filter(c => c.connectionType === 'vsm_info');
      const nearest = findConnectionPointForBounds(infoConnections, bounds, 28) ??
        findNearestConnectionPoint(infoConnections, bounds.centerX, bounds.centerY, 80);
      if (nearest) {
        setCards(getCards().map(c => c.id === card.id ? {
          ...c,
          vsmBoundConnection: {
            sourceId: nearest.sourceId,
            targetId: nearest.targetId,
            connectionId: nearest.connectionId,
          },
        } as any : c));
        metricsFlushed = true;
      } else if ((card as any).vsmBoundConnection) {
        setCards(getCards().map(c => c.id === card.id ? {
          ...c,
          vsmBoundConnection: undefined,
        } as any : c));
        metricsFlushed = true;
      }
    }

    // FLUSH: se houve drag real e nenhum branch acima já disparou setCards,
    // reconcilia a pipeline de métricas com as posições gravadas via fast-path.
    if (didDragMoveRef.current && !metricsFlushed) {
      setCards(getCards());
    }
    didDragMoveRef.current = false;

    draggingCardIdRef.current = null;
    isolatedDragCardIdRef.current = null;
    dragStartPositionsRef.current.clear();
    // Limpar snap guides ao terminar drag
    setSnapGuides({});
  }, [getCards, mapType, processedConnections, setCards]);

  const handleStartConnection = useCallback((id: string, handle: 'right' | 'bottom' | 'top' | 'left') => {
    if (engineeringTool === 'kanban_connection') {
      const cards = getCards();
      const clickedCard = cards.find(card => card.id === id);
      const clickedIsPullBuffer = !!clickedCard && clickedCard.tipo === 'inventory' && (
        ['supermercado', 'pulmao'].includes((clickedCard as any).vsmIcon ?? '') ||
        ((clickedCard as any).vsmIcon === 'estoque' && (clickedCard as any).vsmConvertedToIcon === 'supermercado')
      );
      const current = connectingSourceRef.current;

      if (!clickedIsPullBuffer) {
        kanbanConnectionStartedRef.current = true;
        connectingSourceRef.current = { id, handle, tool: engineeringTool };
        setConnectingSource({ id, handle, tool: engineeringTool });
        return;
      }

      if (current && current.id !== id) {
        handleFinishConnectionRef.current?.(id, handle);
        return;
      }

      if (!current && clickedIsPullBuffer) {
        kanbanConnectionStartedRef.current = false;
        connectingSourceRef.current = null;
        setConnectingSource(null);
        return;
      }
    }

    if (engineeringTool === 'kanban_connection' && !kanbanConnectionStartedRef.current) {
      kanbanConnectionStartedRef.current = true;
      connectingSourceRef.current = { id, handle, tool: engineeringTool };
      setConnectingSource({ id, handle, tool: engineeringTool });
      return;
    }

    const current = connectingSourceRef.current;
    if (current && current.id !== id) {
      if (engineeringTool === 'kanban_connection') {
        const cards = getCards();
        const currentCard = cards.find(card => card.id === current.id);
        const nextCard = cards.find(card => card.id === id);
        const getVisualCenterX = (card: CardData) => {
          const dims = getCardDimensions(card, card.tipo === 'decisao', false);
          if (viewMode !== 'future_simulated' && card.tipo === 'inventory' && (card as any).vsmIcon === 'estoque' && (card as any).vsmConvertedToIcon === 'supermercado') {
            const offset = ((card as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
            const marketW = Math.round(dims.width * 0.92);
            const marketLeft = Math.round(dims.width * 0.46) + (Number(offset.x) || 0);
            return card.posicao.x + marketLeft + marketW / 2;
          }
          return card.posicao.x + dims.width / 2;
        };
        if (currentCard && nextCard && getVisualCenterX(nextCard) > getVisualCenterX(currentCard)) {
          kanbanConnectionStartedRef.current = true;
          connectingSourceRef.current = { id, handle, tool: engineeringTool };
          setConnectingSource({ id, handle, tool: engineeringTool });
          return;
        }
      }
      // Usuário clicou um handle no card DESTINO → completar conexão passando o handle alvo
      handleFinishConnectionRef.current?.(id, handle);
      return;
    }
    if (engineeringTool === 'kanban_connection') {
      kanbanConnectionStartedRef.current = true;
    }
    connectingSourceRef.current = { id, handle, tool: engineeringTool };
    setConnectingSource({ id, handle, tool: engineeringTool });
  }, [engineeringTool, getCards]);

  // Ref estável para handleFinishConnection (evita dependência circular em handleStartConnection)
  const handleFinishConnectionRef = useRef<((targetId: string, targetHandle?: 'right' | 'bottom' | 'top' | 'left') => void) | null>(null);

  const handleFinishConnection = useCallback((targetId: string, targetHandle?: 'right' | 'bottom' | 'top' | 'left') => {
    const activeConnectingSource = connectingSourceRef.current ?? connectingSource;
    if (!activeConnectingSource) return;

    const cards = getCards();
    let sourceCard = cards.find(c => c.id === activeConnectingSource.id);
    let targetCard = cards.find(c => c.id === targetId);
    let normalizedConnectionSourceId = activeConnectingSource.id;
    let normalizedConnectionTargetId = targetId;
    const isKanbanConnectionTool = engineeringTool === 'kanban_connection' || activeConnectingSource.tool === 'kanban_connection' || kanbanConnectionStartedRef.current;
    const isPullSignalConnectionTool = engineeringTool === 'pull_signal_connection' || activeConnectingSource.tool === 'pull_signal_connection';

    // pull_signal_connection: cria seta vsm_pull_signal diretamente entre os dois cards clicados
    if (isPullSignalConnectionTool && sourceCard && targetCard && sourceCard.id !== targetCard.id) {
      const connId = `conn-pull-signal-${sourceCard.id}-${targetCard.id}-${Date.now()}`;
      const newConn = {
        id: connId,
        targetId: targetCard.id,
        connectionType: 'vsm_pull_signal' as const,
        manualCommand: true,
        sourceHandle: 'bottom' as const,
        targetHandle: 'top' as const,
      };
      const updatedCards = cards.map(c =>
        c.id === sourceCard!.id
          ? { ...c, conexoes_saida: [...(c.conexoes_saida ?? []), newConn] }
          : c.id === targetCard!.id
          ? { ...c, vsmPuxadorMaster: true } as CardData
          : c
      );
      setCards(updatedCards);
      setConnectingSource(null);
      connectingSourceRef.current = null;
      setManualArrowMode(false);
      setManualArrowSource(null);
      setManualArrowWaypoints([]);
      setEngineeringTool(null);
      recalculateFlowVolumes();
      return;
    }
    const isConvertedSupermarketCard = (card?: CardData) => !!card && card.tipo === 'inventory' && (card as any).vsmIcon === 'estoque' && (card as any).vsmConvertedToIcon === 'supermercado';
    const isPullBufferCard = (card?: CardData) => !!card && card.tipo === 'inventory' && (
      ['supermercado', 'pulmao'].includes((card as any).vsmIcon ?? '') ||
      isConvertedSupermarketCard(card)
    );

    if (isKanbanConnectionTool && sourceCard && targetCard && isPullBufferCard(sourceCard) && !isPullBufferCard(targetCard)) {
      const originalSourceCard = sourceCard;
      sourceCard = targetCard;
      targetCard = originalSourceCard;
      normalizedConnectionSourceId = sourceCard.id;
      normalizedConnectionTargetId = targetCard.id;
    }
    const getKanbanVisualCenterX = (card: CardData) => {
      const dims = getCardDimensions(card, card.tipo === 'decisao', false);
      if (viewMode !== 'future_simulated' && isConvertedSupermarketCard(card)) {
        const offset = ((card as any).vsmSupermarketOverlayOffset ?? { x: 0, y: 0 }) as { x?: number; y?: number };
        const marketW = Math.round(dims.width * 0.92);
        const marketLeft = Math.round(dims.width * 0.46) + (Number(offset.x) || 0);
        return card.posicao.x + marketLeft + marketW / 2;
      }
      return card.posicao.x + dims.width / 2;
    };

    if (isKanbanConnectionTool && sourceCard && targetCard && !isPullBufferCard(targetCard) && getKanbanVisualCenterX(sourceCard) <= getKanbanVisualCenterX(targetCard)) {
      const originalSourceCard = sourceCard;
      sourceCard = targetCard;
      targetCard = originalSourceCard;
      normalizedConnectionSourceId = sourceCard.id;
      normalizedConnectionTargetId = targetCard.id;
    }

    // POKA-YOKE: Validação de fluxo de material no modo Factory
    const currentMapType = useProjectStore.getState().mapType;
    if (currentMapType === 'FACTORY' && !isKanbanConnectionTool && sourceCard && targetCard) {
      const VSM_EXTERNAL_ICONS = ['externo', 'fornecedor', 'cliente'];
      const isVsmExternal = (c: CardData) => VSM_EXTERNAL_ICONS.includes((c as any).vsmIcon ?? '');

      const validMaterialFlowPairs: Array<[string, string]> = [
        ['card_inicial', 'inventory'],  // Fornecedor → Estoque MP
        ['inventory',    'atividade'],  // Estoque → Processo
        ['atividade',    'inventory'],  // Processo → Estoque PA
        ['inventory',    'card_final'], // Estoque PA → Entidade Externa
        ['atividade',    'atividade'],  // Processo → Processo (push direto)
        ['card_inicial', 'atividade'],  // Entidade Externa → Processo
        ['atividade',    'card_final'], // Processo → Entidade Externa
      ];
      const pairValid = validMaterialFlowPairs.some(
        ([src, tgt]) => sourceCard.tipo === src && targetCard.tipo === tgt
      );
      // VSM entities externas (vsmIcon: cliente/externo/fornecedor) podem ser
      // origem ou destino de qualquer nó de processo ou estoque no mapa fábrica
      const vsmExternalValid =
        isVsmExternal(targetCard) || isVsmExternal(sourceCard);

      if (!pairValid && !vsmExternalValid) {
        const srcLabel = isVsmExternal(sourceCard) ? 'Entidade Externa' : sourceCard.tipo;
        const tgtLabel = isVsmExternal(targetCard) ? 'Entidade Externa' : targetCard.tipo;
        showToast('error', `Fluxo inválido: ${srcLabel} → ${tgtLabel} não é permitido no mapa de fábrica`);
        setConnectingSource(null);
        connectingSourceRef.current = null;
        kanbanConnectionStartedRef.current = false;
        return;
      }
    }

    const connectionType: Connection['connectionType'] =
      currentMapType === 'FACTORY' ? 'push' : undefined;

    // POKA-YOKE V108.0: conexão para/de Entidade Externa (cliente/fornecedor/externo) → vsm_transport por padrão
    const isExternalEntity = (c: CardData) =>
      ['externo', 'fornecedor', 'cliente'].includes((c as any).vsmIcon ?? '');
    const isClientEntity = (c: CardData) => {
      const extType = String((c as any).extType ?? '').toUpperCase();
      return c.id === 'ext-cliente' || extType === 'CLIENTE' || c.tipo === 'card_final' || (c as any).vsmIcon === 'cliente';
    };
    const isExpedicaoToCliente =
      currentMapType === 'FACTORY' &&
      sourceCard && targetCard &&
      (sourceCard as any).vsmSubtype === 'expedition' &&
      (
        targetCard.tipo === 'card_final' ||
        (targetCard as any).vsmIcon === 'externo' ||
        (targetCard as any).vsmIcon === 'cliente'
      );
    const isExternalConnection =
      currentMapType === 'FACTORY' &&
      sourceCard && targetCard &&
      (isExternalEntity(sourceCard) || isExternalEntity(targetCard)) &&
      !isExpedicaoToCliente;
    const isPcpmCard = (card: CardData) =>
      isFactoryPcpmCard(card);
    const isPcpmProcessConnection =
      currentMapType === 'FACTORY' &&
      !isKanbanConnectionTool &&
      sourceCard && targetCard &&
      isPcpmCard(sourceCard) &&
      isFactoryProcessCard(targetCard);

    const isPcpmExternalConnection =
      currentMapType === 'FACTORY' &&
      !isKanbanConnectionTool &&
      sourceCard && targetCard &&
      ((isPcpmCard(sourceCard) && isExternalEntity(targetCard)) || (isExternalEntity(sourceCard) && isPcpmCard(targetCard)));
    const linkedPullBufferCard = isKanbanConnectionTool
      ? isPullBufferCard(sourceCard) ? sourceCard : isPullBufferCard(targetCard) ? targetCard : undefined
      : undefined;
    const kanbanLoopLeadTimeDays = Number((linkedPullBufferCard as any)?.vsmFixedLeadTimeDays ?? (linkedPullBufferCard as any)?.vsmBufferDays ?? (linkedPullBufferCard as any)?.vsmDias ?? 1.5) || 1.5;
    const pcpmCardForInfo = isPcpmExternalConnection && sourceCard && targetCard
      ? isPcpmCard(sourceCard) ? sourceCard : targetCard
      : null;
    const externalCardForInfo = isPcpmExternalConnection && sourceCard && targetCard
      ? isPcpmCard(sourceCard) ? targetCard : sourceCard
      : null;
    const normalizedPcpmInfoSource = isPcpmExternalConnection && sourceCard && targetCard
      ? externalCardForInfo && isClientEntity(externalCardForInfo) ? externalCardForInfo : pcpmCardForInfo
      : sourceCard;
    const normalizedPcpmInfoTarget = isPcpmExternalConnection && sourceCard && targetCard
      ? externalCardForInfo && isClientEntity(externalCardForInfo) ? pcpmCardForInfo : externalCardForInfo
      : targetCard;
    const pcpmInfoTargetIsLeft = !!(normalizedPcpmInfoSource && normalizedPcpmInfoTarget) &&
      normalizedPcpmInfoTarget.posicao.x < normalizedPcpmInfoSource.posicao.x;

    const connection = {
      id: isPcpmProcessConnection && sourceCard && targetCard
        ? `conn-pcpm-process-${sourceCard.id}-${targetCard.id}-${Date.now()}`
        : generateId(),
      targetId: isPcpmExternalConnection && normalizedPcpmInfoTarget ? normalizedPcpmInfoTarget.id : normalizedConnectionTargetId,
      manualCommand: true,
      sourceHandle: isPcpmExternalConnection
        ? (pcpmInfoTargetIsLeft ? 'left' as const : 'right' as const)
        : isKanbanConnectionTool
        ? 'left' as const
        : isPcpmProcessConnection
        ? 'bottom' as const
        : activeConnectingSource.handle,
      ...(isPcpmExternalConnection
        ? { targetHandle: pcpmInfoTargetIsLeft ? 'right' as const : 'left' as const }
        : isKanbanConnectionTool
        ? { targetHandle: 'right' as const }
        : isPcpmProcessConnection
        ? { targetHandle: 'top' as const }
        : targetHandle ? { targetHandle } : {}),
      ...(isPcpmExternalConnection
        ? { connectionType: 'vsm_info' as const }
        : isKanbanConnectionTool
        ? {
            connectionType: 'vsm_info' as const,
            kanbanLoopEnabled: true,
            kanbanSupermarketId: linkedPullBufferCard?.id,
            kanbanLoopLeadTimeDays,
            vsmInfoTransmission: 'Eletrônico' as const,
          }
        : isPcpmProcessConnection
        ? { connectionType: 'vsm_info' as const }
        : isExpedicaoToCliente
        ? { connectionType: 'vsm_transport_delivery' as const, frequencia: 'Diária', lote: 0 }
        : isExternalConnection
        ? { connectionType: 'vsm_transport' as const, frequencia: '', vsmTransportIcon: 'truck' as const }
        : connectionType ? { connectionType } : {}),
    };

    const connectionSourceId = isPcpmExternalConnection && normalizedPcpmInfoSource ? normalizedPcpmInfoSource.id : normalizedConnectionSourceId;
    const updatedCards = cards.map(c => {
      let nextCard = c;
      if (linkedPullBufferCard && c.id === linkedPullBufferCard.id) {
        nextCard = {
          ...nextCard,
          vsmFixedLeadTimeDays: kanbanLoopLeadTimeDays,
          vsmBufferDays: kanbanLoopLeadTimeDays,
          inventoryMode: 'coverage' as const,
        } as CardData;
      }
      return nextCard.id === connectionSourceId
        ? {
            ...nextCard,
            conexoes_saida: [
              // Dedupe: para PCPM→processo, remove conexões pré-existentes para o MESMO target
              // (evita duplicatas que ativam o trunk-merge e renderizam setas horizontais erradas)
              ...(isPcpmProcessConnection && targetCard
                ? nextCard.conexoes_saida.filter(conn =>
                    !(conn.connectionType === 'vsm_info' && conn.targetId === targetCard.id)
                  )
                : nextCard.conexoes_saida),
              connection,
            ],
          }
        : nextCard;
    });

    setCards(updatedCards);
    setConnectingSource(null);
    connectingSourceRef.current = null;
    kanbanConnectionStartedRef.current = false;
    recalculateFlowVolumes();

    if (isKanbanConnectionTool) {
      setEngineeringTool(null);
      if (linkedPullBufferCard) {
        setSelectedCardId(linkedPullBufferCard.id);
        setRightPanelMode('properties');
      }
    }

    // Abre painel de propriedades do card de expedição para configurar logística de entrega
    if (isExpedicaoToCliente && activeConnectingSource) {
      setSelectedCardId(activeConnectingSource.id);
      setRightPanelMode('properties');
    }
  }, [connectingSource, engineeringTool, getCards, setCards, recalculateFlowVolumes, showToast, setSelectedCardId, setRightPanelMode]);

  // Manter ref sincronizado com a versão mais recente
  handleFinishConnectionRef.current = handleFinishConnection;

  const handleUpdateCardData = useCallback((id: string, data: Partial<CardData>) => {
    if (isCurrentSnapshotReadOnly) {
      showToast('info', 'ATUAL bloqueado: consulte o estado original ou altere a camada OTIMIZAÇÃO.');
      return;
    }

    const rootParameterKeys = new Set(['vsmCT', 'vsmCTUnit', 'vsmCO', 'vsmDisp', 'vsmOp']);
    const touchesRootParameter = Object.keys(data).some((key) => rootParameterKeys.has(key));
    const hasLinkedReductionKaizen = postIts.some((postIt) => {
      if (postIt.type !== 'action') return false;
      const hasReductionImpact = postIt.impactos?.some((impact) => impact.targetNodeId === id && impact.reducao > 0);
      const textMentionsReduction = /redu[cç][aã]o|reducao/i.test(postIt.text ?? '');
      return Boolean(hasReductionImpact || (postIt.fixedToNodeId === id && textMentionsReduction));
    });

    if (viewMode === 'kaizen_work' && touchesRootParameter && !hasLinkedReductionKaizen) {
      showToast('warning', 'Modo Otimização preserva os parâmetros raiz do mapa atual. Use um Kaizen de Redução vinculado ao card.');
      return;
    }

    const currentCards = getCards();
    const currentCard = currentCards.find(card => card.id === id);
    const isStockToSupermarketConversion = viewMode === 'kaizen_work'
      && mapType === 'FACTORY'
      && currentCard?.tipo === 'inventory'
      && (currentCard as any).vsmIcon === 'estoque'
      && ((data as any).vsmIcon === 'supermercado' || (data as any).vsmConvertedToIcon === 'supermercado');

    if (isStockToSupermarketConversion && currentCard) {
      const originalInventory = Number((currentCard as any).vsmOriginalInventory ?? (currentCard as any).inventory) || 0;
      const originalUnit = String((currentCard as any).vsmOriginalInventoryUnit ?? (currentCard as any).vsmUnit ?? 'LE');
      const bufferDays = Math.max(0.1, Number((data as any).vsmBufferDays ?? (currentCard as any).vsmBufferDays ?? (currentCard as any).vsmDias ?? 1) || 1);

      const updatedCards = currentCards.map(card => {
        if (card.id === id) {
          return {
            ...card,
            ...data,
            vsmIcon: 'estoque',
            atividade: card.atividade ?? 'Estoque',
            inventoryMode: 'coverage',
            vsmBufferDays: bufferDays,
            vsmConvertedFromIcon: 'estoque',
            vsmConvertedToIcon: 'supermercado',
            vsmOriginalInventory: originalInventory,
            vsmOriginalInventoryUnit: originalUnit,
          } as CardData;
        }

        return card;
      });

      setCards(updatedCards);
      recalculateFlowVolumes();
      showToast('success', 'Supermercado indicado acima do estoque, sem alterar o estoque nem as setas existentes.');
      return;
    }

    updateCardData(id, data);
    recalculateFlowVolumes();
  }, [getCards, isCurrentSnapshotReadOnly, mapType, postIts, recalculateFlowVolumes, setCards, showToast, updateCardData, viewMode]);

  const handleYamazumiSync = useCallback(() => {
    const patches = activeActivityCards.flatMap((card) => {
      const targetColumnId = yamazumiAssignments[card.id] ?? UNASSIGNED_ROLE_ID;
      const targetColumn = yamazumiColumns.find((column) => column.id === targetColumnId);
      if (!targetColumn) return [];

      const nextQty = Math.max(1, Number(yamazumiFteByRole[targetColumnId] ?? 1));
      const currentColumnId = yamazumiBaseAssignments[card.id] ?? UNASSIGNED_ROLE_ID;
      const currentQty = Math.max(Number(card.resources?.[0]?.quantity) || Number(card.pessoasSimultaneas) || 1, 1);
      const roleChanged = targetColumnId !== currentColumnId;
      const qtyChanged = currentQty !== nextQty;

      return roleChanged || qtyChanged ? [{ card, targetColumn, nextQty }] : [];
    });

    if (patches.length === 0) {
      showToast('info', 'Nenhuma alteração Yamazumi para sincronizar');
      return;
    }

    patches.forEach(({ card, targetColumn, nextQty }) => {
      const isUnassigned = !targetColumn.roleId;
      handleUpdateCardData(card.id, {
        responsavel: isUnassigned ? '' : targetColumn.name,
        quem: isUnassigned ? '' : targetColumn.name,
        resources: targetColumn.roleId ? ([{ roleId: targetColumn.roleId, quantity: nextQty, role: targetColumn.name }] as any) : ([] as any),
        pessoasSimultaneas: nextQty,
      } as Partial<CardData>);
    });

    setYamazumiBaseAssignments(yamazumiAssignments);
    setYamazumiBaseFteByRole(yamazumiFteByRole);
    showToast('success', `${patches.length} atividade(s) sincronizadas com o mapa`);
  }, [activeActivityCards, yamazumiAssignments, yamazumiColumns, yamazumiFteByRole, yamazumiBaseAssignments, handleUpdateCardData, showToast]);

  const [yamazumiSaveBaselineToken, setYamazumiSaveBaselineToken] = useState(0);
  const [yamazumiHasSavedBaseline, setYamazumiHasSavedBaseline] = useState(false);
  const [yamazumiZoom, setYamazumiZoom] = useState(1.0);
  const [yamazumiDemandaPercent, setYamazumiDemandaPercent] = useState(0);
  const [yamazumiSavedBaselineKpis, setYamazumiSavedBaselineKpis] = useState<{ occupation: number; leadMinutes: number } | null>(null);

  // Current KPI snapshot used when saving baseline
  const yamazumiCurrentKpis = useMemo(() => {
    const assignedCols = yamazumiColumns.filter((c) => c.id !== UNASSIGNED_ROLE_ID);
    const totalsByColumn: Record<string, number> = {};
    for (const col of yamazumiColumns) totalsByColumn[col.id] = 0;
    for (const card of activeActivityCards) {
      const colId = yamazumiAssignments[card.id] ?? UNASSIGNED_ROLE_ID;
      totalsByColumn[colId] = (totalsByColumn[colId] ?? 0) + getCycleTimeMinutes(card);
    }
    const simulated = yamazumiDemandaPercent > 0 ? simularDemanda(totalsByColumn, yamazumiDemandaPercent) : totalsByColumn;
    let overallLoad = 0;
    let overallCapacity = 0;
    for (const col of assignedCols) {
      const fte = Math.max(1, Number(yamazumiFteByRole[col.id] ?? 1));
      overallLoad += simulated[col.id] ?? 0;
      overallCapacity += yamazumiShiftMinutes * fte;
    }
    const occupation = overallCapacity > 0 ? Math.round((overallLoad / overallCapacity) * 100) : 0;
    const leadMinutes = calcularLeadTime(simulated);
    return { occupation, leadMinutes };
  }, [yamazumiColumns, activeActivityCards, yamazumiAssignments, yamazumiDemandaPercent, yamazumiFteByRole, yamazumiShiftMinutes]);

  const handleYamazumiAutoBalance = useCallback(() => {
    const assignableCols = yamazumiColumns.filter((c) => c.id !== UNASSIGNED_ROLE_ID);
    if (assignableCols.length === 0) return;
    const sorted = [...activeActivityCards].sort((a, b) => getCycleTimeMinutes(b) - getCycleTimeMinutes(a));
    const loads: Record<string, number> = {};
    for (const col of assignableCols) loads[col.id] = 0;
    const next: Record<string, string> = {};
    for (const card of sorted) {
      const minCol = assignableCols.reduce((a, b) => (loads[a.id] ?? 0) <= (loads[b.id] ?? 0) ? a : b);
      next[card.id] = minCol.id;
      loads[minCol.id] = (loads[minCol.id] ?? 0) + getCycleTimeMinutes(card);
    }
    setYamazumiAssignments(next);
  }, [activeActivityCards, yamazumiColumns]);

  const handleYamazumiSaveBaseline = useCallback(() => {
    setYamazumiSaveBaselineToken((t) => t + 1);
    setYamazumiHasSavedBaseline(true);
    setYamazumiSavedBaselineKpis(yamazumiCurrentKpis);
  }, [yamazumiCurrentKpis]);

  const [syncingYamazumiToExecution, setSyncingYamazumiToExecution] = useState(false);
  const [yamazumiViewMode, setYamazumiViewMode] = useState<'planned' | 'real'>('planned');

  const { realLoadByRole: yamazumiRealLoadByRole, kpis: yamazumiRealKpis, loading: yamazumiRealLoading } = useYamazumiReal({
    organizationId: user?.organizationId ?? null,
    instanceId: null, // mapa não tem instância — busca dados de toda a org
    enabled: yamazumiViewMode === 'real',
  });

  const yamazumiKPIBarData = calcularKPIs(Object.values(yamazumiRealLoadByRole));
  const handleYamazumiSyncToExecution = useCallback(async () => {
    const orgId = organization?.id;
    if (!orgId) return;
    setSyncingYamazumiToExecution(true);
    try {
      const syncActivities = buildSyncActivities(
        activeActivityCards,
        yamazumiAssignments,
        Object.fromEntries((organization?.roles ?? []).map((r: { id: string }) => [r.id, r.id])),
      );
      // instanceId não disponível no contexto do mapa — sincroniza sem filtro de instância
      const result = await syncYamazumiToTasks(orgId, undefined, syncActivities);
      if (result.errors.length > 0) {
        showToast('warning', `Sincronizado com ${result.synced} tarefa(s). ${result.errors.length} erro(s).`);
      } else {
        showToast('success', `Atualizado — ${result.synced} tarefa(s) sincronizada(s)`);
      }
    } catch {
      showToast('error', 'Falha ao sincronizar com o workflow');
    } finally {
      setSyncingYamazumiToExecution(false);
    }
  }, [organization, activeActivityCards, yamazumiAssignments, showToast]);

  // ============================================================================
  // POST-IT HANDLERS
  // ============================================================================
  const handleCreatePostItOnCard = useCallback((cardId: string, clientX: number, clientY: number) => {
    // Converter coordenadas de tela para coordenadas do canvas
    const totalScale = scale * (contentScale / 12);
    let canvasX = 0;
    let canvasY = 0;
    if (canvasRef.current) {
      const rect = canvasRef.current.getBoundingClientRect();
      canvasX = (clientX - rect.left - pan.x) / totalScale;
      canvasY = (clientY - rect.top - pan.y) / totalScale;
    }

    // Calcular relativeOffset a partir do card pai
    let relOffset = { x: 0, y: 0 };
    if (cardId) {
      const parentCard = cards.find(c => c.id === cardId);
      if (parentCard) {
        const pos = parentCard.posicao || { x: 0, y: 0 };
        relOffset = { x: canvasX - pos.x, y: canvasY - pos.y };
      }
    }

    const newPostIt: PostIt = {
      id: generateId(),
      type: creatingPostItType,
      text: '',
      x: canvasX,
      y: canvasY,
      fixedToNodeId: cardId || '',
      relativeOffset: relOffset,
      color: getPostItColor(creatingPostItType),
      isMinimized: false,
      optimizationOnly: viewMode === 'kaizen_work' && creatingPostItType === 'action',
      impactos: creatingPostItType === 'action' && cardId
        ? [{ targetNodeId: cardId, tipo: 'TP', reducao: 0 }]
        : undefined,
    };
    setPostIts([...postIts, newPostIt]);
    setEditingPostItId(newPostIt.id);
    // Desativar modo de criação após inserir 1 post-it (single-shot)
    setIsCreatingPostIt(false);
  }, [creatingPostItType, postIts, setPostIts, scale, contentScale, pan, cards, viewMode]);

  const handlePostItClick = useCallback((id: string, e?: React.MouseEvent) => {
    if (e?.shiftKey) {
      setSelectedPostItIds(prev => {
        const newSet = new Set(prev);
        if (newSet.has(id)) {
          newSet.delete(id);
        } else {
          newSet.add(id);
        }
        return newSet;
      });
    } else {
      setSelectedPostItIds(new Set([id]));
      // Ativar modo click-to-link: clicar no post-it e depois clicar em um card
      // Se já estava no modo linking com este post-it, desativar
      if (linkingPostItId === id) {
        setLinkingPostItId(null);
      } else {
        setLinkingPostItId(id);
      }
    }
  }, [linkingPostItId]);

  const handlePostItStartDrag = useCallback((id: string, offsetX: number, offsetY: number) => {
    setDraggingPostIt({ id, offsetX, offsetY });
    // Também ativar linking mode para que o cursor mude
    setLinkingPostItId(id);
  }, []);

  const handlePostItDrag = useCallback((clientX: number, clientY: number) => {
    if (!draggingPostIt || !canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const totalScale = scale * (contentScale / 12);
    // offsetX/Y são em pixels de tela (distância do mouse ao canto do post-it)
    const canvasX = (clientX - draggingPostIt.offsetX - rect.left - pan.x) / totalScale;
    const canvasY = (clientY - draggingPostIt.offsetY - rect.top - pan.y) / totalScale;
    const updated = postIts.map(p =>
      p.id === draggingPostIt.id
        ? { ...p, x: canvasX, y: canvasY, fixedToNodeId: '', relativeOffset: { x: 0, y: 0 } }
        : p
    );
    setPostIts(updated);

    // Detectar snap-to-card: verificar se o post-it está sobre um card
    const POST_IT_WIDTH = 80;
    const POST_IT_HEIGHT = 72;
    const postItCenterX = canvasX + POST_IT_WIDTH / 2;
    const postItCenterY = canvasY + POST_IT_HEIGHT / 2;
    const SNAP_THRESHOLD = 40; // pixels de margem extra para detecção
    let foundSnapTarget: string | null = null;
    for (const card of cards) {
      if (card.status === 'excluido' || card.tipo === 'card_inicial' || card.tipo === 'card_final') continue;
      const cw = card.calculatedWidth || 240;
      const ch = 180;
      const cx = card.posicao.x;
      const cy = card.posicao.y;
      if (
        postItCenterX >= cx - SNAP_THRESHOLD &&
        postItCenterX <= cx + cw + SNAP_THRESHOLD &&
        postItCenterY >= cy - SNAP_THRESHOLD &&
        postItCenterY <= cy + ch + SNAP_THRESHOLD
      ) {
        foundSnapTarget = card.id;
        break;
      }
    }
    setPostItSnapTargetId(foundSnapTarget);
  }, [draggingPostIt, postIts, setPostIts, scale, contentScale, pan, cards]);



  const handlePostItEndDrag = useCallback(() => {
    // Se estava sobre um card (snap target), vincular o post-it ao card
    if (draggingPostIt && postItSnapTargetId) {
      linkPostItToCard(draggingPostIt.id, postItSnapTargetId);
    }
    setDraggingPostIt(null);
    setPostItSnapTargetId(null);
    setLinkingPostItId(null);
  }, [draggingPostIt, postItSnapTargetId, linkPostItToCard]);

  const handleCancelDrag = useCallback(() => {
    setDraggingPostIt(null);
    setPostItSnapTargetId(null);
    setLinkingPostItId(null);
  }, []);

  const handlePostItTextChange = useCallback((id: string, text: string) => {
    updatePostIt(id, { text });
  }, [updatePostIt]);

  const handleTogglePostItMinimize = useCallback((id: string) => {
    const postIt = postIts.find(p => p.id === id);
    if (postIt) {
      updatePostIt(id, { isMinimized: !postIt.isMinimized });
    }
  }, [postIts, updatePostIt]);

  // ============================================================================
  // RIBBON ACTIONS
  // ============================================================================
  const handleAddActivity = useCallback(() => {
    const currentCards = getCards();
    const isFactory = useProjectStore.getState().mapType === 'FACTORY';
    const newCard: CardData = {
      id: generateId(),
      tipo: 'atividade',
      atividade: 'Nova Atividade',
      status: 'ativo',
      posicao: { x: 400, y: 300 },
      conexoes_saida: [],
      p: 100,
      tp: 0,
      lt: 0,
      tt: 0,
      layoutType: isFactory ? 'classic' : currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      mapOrigin: isFactory ? 'FACTORY' : 'OFFICE',
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };

    addCard(newCard);
  }, [addCard, getCards]);

  const handleAddDecision = useCallback(() => {
    const currentCards = getCards();
    const isFactory = useProjectStore.getState().mapType === 'FACTORY';
    const newCard: CardData = {
      id: generateId(),
      tipo: 'decisao',
      atividade: 'Nova Decisão',
      status: 'ativo',
      posicao: { x: 400, y: 300 },
      conexoes_saida: [],
      decisao_sim_pct: 50,
      decisao_nao_pct: 50,
      layoutType: isFactory ? 'classic' : currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      mapOrigin: isFactory ? 'FACTORY' : 'OFFICE',
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };
    addCard(newCard);
  }, [addCard, getCards]);

  const handleAddStart = useCallback(() => {
    const currentCards = getCards();
    const isFactory = useProjectStore.getState().mapType === 'FACTORY';
    const newCard: CardData = {
      id: generateId(),
      tipo: 'card_inicial',
      atividade: 'Início',
      status: 'ativo',
      posicao: { x: 400, y: 300 },
      conexoes_saida: [],
      layoutType: isFactory ? 'classic' : currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      mapOrigin: isFactory ? 'FACTORY' : 'OFFICE',
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };
    addCard(newCard);
  }, [addCard, getCards]);

  const handleAddFinalCard = useCallback(() => {
    const currentCards = getCards();
    const isFactory = useProjectStore.getState().mapType === 'FACTORY';
    const newCard: CardData = {
      id: generateId(),
      tipo: 'card_final',
      atividade: 'Fim',
      status: 'ativo',
      posicao: { x: 400, y: 300 },
      conexoes_saida: [],
      layoutType: isFactory ? 'classic' : currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      mapOrigin: isFactory ? 'FACTORY' : 'OFFICE',
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };
    addCard(newCard);
  }, [addCard, getCards]);

  const handleAddInventory = useCallback(() => {
    const currentCards = getCards();
    const isFactory = useProjectStore.getState().mapType === 'FACTORY';
    const newCard: CardData = {
      id: generateId(),
      tipo: 'inventory',
      atividade: 'Estoque',
      status: 'ativo',
      posicao: { x: 400, y: 300 },
      conexoes_saida: [],
      layoutType: isFactory ? 'classic' : currentLayoutRef.current,
      layoutColor: currentColorRef.current,
      mapOrigin: isFactory ? 'FACTORY' : 'OFFICE',
      numeroSequencial: generateNextSequentialNumber(currentCards),
    };
    addCard(newCard);
  }, [addCard, getCards]);

  const handleAddPostIt = useCallback((type: PostItType) => {
    setCreatingPostItType(type);
    setIsCreatingPostIt(true);
  }, []);

  const handleDeleteSelected = useCallback(() => {
    if (selectedConnection) {
      setCards(getCards().map(card => card.id === selectedConnection.sourceId ? {
        ...card,
        conexoes_saida: (card.conexoes_saida ?? []).filter(conn => conn.id !== selectedConnection.connectionId),
      } : card));
      setSelectedConnection(null);
      return;
    }

    if (selectedCardIds.size > 0) {
      selectedCardIds.forEach(id => {
        handleDeleteTotal(id);
      });

      setSelectedCardIds(new Set());
      setSelectedCardId(null);
      return;
    }

    if (selectedCardId) {
      handleDeleteTotal(selectedCardId);
      setSelectedCardId(null);
    }
  }, [selectedConnection, getCards, setCards, selectedCardIds, handleDeleteTotal, setSelectedCardIds, setSelectedCardId]);

  // Tecla Delete: excluir cards selecionados
  useEffect(() => {
    const handleDeleteKey = (e: KeyboardEvent) => {
      if (e.key !== 'Delete') return;
      // Não interceptar se estiver editando texto
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

      if (selectedConnection) {
        e.preventDefault();
        setCards(getCards().map(card => card.id === selectedConnection.sourceId ? {
          ...card,
          conexoes_saida: (card.conexoes_saida ?? []).filter(conn => conn.id !== selectedConnection.connectionId),
        } : card));
        setSelectedConnection(null);
      } else if (selectedCardIds.size > 0) {
        e.preventDefault();
        selectedCardIds.forEach(id => handleDeleteTotal(id));
        setSelectedCardIds(new Set());
        setSelectedCardId(null);
      } else if (selectedCardId) {
        e.preventDefault();
        handleDeleteTotal(selectedCardId);
        setSelectedCardId(null);
      }
    };
    window.addEventListener('keydown', handleDeleteKey);
    return () => window.removeEventListener('keydown', handleDeleteKey);
  }, [selectedConnection, getCards, setCards, selectedCardIds, selectedCardId, handleDeleteTotal, setSelectedCardIds, setSelectedCardId]);

  const handleToggleGrid = useCallback(() => {
    setShowGrid(prev => !prev);
  }, []);

  const handleToggleSnap = useCallback(() => {
    setSnapToGridEnabled(prev => !prev);
  }, []);

  const handleSwimlanesApplied = useCallback((lanes: SwimLaneInfo[]) => {
    setSwimLanes(lanes);
    setSwimLanesVisible(true);
    showToast(`${lanes.length} raias criadas! Use o bot\u00e3o "Raias" no Ribbon para ligar/desligar.`, 'success');
  }, [showToast]);

  const handleToggleHeatmap = useCallback(() => {
    setHeatmapMode(!heatmapMode);
  }, [heatmapMode, setHeatmapMode]);

  const handleToggleLiveMode = useCallback(async () => {
    if (liveMode) {
      setLiveMode(false);
      return;
    }
    if (!linkedBlueprintId || !user?.organizationId) {
      showToast('Nenhum blueprint vinculado. Use "Publicar como Pré-Workflow" primeiro.', 'warning');
      return;
    }
    const { data, error } = await supabase
      .from('process_blueprints')
      .select('generated_template_id')
      .eq('id', linkedBlueprintId)
      .eq('organization_id', user.organizationId)
      .maybeSingle();
    if (error || !data?.generated_template_id) {
      showToast('Este blueprint ainda não gerou um workflow executável. Use "Publicar como Pré-Workflow" para criar o workflow.', 'warning');
      return;
    }
    setLiveTemplateId(data.generated_template_id);
    setLiveMode(true);
    showToast('Mapa Vivo ativado — dados em tempo real do workflow', 'success');
  }, [liveMode, linkedBlueprintId, user?.organizationId, showToast]);

  const handleTogglePageMode = useCallback((format?: 'a4-portrait' | 'a4-landscape' | 'a3-portrait' | 'a3-landscape') => {
    setPageMode(prev => ({
      enabled: format ? true : !prev.enabled,
      format: format || prev.format
    }));
  }, []);

  const handleFitToCanvas = useCallback(() => {
    const visibleCards = activeCards.filter(card => card.status !== 'cancelado');
    if (visibleCards.length === 0) {
      setScale(1);
      setPan({ x: 0, y: 0 });
      return;
    }

    const bounds = visibleCards.map(getVsmCardBounds);
    const minX = Math.min(...bounds.map(item => item.x));
    const minY = Math.min(...bounds.map(item => item.y));
    const maxX = Math.max(...bounds.map(item => item.x + item.width));
    const maxY = Math.max(...bounds.map(item => item.y + item.height));
    const boundsWidth = Math.max(1, maxX - minX);
    const boundsHeight = Math.max(1, maxY - minY);
    const padding = 72;
    const availableWidth = Math.max(1, viewportSize.width - padding * 2);
    const availableHeight = Math.max(1, viewportSize.height - padding * 2);
    const scaleFactor = contentScale / 12;
    const targetScale = Math.max(0.1, Math.min(1.25, availableWidth / (boundsWidth * scaleFactor), availableHeight / (boundsHeight * scaleFactor)));
    const totalScale = targetScale * scaleFactor;

    setScale(targetScale);
    setPan({
      x: Math.round((viewportSize.width - boundsWidth * totalScale) / 2 - minX * totalScale),
      y: Math.round((viewportSize.height - boundsHeight * totalScale) / 2 - minY * totalScale),
    });
  }, [activeCards, contentScale, viewportSize.height, viewportSize.width]);

  const handleResetZoom = useCallback(() => {
    handleFitToCanvas();
  }, [handleFitToCanvas]);

  const mapNavigationState = location.state as { selectedCardId?: string; focusOrigin?: boolean } | null;
  const shouldFocusOriginCard = Boolean(mapNavigationState?.focusOrigin && mapNavigationState?.selectedCardId);
  const originFocusTargetExists = Boolean(
    shouldFocusOriginCard &&
    mapNavigationState?.selectedCardId &&
    activeCards.some(card => card.id === mapNavigationState.selectedCardId),
  );
  const mapFitSignature = useMemo(() => {
    const visibleCards = activeCards.filter(card => card.status !== 'cancelado');
    if (visibleCards.length === 0) return 'empty';
    const bounds = visibleCards.map(card => {
      const cardBounds = getVsmCardBounds(card);
      return `${card.id}:${Math.round(cardBounds.x)},${Math.round(cardBounds.y)},${Math.round(cardBounds.width)},${Math.round(cardBounds.height)}`;
    });
    return `${mapType}:${viewMode}:${Math.round(viewportSize.width)}x${Math.round(viewportSize.height)}:${visibleCards.length}:${bounds.join('|')}`;
  }, [activeCards, mapType, viewMode, viewportSize.height, viewportSize.width]);
  const fitToCanvasRef = useRef(handleFitToCanvas);
  useEffect(() => {
    fitToCanvasRef.current = handleFitToCanvas;
  }, [handleFitToCanvas]);

  const autoFitOnEntryRef = useRef<{ routeKey: string; signature: string | null; attempts: number }>({
    routeKey: '',
    signature: null,
    attempts: 0,
  });
  useEffect(() => {
    const routeKey = location.key ?? location.pathname;
    if (autoFitOnEntryRef.current.routeKey !== routeKey) {
      autoFitOnEntryRef.current = { routeKey, signature: null, attempts: 0 };
    }
    if (originFocusTargetExists || activeCards.length === 0 || viewportSize.width <= 1 || viewportSize.height <= 1) return;
    if (autoFitOnEntryRef.current.signature === mapFitSignature || autoFitOnEntryRef.current.attempts >= 12) return;

    autoFitOnEntryRef.current.signature = mapFitSignature;
    autoFitOnEntryRef.current.attempts += 1;
    const timers = [80, 240, 520, 900].map(delay => window.setTimeout(() => fitToCanvasRef.current(), delay));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [activeCards.length, location.key, location.pathname, mapFitSignature, originFocusTargetExists, viewportSize.height, viewportSize.width]);

  const previousJourneySidebarOpenRef = useRef(showJourneySidebar);
  useEffect(() => {
    if (previousJourneySidebarOpenRef.current === showJourneySidebar) return;
    previousJourneySidebarOpenRef.current = showJourneySidebar;
    if (originFocusTargetExists || activeCards.length === 0 || viewportSize.width <= 1 || viewportSize.height <= 1) return;
    const timers = [120, 360, 720].map(delay => window.setTimeout(() => fitToCanvasRef.current(), delay));
    return () => timers.forEach(timer => window.clearTimeout(timer));
  }, [activeCards.length, originFocusTargetExists, showJourneySidebar, viewportSize.height, viewportSize.width]);

  // ── Search state ──────────────────────────────────────────────────────────
  const [searchResults, setSearchResults] = useState<string[]>([]);
  const [searchIndex, setSearchIndex] = useState(0);
  const [searchHighlightId, setSearchHighlightId] = useState<string | null>(null);
  const [searchGlowPhase, setSearchGlowPhase] = useState<'full' | 'border' | null>(null);

  // Smooth pan/zoom animation via RAF
  const animRafRef = useRef<number | null>(null);
  const animatePanTo = useCallback((targetPan: { x: number; y: number }, targetScale: number) => {
    if (animRafRef.current !== null) cancelAnimationFrame(animRafRef.current);
    const startTime = performance.now();
    const duration = 420; // ms
    // read current values at animation start
    const fromPan = { x: pan.x, y: pan.y };
    const fromScale = scale;
    const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t; // ease-in-out quad
    const step = (now: number) => {
      const raw = Math.min((now - startTime) / duration, 1);
      const t = ease(raw);
      const newPan = {
        x: fromPan.x + (targetPan.x - fromPan.x) * t,
        y: fromPan.y + (targetPan.y - fromPan.y) * t,
      };
      const newScale = fromScale + (targetScale - fromScale) * t;
      setScale(newScale);
      setPan(newPan);
      if (raw < 1) animRafRef.current = requestAnimationFrame(step);
      else animRafRef.current = null;
    };
    animRafRef.current = requestAnimationFrame(step);
  }, [pan, scale, setScale, setPan]);

  // Glow timer ref
  const glowTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const centerOnCard = useCallback((cardId: string, zoom?: number) => {
    const card = activeCards.find(c => c.id === cardId);
    if (!card) return;
    const targetScale = zoom ?? 1.2;
    const totalScale = targetScale * (contentScale / 12);
    const vw = viewportSize.width;
    const vh = viewportSize.height;
    const cardW = card.tipo === 'decisao' ? 160 : (card.calculatedWidth || 240);
    const cardH = 180;
    const cx = card.posicao.x + cardW / 2;
    const cy = card.posicao.y + cardH / 2;
    const targetPan = { x: vw / 2 - cx * totalScale, y: vh / 2 - cy * totalScale };
    animatePanTo(targetPan, targetScale);

    // Reset glow — clear instantly visible state, then fade-in on next tick
    if (glowTimerRef.current) clearTimeout(glowTimerRef.current);
    setSearchHighlightId(cardId);
    setSearchGlowPhase(null);
    requestAnimationFrame(() => {
      setSearchGlowPhase('full');
      // After 3s, transition glow away but keep border
      glowTimerRef.current = setTimeout(() => {
        setSearchGlowPhase('border');
        // After another 3s fully clear
        glowTimerRef.current = setTimeout(() => setSearchGlowPhase(null), 3000);
      }, 3000);
    });
  }, [activeCards, contentScale, viewportSize, animatePanTo]);

  const didHandleOriginCardRef = useRef(false);
  useEffect(() => {
    const selectedOriginCardId = mapNavigationState?.selectedCardId;
    if (!shouldFocusOriginCard || didHandleOriginCardRef.current || !selectedOriginCardId || activeCards.length === 0) return;
    const exists = activeCards.some(card => card.id === selectedOriginCardId);
    if (!exists) return;

    didHandleOriginCardRef.current = true;
    autoFitOnEntryRef.current = {
      routeKey: location.key ?? location.pathname,
      signature: mapFitSignature,
      attempts: 12,
    };
    setSelectedCardId(selectedOriginCardId);
    setSelectedCardIds(new Set([selectedOriginCardId]));
    setRightPanelMode('properties');
    window.setTimeout(() => centerOnCard(selectedOriginCardId, 1.2), 140);
    navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [activeCards, centerOnCard, location.key, location.pathname, location.search, mapFitSignature, mapNavigationState?.selectedCardId, navigate, setRightPanelMode, setSelectedCardId, setSelectedCardIds, shouldFocusOriginCard]);

  const computeSearchMatches = useCallback((query: string): string[] => {
    const raw = query.trim();
    if (!raw) return [];

    // ── Modo numérico: input puramente numérico ou prefixado com # ──────────
    const isNumericSearch = /^#?\d+$/.test(raw);
    if (isNumericSearch) {
      const num = parseInt(raw.replace(/^#/, ''), 10);
      return activeCards
        .filter(c => c.numeroSequencial === num)
        .map(c => c.id);
    }

    // ── Modo texto: busca multicampo ─────────────────────────────────────────
    const q = raw.toLowerCase();
    const roles = useProjectStore.getState().organization.roles;
    return activeCards
      .filter(c => {
        // Excluir card_inicial/card_final da busca geral — incluir só se o termo for explícito
        if (c.tipo === 'card_inicial' && !q.includes('inic')) return false;
        if (c.tipo === 'card_final' && !q.includes('fim') && !q.includes('final')) return false;
        return (
          (c.atividade ?? '').toLowerCase().includes(q) ||
          (c.sector ?? '').toLowerCase().includes(q) ||
          (c.sistema ?? '').toLowerCase().includes(q) ||
          (c.resources ?? []).some(r => {
            const role = roles.find(ro => ro.id === r.roleId);
            return role ? role.name.toLowerCase().includes(q) : false;
          })
        );
      })
      .map(c => c.id);
  }, [activeCards]);

  const handleSearch = useCallback((query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setSearchIndex(0);
      setSearchHighlightId(null);
      return;
    }
    const matches = computeSearchMatches(query);
    setSearchResults(matches);
    setSearchIndex(0);
    if (matches.length === 1) {
      // Match único: navegar instantaneamente com zoom de foco 1.2×
      centerOnCard(matches[0], 1.2);
    } else if (matches.length > 1) {
      centerOnCard(matches[0]);
    } else {
      setSearchHighlightId(null);
    }
  }, [computeSearchMatches, centerOnCard]);

  const handleSearchNext = useCallback(() => {
    if (searchResults.length === 0) return;
    const next = (searchIndex + 1) % searchResults.length;
    setSearchIndex(next);
    centerOnCard(searchResults[next]);
  }, [searchResults, searchIndex, centerOnCard]);

  const handleSearchPrev = useCallback(() => {
    if (searchResults.length === 0) return;
    const prev = (searchIndex - 1 + searchResults.length) % searchResults.length;
    setSearchIndex(prev);
    centerOnCard(searchResults[prev]);
  }, [searchResults, searchIndex, centerOnCard]);

  const handleSearchClear = useCallback(() => {
    setSearchResults([]);
    setSearchIndex(0);
    setSearchHighlightId(null);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale(prev => Math.min(prev * 1.2, 5));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  }, []);

  const handleTogglePanMode = useCallback(() => {
    setPanModeActive(prev => !prev);
  }, []);

  const handleToggleRulers = useCallback(() => {
    setShowRulers(prev => !prev);
  }, []);

  const handleToggleManualArrow = useCallback(() => {
    setManualArrowMode(prev => !prev);
    if (manualArrowMode) {
      setManualArrowSource(null);
      setManualArrowWaypoints([]);
      setConnectingSource(null);
      connectingSourceRef.current = null;
    }
  }, [manualArrowMode]);

  const handleCancelManualArrowCreation = useCallback(() => {
    setManualArrowMode(false);
    setManualArrowSource(null);
    setManualArrowWaypoints([]);
    setConnectingSource(null);
    connectingSourceRef.current = null;
  }, []);

  const handleToggleAnalysisMode = useCallback(() => {
    setAnalysisMode(prev => !prev);
  }, []);

  const handleLayoutChange = useCallback((layout: 'default' | 'postit' | 'minimal' | 'classic') => {
    setCurrentLayout(layout);
    // Aplicar layout aos cards selecionados
    if (selectedCardIds.size > 0) {
      selectedCardIds.forEach(id => updateCardData(id, { layoutType: layout }));
    } else if (selectedCardId) {
      updateCardData(selectedCardId, { layoutType: layout });
    }
  }, [selectedCardIds, selectedCardId, updateCardData]);

  const handleColorChange = useCallback((color: string) => {
    setCurrentColor(color);
    // Aplicar cor aos cards selecionados
    if (selectedCardIds.size > 0) {
      selectedCardIds.forEach(id => updateCardData(id, { layoutColor: color }));
    } else if (selectedCardId) {
      updateCardData(selectedCardId, { layoutColor: color });
    }
  }, [selectedCardIds, selectedCardId, updateCardData]);

  const handleOpenVisualSettings = useCallback(() => {
    setShowVisualSettingsModal(true);
  }, []);

  const handleOpenProjectSettings = useCallback(() => {
    setShowProjectSettingsModal(true);
  }, []);

  const handleOpenOrgManager = useCallback(() => {
    setShowOrgManagerModal(true);
  }, []);

  // ============================================================================
  // UTILITY
  // ============================================================================
  const getPostItColor = (type: PostItType): string => {
    const colors: Record<PostItType, string> = {
      idea: '#fef3c7',
      problem: '#fecaca',
      action: '#bbf7d0',
      data: '#ddd6fe',
      risk: '#fed7aa'
    };
    return colors[type];
  };

  const openContextMenu = useCallback((id: string, x: number, y: number) => {
    setPostItContextMenu({ id, x, y });
  }, []);

  const handleDeletePostIt = useCallback((id: string) => {
    setPostIts(postIts.filter(p => p.id !== id));
    setPostItContextMenu(null);
    if (selectedPostItIds.has(id)) {
      const next = new Set(selectedPostItIds);
      next.delete(id);
      setSelectedPostItIds(next);
    }
  }, [postIts, setPostIts, selectedPostItIds, setSelectedPostItIds]);

  const handleChangePostItType = useCallback((id: string, newType: PostItType) => {
    const colorMap: Record<PostItType, string> = {
      idea: '#fef3c7', problem: '#fecaca', action: '#bbf7d0', data: '#ddd6fe', risk: '#fed7aa'
    };
    setPostIts(postIts.map(p =>
      p.id === id ? { ...p, type: newType, color: colorMap[newType] } : p
    ));
    setPostItContextMenu(null);
  }, [postIts, setPostIts]);

  const handleDetachPostIt = useCallback((id: string) => {
    setPostIts(postIts.map(p => {
      if (p.id !== id || !p.fixedToNodeId) return p;
      const parent = cards.find(c => c.id === p.fixedToNodeId);
      if (!parent) return { ...p, fixedToNodeId: '', relativeOffset: { x: 0, y: 0 } };
      return {
        ...p,
        x: parent.x + p.relativeOffset.x,
        y: parent.y + p.relativeOffset.y,
        fixedToNodeId: '',
        relativeOffset: { x: 0, y: 0 },
      };
    }));
    setPostItContextMenu(null);
  }, [postIts, setPostIts, cards]);

  const handleOpenInMemo = useCallback((cardId: string, actionId?: string) => {
    // Implement memo opening
  }, []);

  const getActionsByNode = useCallback((cardId: string) => {
    return [];
  }, []);

  const handleRightPanelModeChange = useCallback((mode: RightPanelMode) => {
    setRightPanelMode(mode);
    // Mutual exclusion: opening RightPanel closes JourneyPanel
    if (mode !== 'none') setShowJourneySidebar(false);
    if (mode !== 'kaizen' && selectedPostItId) {
      setSelectedPostItId(null);
    }
  }, [selectedPostItId, setRightPanelMode, setSelectedPostItId]);

  const handleCloseRightPanel = useCallback(() => {
    setRightPanelMode('none');
    clearMapFilters();
  }, [clearMapFilters, setRightPanelMode]);

  const handleToggleDashboardMode = useCallback(() => {
    setDashboardMode(prev => prev === 'sidebar' ? 'fullscreen' : 'sidebar');
  }, []);

  const handleToggleSimulationPanel = useCallback(() => {
    setIsSimulationMode((prev) => {
      const next = !prev;
      setRightPanelMode('none');
      if (next) {
        createSimulationBaseline();
      }
      if (!next) {
        clearMapFilters();
      }
      return next;
    });
  }, [clearMapFilters, createSimulationBaseline]);

  const handleExitSimulationAndReset = useCallback(() => {
    setIsSimulationMode(false);
    setSimulationSuggestion(null);
    clearMapFilters();
  }, [clearMapFilters]);

  const animateViewport = useCallback(
    (targetScale: number, targetPan: { x: number; y: number }, duration = 800) => {
      const startScale = scale;
      const startPan = pan;
      const startTs = performance.now();
      const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

      const step = (now: number) => {
        const progress = Math.min(1, (now - startTs) / duration);
        const eased = easeOutCubic(progress);
        setScale(startScale + (targetScale - startScale) * eased);
        setPan({
          x: startPan.x + (targetPan.x - startPan.x) * eased,
          y: startPan.y + (targetPan.y - startPan.y) * eased,
        });
        if (progress < 1) requestAnimationFrame(step);
      };

      requestAnimationFrame(step);
    },
    [pan, scale],
  );

  const handleFocusBottleneck = useCallback((cardId: string) => {
    const targetCard = cards.find((card) => card.id === cardId);
    if (!targetCard) return;

    const cardWidth = targetCard.tipo === 'decisao' ? 160 : (targetCard.calculatedWidth || 240);
    const cardHeight = targetCard.tipo === 'decisao' ? 160 : 180;
    const centerX = targetCard.posicao.x + cardWidth / 2;
    const centerY = targetCard.posicao.y + cardHeight / 2;

    const availableWidth = Math.max(1, viewportSize.width);
    const availableHeight = Math.max(1, viewportSize.height);
    const targetScale = Math.max(1, Math.min(1.4, scale < 1 ? 1.1 : scale));
    const targetPan = {
      x: availableWidth / 2 - centerX * targetScale,
      y: availableHeight / 2 - centerY * targetScale,
    };

    animateViewport(targetScale, targetPan, 800);
  }, [animateViewport, cards, scale, viewportSize.height, viewportSize.width]);

  const handleTogglePathHighlight = useCallback((ids: string[] | null) => {
    setHighlightedPath(ids);
  }, [setHighlightedPath]);

  const handleSimulateInsightSolution = useCallback(
    (suggestion: { focusResource?: string; suggestedExtraFte?: number; suggestedWaitReductionPercent?: number }) => {
      const normalize = (value: string | undefined | null) =>
        (value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();

      const normalizedFocus = normalize(suggestion.focusResource);
      const focusCard =
        normalizedFocus.length > 0
          ? cards.find((card) => {
              if (card.tipo !== 'atividade' || card.status === 'removido') return false;
              const resourceLabel = normalize(card.quem || card.responsavel || card.atividade || '');
              return resourceLabel.includes(normalizedFocus) || normalizedFocus.includes(resourceLabel);
            }) ?? null
          : null;

      if (focusCard?.id) {
        setSelectedCardId(focusCard.id);
        setSelectedCardIds(new Set([focusCard.id]));
      }

      // Sandbox: sempre captura baseline antes de aplicar sugestão de simulação.
      createSimulationBaseline();

      setSimulationSuggestion({
        id: Date.now(),
        focusResource: suggestion.focusResource,
        focusCardId: focusCard?.id,
        suggestedExtraFte: suggestion.suggestedExtraFte ?? 1,
        suggestedWaitReductionPercent: suggestion.suggestedWaitReductionPercent ?? 20,
      });

      setDashboardFilterResetToken((prev) => prev + 1);
      setRightPanelMode('none');
      setIsSimulationMode(true);
    },
    [cards, createSimulationBaseline, setSelectedCardId],
  );

  const handleSelectPostIt = useCallback((id: string) => {
    setSelectedPostItId(id);
    setSelectedPostItIds(new Set([id]));
  }, [setSelectedPostItId, setSelectedPostItIds]);

  const handleReorderPostIts = useCallback((updated: PostIt[]) => {
    setPostIts(updated);
  }, [setPostIts]);

  const handleUpdatePostItData = useCallback((postItId: string, updates: Partial<PostIt>) => {
    if (isCurrentSnapshotReadOnly) {
      showToast('info', 'ATUAL bloqueado: ideias de melhoria e kaizens pertencem à camada OTIMIZAÇÃO.');
      return;
    }

    updatePostIt(postItId, updates);
  }, [isCurrentSnapshotReadOnly, showToast, updatePostIt]);

  const handleClearCardFilter = useCallback(() => {
    clearMapFilters();
  }, [clearMapFilters]);

  const handleToggleKaizenCollapsed = useCallback(() => {
    setIsKaizenCollapsed(prev => !prev);
  }, []);

  const handleKaizenFilterChange = useCallback((next: 'all' | PostItType) => {
    setKaizenFilter(next);
  }, []);

  const handleOpenActionsKaizen = useCallback(() => {
    return undefined;
  }, []);

  const handleCreateActionFromPostIt = useCallback((linkedPostItId?: string) => {
    void linkedPostItId;
  }, []);

  const handleOpenActionDetail = useCallback((actionId: string) => {
    void actionId;
  }, []);

  const handleGenerateFutureMap = useCallback(() => {
    return undefined;
  }, []);

  const onDropCardOnTarget = useCallback((cardType: string, targetId: string, isYesPath: boolean) => {
    if (VSM_CONNECTOR_DRAG_TYPES.has(cardType)) return;

    const currentCards = getCards();
    const targetCard = currentCards.find(c => c.id === targetId);
    if (!targetCard) return;

    const newCardId = generateId();

    // Resolve vsm_* drag types (from Ribbon) to canonical tipo + vsmIcon
    const VSM_DRAG_MAP: Record<string, any> = {
      vsm_estoque:      { tipo: 'inventory', vsmIcon: 'estoque',      atividade: 'Estoque',      isInventory: true },
      vsm_pulmao:       { tipo: 'inventory', vsmIcon: 'pulmao',       atividade: 'Pulmão',       isInventory: true },
      vsm_supermercado: { tipo: 'inventory', vsmIcon: 'supermercado', atividade: 'Supermercado', isInventory: true },
      vsm_fifo:         { tipo: 'inventory', vsmIcon: 'fifo',         atividade: 'FIFO',         isInventory: true },
      vsm_emergencia:   { tipo: 'inventory', vsmIcon: 'emergencia',   atividade: 'Emergência',   isInventory: true },
      vsm_processo:     { tipo: 'atividade', vsmIcon: 'processo',     atividade: 'Processo' },
      vsm_operador:     { tipo: 'atividade', vsmIcon: 'operador',     atividade: 'Operador' },
      vsm_databox:      { tipo: 'atividade', vsmIcon: 'databox',      atividade: 'Dados' },
      vsm_comunicacao:  { tipo: 'atividade', vsmIcon: 'comunicacao',  atividade: 'Comunicação' },
      vsm_info_manual:  { tipo: 'atividade', vsmIcon: 'info_manual',  atividade: 'Info Manual' },
      vsm_info_eletro:  { tipo: 'atividade', vsmIcon: 'info_eletro',  atividade: 'Info Eletrôn.' },
      vsm_empurrado:    { tipo: 'atividade', vsmIcon: 'empurrado',    atividade: 'Empurrado' },
      vsm_puxado:       { tipo: 'atividade', vsmIcon: 'puxado',       atividade: 'Puxado' },
      vsm_automatizado: { tipo: 'atividade', vsmIcon: 'automatizado', atividade: 'Auto' },
      vsm_empilhador:   { tipo: 'atividade', vsmIcon: 'empilhador',   atividade: 'Empilhador' },
      vsm_caminhao:     { tipo: 'atividade', vsmIcon: 'caminhao',     atividade: 'Caminhão' },
      vsm_transport:    { tipo: 'atividade', vsmIcon: 'transporte',   atividade: 'Transporte' },
      vsm_transporte:   { tipo: 'atividade', vsmIcon: 'transporte',   atividade: 'Transporte' },
      vsm_aviao:        { tipo: 'atividade', vsmIcon: 'aviao',        atividade: 'Avião' },
      vsm_barco:        { tipo: 'atividade', vsmIcon: 'barco',        atividade: 'Barco' },
      vsm_trem:         { tipo: 'atividade', vsmIcon: 'trem',         atividade: 'Trem' },
      vsm_kanban_prod:  { tipo: 'atividade', vsmIcon: 'kanban_prod',  atividade: 'K. Produção' },
      vsm_kanban_ret:   { tipo: 'atividade', vsmIcon: 'kanban_ret',   atividade: 'K. Retirada' },
      vsm_kanban_sinal: { tipo: 'atividade', vsmIcon: 'kanban_sinal', atividade: 'K. Sinal' },
      vsm_kanban_lote:  { tipo: 'atividade', vsmIcon: 'kanban_lote',  atividade: 'K. Lote' },
      vsm_kanban_posto: { tipo: 'atividade', vsmIcon: 'kanban_posto', atividade: 'K. Posto' },
      vsm_nivelamento:  { tipo: 'atividade', vsmIcon: 'nivelamento',  atividade: 'Nivelamento' },
      vsm_vaver:        { tipo: 'atividade', vsmIcon: 'vaver',        atividade: 'Vá Ver' },
      vsm_kaizen:       { tipo: 'atividade', vsmIcon: 'kaizen',       atividade: 'Kaizen' },
      vsm_entrega:      { tipo: 'atividade', vsmIcon: 'entrega',      atividade: 'Entrega' },
      vsm_produto:      { tipo: 'atividade', vsmIcon: 'produto',      atividade: 'Produto', vsmOrientation: 'vertical' },
      vsm_vertical_flow:{ tipo: 'vsm_vertical_flow', vsmIcon: 'vertical_flow', atividade: '' },
      vsm_cliente:      { tipo: 'card_final', vsmIcon: 'externo',     atividade: 'Cliente' },
      vsm_fornecedor:   { tipo: 'card_inicial', vsmIcon: 'externo',   atividade: 'Fornecedor' },
    };
    const vsmResolved = VSM_DRAG_MAP[cardType];
    const resolvedCardType = vsmResolved ? vsmResolved.tipo : cardType;
    const resolvedVsmIcon  = vsmResolved?.vsmIcon;
    const resolvedAtividade = vsmResolved?.atividade;
    const type = resolvedCardType as NodeType;

    if (isVsmTransportDrag(cardType) && isVsmVerticalFlowCard(targetCard)) {
      const dock = buildTransportDockPosition(targetCard);
      const transportIcon = cardType === 'vsm_caminhao' ? 'caminhao' : 'transporte';
      const existingTransport = currentCards.find(card => (card as any).transportDockParentId === targetCard.id && ['caminhao', 'transporte'].includes((card as any).vsmIcon ?? ''));

      if (existingTransport) {
        setCards(currentCards.map(card => card.id === existingTransport.id ? {
          ...card,
          posicao: dock,
          vsmIcon: transportIcon,
          atividade: card.atividade || 'Diária',
          vsmFrequencia: (card as any).vsmFrequencia || card.atividade || 'Diária',
        } : card));
        return;
      }

      const newTransportCard = {
        id: newCardId,
        tipo: 'atividade',
        atividade: 'Diária',
        vsmFrequencia: 'Diária',
        vsmIcon: transportIcon,
        transportDockParentId: targetCard.id,
        status: 'ativo',
        posicao: dock,
        conexoes_saida: [],
        layoutType: currentLayoutRef.current,
        layoutColor: currentColorRef.current,
        mapOrigin: 'FACTORY',
        numeroSequencial: generateNextSequentialNumber(currentCards),
      } as any;

      setCards(currentCards.map(card => card.id === targetCard.id ? { ...card, vsmTransportId: newCardId } : card).concat(newTransportCard));
      return;
    }

    if (cardType === 'vsm_vertical_flow') {
      const targetBounds = getVsmCardBounds(targetCard);
      const anchors = findVerticalFlowAnchors(currentCards, targetBounds.centerX, targetBounds.bottom + 80);
      const source = ['inventory'].includes(targetCard.tipo) ? anchors.source : targetCard;
      const target = targetCard.tipo === 'inventory' ? targetCard : anchors.target;
      const draftFlow = {
        id: newCardId,
        tipo: 'atividade',
        atividade: '',
        vsmIcon: 'vertical_flow',
        status: 'ativo',
        posicao: { x: targetBounds.centerX - VSM_VERTICAL_FLOW_WIDTH / 2, y: targetBounds.bottom + VSM_VERTICAL_FLOW_DOCK_GAP },
        conexoes_saida: [],
        vsmFlowSourceId: source?.id,
        vsmFlowTargetId: target?.id,
        vsmFlowHeight: VSM_VERTICAL_FLOW_DEFAULT_HEIGHT,
        layoutType: currentLayoutRef.current,
        layoutColor: currentColorRef.current,
        mapOrigin: 'FACTORY',
        numeroSequencial: generateNextSequentialNumber(currentCards),
      } as any;
      const geometry = buildVerticalFlowGeometry(draftFlow, currentCards.concat(draftFlow));
      setCards(currentCards.concat({ ...draftFlow, posicao: { x: geometry.x, y: geometry.y }, vsmFlowHeight: geometry.height }));
      return;
    }
    
    // Use getCardDimensions for accurate target dimensions (handles processo with/without databox)
    const _targetDims = getCardDimensions(targetCard, targetCard.tipo === 'decisao', false);
    const targetWidth  = _targetDims.width;
    const targetHeight = _targetDims.height;
    const effectiveTargetRight = targetCard.posicao.x + targetWidth;
    // For PD card: Y centering uses processo body area (top 48.3% of 207 ≈ 100px, center = 50px)
    // Other cards: full card height
    const PD_BODY_H = 100; // processo body portion of PD card
    const targetIsProcessoDataBox = targetCard.tipo === 'atividade' &&
      (targetCard as any).vsmIcon === 'processo' &&
      !!((targetCard as any).vsmCT || (targetCard as any).vsmCO || (targetCard as any).vsmDisp);
    const effectiveTargetHeight = targetIsProcessoDataBox ? PD_BODY_H : targetHeight;
    // Dimensões do novo card (resolvedCardType)
    const newCardWidth = type === 'decisao' ? 160 : type === 'inventory' ? 84 : resolvedVsmIcon === 'processo' ? 110 : 240;
    const newCardHeight = type === 'decisao' ? 160 : type === 'inventory' ? 56 : resolvedVsmIcon === 'processo' ? 90 : 180;
    
    // Constantes de espaçamento (mesmas do VsmCanvas ghost)
    const GRID_SIZE = 20;
    const HORIZONTAL_GAP = 36;  // VSM compacto
    const VERTICAL_GAP = 180;   // Espaçamento vertical quando há colisão (altura de 1 card atividade)
    const CARD_GAP = 180;       // Gap entre base de um card e topo do próximo (1 altura de card)
    const PARALLEL_VERTICAL_OFFSET = targetHeight + CARD_GAP; // Offset dinâmico: targetHeight + gap
    const ROW_SNAP_THRESHOLD = 100;
    
    // Função para verificar se posição está ocupada — usa dimensões reais de cada card
    const checkPositionOccupied = (x: number, y: number, width: number, height: number): boolean => {
      const margin = 20;
      return currentCards.some(card => {
        if (card.id === targetCard.id) return false;
        const _d = getCardDimensions(card, card.tipo === 'decisao', false);
        const cardW = _d.width;
        const cardH = _d.height;
        return !(x + width + margin < card.posicao.x || 
                 x - margin > card.posicao.x + cardW ||
                 y + height + margin < card.posicao.y || 
                 y - margin > card.posicao.y + cardH);
      });
    };
    
    let rawX: number, rawY: number;
    
    if (isYesPath === false) {
      // CAMINHO "NÃO": Direita + PARALLEL_VERTICAL_OFFSET abaixo da origem da decisão
      rawX = targetCard.posicao.x + targetWidth + HORIZONTAL_GAP;
      rawY = targetCard.posicao.y + PARALLEL_VERTICAL_OFFSET;
    } else {
      // CAMINHO "SIM" ou PADRÃO: À direita visual, mesma altura (centralizado verticalmente)
      rawX = effectiveTargetRight + HORIZONTAL_GAP;
      // processo (no-db): geometric center
      const effectiveNewHalfH = newCardHeight / 2;
      rawY = targetCard.posicao.y + effectiveTargetHeight / 2 - effectiveNewHalfH;
    }
    
    // Verificar se posição está ocupada - continuar descendo até encontrar posição livre
    {
      let attempts = 0;
      const maxAttempts = 20;
      while (checkPositionOccupied(rawX, rawY, newCardWidth, newCardHeight) && attempts < maxAttempts) {
        rawY += newCardHeight + CARD_GAP;
        attempts++;
      }
    }
    
    // Aplicar snap to grid (para VSM, desativar precisão vertical para alinhar centros exatos)
    const isVsmNode = Boolean(resolvedVsmIcon) || currentLayout === 'blueprint' || currentLayout === 'sketch';
    let snappedX = Math.round(rawX / GRID_SIZE) * GRID_SIZE;
    let snappedY = isVsmNode ? rawY : Math.round(rawY / GRID_SIZE) * GRID_SIZE;
    
    // REGRA DE ALINHAMENTO: Cards paralelos (isYesPath=false) NÃO devem alinhar com rows próximas
    // porque precisam manter o offset exato de 220px. Alinhamento só para fluxo principal.
    // Para VSM, nós pre-calculamos os centros exatos, NÃO force snap no TOPO!
    if (isYesPath !== false && !isVsmNode) {
      // Alinhar com row mais próxima se disponível (apenas para caminho "Sim" ou padrão)
      const nearbyRows = currentCards
        .map(c => c.posicao.y)
        .filter(cardY => Math.abs(cardY - snappedY) < ROW_SNAP_THRESHOLD);
      
      if (nearbyRows.length > 0) {
        const closestRow = nearbyRows.reduce((closest, rowY) =>
          Math.abs(rowY - snappedY) < Math.abs(closest - snappedY) ? rowY : closest
        );
        snappedY = closestRow;
      }
    }
    
    let newCard: CardData;
    
    switch (type) {
      case 'atividade':
        newCard = {
          id: newCardId,
          tipo: 'atividade',
          atividade: resolvedAtividade ?? 'Nova Atividade',
          vsmIcon: resolvedVsmIcon,
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          p: 100,
          tp: 0,
          lt: 0,
          tt: 0,
          layoutType: currentLayoutRef.current,
          layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards),
        };
        break;
      case 'decisao':
        newCard = {
          id: newCardId,
          tipo: 'decisao',
          atividade: 'Nova Decisão',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          decisao_sim_pct: 50,
          decisao_nao_pct: 50,
          layoutType: currentLayoutRef.current,
          layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards),
        };
        break;
      case 'card_inicial':
        newCard = {
          id: newCardId,
          tipo: 'card_inicial',
          atividade: resolvedAtividade ?? 'Início',
          vsmIcon: resolvedVsmIcon,
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: currentLayoutRef.current,
          layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards),
        };
        break;
      case 'card_final':
        newCard = {
          id: newCardId,
          tipo: 'card_final',
          atividade: resolvedAtividade ?? 'Fim',
          vsmIcon: resolvedVsmIcon,
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: currentLayoutRef.current,
          layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards),
        };
        break;
      case 'inventory':
        newCard = {
          id: newCardId,
          tipo: 'inventory',
          atividade: resolvedAtividade ?? 'Estoque',
          vsmIcon: resolvedVsmIcon ?? 'estoque',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: currentLayoutRef.current,
          layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards),
        };
        break;
      default:
        return;
    }

    if (isVsmInventoryDrag(cardType) && isVerticalLogisticsAnchor(targetCard)) {
      const belowY = targetCard.posicao.y + getCardDimensions(targetCard, targetCard.tipo === 'decisao', false).height + 120;
      newCard = {
        ...newCard,
        posicao: buildCenteredBelowPosition(targetCard, newCard, belowY),
      };
    }

    // Decisão NÃO: seta sai pelo bottom do losango. Decisão SIM / Padrão: sai pela direita.
    const sourceHandle = targetCard.tipo === 'decisao' && isYesPath === false ? 'bottom' : 'right';
    
    const connection: import('../types').Connection = {
      id: generateId(),
      targetId: newCardId,
      sourceHandle: sourceHandle as 'right' | 'bottom',
      connectionType: (targetCard.tipo === 'inventory' || type === 'inventory') ? 'material' : undefined,
    };

    const updatedCards = currentCards.map(c =>
      c.id === targetId
        ? { ...c, conexoes_saida: [...c.conexoes_saida, connection] }
        : c
    );

    const finalCards = [...updatedCards, newCard];
    setCards(finalCards);
  }, [getCards, setCards]);

  // Insere um novo card ANTES do target: ocupa a posicao do target, empurrando-o para a direita
  const onDropCardBeforeTarget = useCallback((cardType: string, targetId: string) => {
    if (VSM_CONNECTOR_DRAG_TYPES.has(cardType)) return;

    const currentCards = getCards();
    const targetCard = currentCards.find(c => c.id === targetId);
    if (!targetCard) return;

    const newCardId = generateId();
    
    const VSM_DRAG_MAP: Record<string, any> = {
      vsm_estoque:      { tipo: 'inventory', vsmIcon: 'estoque',      atividade: 'Estoque',      isInventory: true },
      vsm_pulmao:       { tipo: 'inventory', vsmIcon: 'pulmao',       atividade: 'Pulmão',       isInventory: true },
      vsm_supermercado: { tipo: 'inventory', vsmIcon: 'supermercado', atividade: 'Supermercado', isInventory: true },
      vsm_fifo:         { tipo: 'inventory', vsmIcon: 'fifo',         atividade: 'FIFO',         isInventory: true },
      vsm_emergencia:   { tipo: 'inventory', vsmIcon: 'emergencia',   atividade: 'Emergência',   isInventory: true },
      vsm_processo:     { tipo: 'atividade', vsmIcon: 'processo',     atividade: 'Processo' },
      vsm_operador:     { tipo: 'atividade', vsmIcon: 'operador',     atividade: 'Operador' },
      vsm_databox:      { tipo: 'atividade', vsmIcon: 'databox',      atividade: 'Dados' },
      vsm_comunicacao:  { tipo: 'atividade', vsmIcon: 'comunicacao',  atividade: 'Comunicação' },
      vsm_info_manual:  { tipo: 'atividade', vsmIcon: 'info_manual',  atividade: 'Info Manual' },
      vsm_info_eletro:  { tipo: 'atividade', vsmIcon: 'info_eletro',  atividade: 'Info Eletrôn.' },
      vsm_empurrado:    { tipo: 'atividade', vsmIcon: 'empurrado',    atividade: 'Empurrado' },
      vsm_puxado:       { tipo: 'atividade', vsmIcon: 'puxado',       atividade: 'Puxado' },
      vsm_automatizado: { tipo: 'atividade', vsmIcon: 'automatizado', atividade: 'Auto' },
      vsm_empilhador:   { tipo: 'atividade', vsmIcon: 'empilhador',   atividade: 'Empilhador' },
      vsm_caminhao:     { tipo: 'atividade', vsmIcon: 'caminhao',     atividade: 'Caminhão' },
      vsm_transport:    { tipo: 'atividade', vsmIcon: 'transporte',   atividade: 'Transporte' },
      vsm_transporte:   { tipo: 'atividade', vsmIcon: 'transporte',   atividade: 'Transporte' },
      vsm_aviao:        { tipo: 'atividade', vsmIcon: 'aviao',        atividade: 'Avião' },
      vsm_barco:        { tipo: 'atividade', vsmIcon: 'barco',        atividade: 'Barco' },
      vsm_trem:         { tipo: 'atividade', vsmIcon: 'trem',         atividade: 'Trem' },
      vsm_kanban_prod:  { tipo: 'atividade', vsmIcon: 'kanban_prod',  atividade: 'K. Produção' },
      vsm_kanban_ret:   { tipo: 'atividade', vsmIcon: 'kanban_ret',   atividade: 'K. Retirada' },
      vsm_kanban_sinal: { tipo: 'atividade', vsmIcon: 'kanban_sinal', atividade: 'K. Sinal' },
      vsm_kanban_lote:  { tipo: 'atividade', vsmIcon: 'kanban_lote',  atividade: 'K. Lote' },
      vsm_kanban_posto: { tipo: 'atividade', vsmIcon: 'kanban_posto', atividade: 'K. Posto' },
      vsm_nivelamento:  { tipo: 'atividade', vsmIcon: 'nivelamento',  atividade: 'Nivelamento' },
      vsm_vaver:        { tipo: 'atividade', vsmIcon: 'vaver',        atividade: 'Vá Ver' },
      vsm_kaizen:       { tipo: 'atividade', vsmIcon: 'kaizen',       atividade: 'Kaizen' },
      vsm_entrega:      { tipo: 'atividade', vsmIcon: 'entrega',      atividade: 'Entrega' },
      vsm_produto:      { tipo: 'atividade', vsmIcon: 'produto',      atividade: 'Produto', vsmOrientation: 'vertical' },
      vsm_vertical_flow:{ tipo: 'vsm_vertical_flow', vsmIcon: 'vertical_flow', atividade: '' },
      vsm_cliente:      { tipo: 'card_final', vsmIcon: 'externo',     atividade: 'Cliente' },
      vsm_fornecedor:   { tipo: 'card_inicial', vsmIcon: 'externo',   atividade: 'Fornecedor' },
    };
    
    const vsmResolved = VSM_DRAG_MAP[cardType];
    const resolvedCardType = vsmResolved ? vsmResolved.tipo : cardType;
    const resolvedVsmIcon  = vsmResolved?.vsmIcon;
    const resolvedAtividade = vsmResolved?.atividade;
    const type = resolvedCardType as NodeType;

    const INV_W_B: Record<string, number> = { fifo: 240, puxado: 104, nivelamento: 192, entrega: 114 };
    const INV_H_B: Record<string, number> = { fifo: 80,  puxado: 80,  nivelamento: 144, entrega: 86  };
    const targetWidth = targetCard.tipo === 'decisao' ? 160
      : targetCard.tipo === 'inventory' ? (INV_W_B[targetCard.vsmIcon || ''] ?? 84)
      : (targetCard.calculatedWidth || 240);
    const targetHeight = targetCard.tipo === 'decisao' ? 160
      : targetCard.tipo === 'inventory' ? (INV_H_B[targetCard.vsmIcon || ''] ?? 56)
      : 180;
    
    // Dimensões do novo card
    const newCardWidth = type === 'decisao' ? 160 : type === 'inventory' ? 84 : resolvedVsmIcon === 'processo' ? 110 : 240;
    const newCardHeight = type === 'decisao' ? 160 : type === 'inventory' ? 56 : resolvedVsmIcon === 'processo' ? 90 : 180;
    
    const HORIZONTAL_GAP = 36;
    // O espaco que precisamos abrir eh a largura do card NOVO que estamos inserindo
    const offset = newCardWidth + HORIZONTAL_GAP;
    const GRID_SIZE = 20;

    const isVsmNode = Boolean(resolvedVsmIcon) || currentLayoutRef.current === 'blueprint' || currentLayoutRef.current === 'sketch';

    const snappedX = Math.round(targetCard.posicao.x / GRID_SIZE) * GRID_SIZE;
    
    // Alinhar centers para nodes VSM, senão topo (snap to grid)
    let snappedY = Math.round(targetCard.posicao.y / GRID_SIZE) * GRID_SIZE;
    if (isVsmNode) {
       const targetIsProcessoDataBox = targetCard.tipo === 'atividade' && targetCard.vsmIcon === 'processo' && hasDataBoxField(targetCard);
       const effectiveTargetHeight = targetIsProcessoDataBox ? 90 : targetHeight; // PD_BODY_H = 90
       const effectiveNewHalfH = newCardHeight / 2;
       snappedY = targetCard.posicao.y + effectiveTargetHeight / 2 - effectiveNewHalfH;
    }

    let newCard: CardData;
    switch (type) {
      case 'decisao':
        newCard = { id: newCardId, tipo: 'decisao', atividade: 'Nova Decisao', status: 'ativo',
          posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], decisao_sim_pct: 50, decisao_nao_pct: 50,
          layoutType: currentLayoutRef.current, layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards) };
        break;
      case 'inventory':
        newCard = { id: newCardId, tipo: 'inventory', atividade: resolvedAtividade ?? 'Estoque', vsmIcon: resolvedVsmIcon, status: 'ativo',
          posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: currentLayoutRef.current, layoutColor: currentColorRef.current };
        break;
      case 'card_inicial':
      case 'card_final':
        newCard = { id: newCardId, tipo: type, atividade: resolvedAtividade ?? 'EXTERNO', vsmIcon: resolvedVsmIcon, extType: type === 'card_inicial' ? 'FORNECEDOR' : 'CLIENTE', status: 'ativo',
          posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: currentLayoutRef.current, layoutColor: currentColorRef.current };
        break;
      case 'atividade':
      default:
        newCard = { id: newCardId, tipo: 'atividade', atividade: resolvedAtividade ?? 'Nova Atividade', vsmIcon: resolvedVsmIcon, status: 'ativo',
          posicao: { x: snappedX, y: snappedY }, conexoes_saida: [],
          p: 100, tp: 0, lt: 0, tt: 0,
          layoutType: currentLayoutRef.current, layoutColor: currentColorRef.current,
          numeroSequencial: generateNextSequentialNumber(currentCards) };
    }

    // Novo card aponta para o target (que sera deslocado para a direita)
    (newCard as CardData).conexoes_saida = [{ id: generateId(), targetId }];

    // BFS: coleta target + todos os seus downstream para deslocar para a direita
    const cardsToShift = new Set<string>();
    const bfsQ = [targetId];
    while (bfsQ.length > 0) {
      const curr = bfsQ.shift()!;
      if (!cardsToShift.has(curr)) {
        cardsToShift.add(curr);
        const fc = currentCards.find(a => a.id === curr);
        if (fc) (fc.conexoes_saida || []).forEach(cn => {
          if (!cardsToShift.has(cn.targetId)) bfsQ.push(cn.targetId);
        });
      }
    }

    const updatedCards = currentCards.map(c => {
      // Deslocar target + downstream para a direita
      if (cardsToShift.has(c.id)) {
        return { ...c, posicao: { x: c.posicao.x + offset, y: c.posicao.y } };
      }
      // Predecessores do target passam a apontar para o novo card
      if (c.conexoes_saida && c.conexoes_saida.some(cn => cn.targetId === targetId)) {
        return {
          ...c,
          conexoes_saida: c.conexoes_saida.map(cn =>
            cn.targetId === targetId
              ? { ...cn, targetId: newCardId, id: generateId(), waypoints: [] }
              : cn
          ),
        };
      }
      return c;
    });

    setCards([...updatedCards, newCard]);
  }, [getCards, setCards]);

  const handleModifyConnection = useCallback((
    sourceId: string, connectionId: string, patch: Partial<import('../types').Connection>
  ) => {
    const currentCards = getCards();
    const updated = currentCards.map(c => {
      if (c.id !== sourceId) return c;
      return {
        ...c,
        conexoes_saida: c.conexoes_saida.map(conn =>
          conn.id === connectionId ? { ...conn, ...patch } : conn
        ),
      };
    });
    setCards(updated);
  }, [getCards, setCards]);

  const onDropCardOnCanvas = useCallback((type: string, x: number, y: number, targetCardId?: string) => {
    if (VSM_CONNECTOR_DRAG_TYPES.has(type)) return;

    // Tratar post-its arrastados da barra
    const postItTypes = ['action', 'problem', 'idea', 'data', 'risk'];
    if (postItTypes.includes(type)) {
      const GRID_SIZE = 20;
      const snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
      const snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
      // Se solto em cima de um card, vincular ao card e calcular relativeOffset
      let fixedToNodeId = '';
      let relativeOffset = { x: 0, y: 0 };
      if (targetCardId) {
        const parentCard = getCards().find(c => c.id === targetCardId);
        if (parentCard) {
          fixedToNodeId = targetCardId;
          relativeOffset = { x: snappedX - parentCard.posicao.x, y: snappedY - parentCard.posicao.y };
        }
      }
      const newPostIt: PostIt = {
        id: generateId(),
        type: type as PostItType,
        text: '',
        x: snappedX,
        y: snappedY,
        fixedToNodeId,
        relativeOffset,
        color: getPostItColor(type as PostItType),
        isMinimized: false,
        optimizationOnly: viewMode === 'kaizen_work' && type === 'action',
        impactos: type === 'action' && fixedToNodeId
          ? [{ targetNodeId: fixedToNodeId, tipo: 'TP', reducao: 0 }]
          : undefined,
      };
      setPostIts(prev => [...prev, newPostIt]);
      setEditingPostItId(newPostIt.id);
      return;
    }

    const cardType = type as NodeType;
    const newCardId = generateId();
    
    // Quando mapType é FACTORY, forçar layoutType 'classic' para renderizar formas VSM factory
    const effectiveLayout = useProjectStore.getState().mapType === 'FACTORY' ? 'classic' : currentLayoutRef.current;
    
    // Aplicar snap to grid para garantir posições alinhadas conforme regras do vsmGeometry.ts
    const GRID_SIZE = 20;
    const ROW_SNAP_THRESHOLD = 100; // Pixels de tolerância para alinhar com row existente
    
    let snappedX = Math.round(x / GRID_SIZE) * GRID_SIZE;
    let snappedY = Math.round(y / GRID_SIZE) * GRID_SIZE;
    
    // Tentar alinhar com a row (linha horizontal) mais próxima para formar grid estruturado
    const currentCards = getCards();
    const targetCard = targetCardId ? currentCards.find(card => card.id === targetCardId) : undefined;

    if (targetCard && isVsmTransportDrag(type) && isVsmVerticalFlowCard(targetCard)) {
      const dock = buildTransportDockPosition(targetCard);
      const transportIcon = type === 'vsm_caminhao' ? 'caminhao' : 'transporte';
      const existingTransport = currentCards.find(card => (card as any).transportDockParentId === targetCard.id && ['caminhao', 'transporte'].includes((card as any).vsmIcon ?? ''));

      if (existingTransport) {
        setCards(currentCards.map(card => card.id === existingTransport.id ? {
          ...card,
          posicao: dock,
          vsmIcon: transportIcon,
          atividade: card.atividade || 'Diária',
          vsmFrequencia: (card as any).vsmFrequencia || card.atividade || 'Diária',
        } : card));
        return;
      }

      const newTransportCard = {
        id: newCardId,
        tipo: 'atividade',
        atividade: 'Diária',
        vsmFrequencia: 'Diária',
        vsmIcon: transportIcon,
        transportDockParentId: targetCard.id,
        status: 'ativo',
        posicao: dock,
        conexoes_saida: [],
        p: 100,
        tp: 0,
        lt: 0,
        tt: 0,
        layoutType: effectiveLayout,
        layoutColor: currentColorRef.current,
        mapOrigin: 'FACTORY',
      } as any;
      setCards(currentCards.map(card => card.id === targetCard.id ? { ...card, vsmTransportId: newCardId } : card).concat(newTransportCard));
      return;
    }

    const nearbyRows = currentCards
      .map(c => {
        const dims = getCardDimensions(c, c.tipo === 'decisao', false);
        const h = c.tipo === 'decisao' ? dims.decisionSize : dims.height;
        const w = dims.width || 100;
        return { card: c, centerY: c.posicao.y + h / 2, halfW: w / 2 };
      })
      .filter(({ centerY }) => Math.abs(centerY - snappedY) < ROW_SNAP_THRESHOLD);
    
    if (nearbyRows.length > 0 && type !== 'vsm_vertical_flow') {
      // Find closest row by center Y
      const closestRow = nearbyRows.reduce((closest, row) =>
        Math.abs(row.centerY - snappedY) < Math.abs(closest.centerY - snappedY) ? row : closest
      );
      // Only snap Y if the drop X is NOT directly on top of the anchor card (use center X)
      const anchorCenterX = closestRow.card.posicao.x + closestRow.halfW;
      const xOverlap = Math.abs(anchorCenterX - snappedX) < closestRow.halfW + 60;
      if (!xOverlap) {
        const newCardDims = getCardDimensions({ tipo: cardType as any, vsmIcon: undefined } as any, cardType === 'decisao', false);
        const newH = cardType === 'decisao' ? newCardDims.decisionSize : newCardDims.height || 90;
        snappedY = Math.round(closestRow.centerY - newH / 2);
      }
    }
    
    let newCard: CardData | null = null;
    
    switch (cardType) {
      case 'atividade': {
        newCard = {
          id: newCardId,
          tipo: 'atividade',
          atividade: 'Nova Atividade',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          p: 100,
          tp: 0,
          lt: 0,
          tt: 0,
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: effectiveLayout === 'classic' ? 'FACTORY' : 'OFFICE',
        };
        break;
      }
      case 'decisao': {
        newCard = {
          id: newCardId,
          tipo: 'decisao',
          atividade: 'Nova Decisão',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          decisao_sim_pct: 50,
          decisao_nao_pct: 50,
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: effectiveLayout === 'classic' ? 'FACTORY' : 'OFFICE',
        };
        break;
      }
      case 'card_inicial': {
        newCard = {
          id: newCardId,
          tipo: 'card_inicial',
          atividade: 'Início',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: effectiveLayout === 'classic' ? 'FACTORY' : 'OFFICE',
        };
        break;
      }
      case 'card_final': {
        newCard = {
          id: newCardId,
          tipo: 'card_final',
          atividade: 'Fim',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: effectiveLayout === 'classic' ? 'FACTORY' : 'OFFICE',
        };
        break;
      }
      case 'inventory': {
        newCard = {
          id: newCardId,
          tipo: 'inventory',
          atividade: 'Estoque',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: effectiveLayout === 'classic' ? 'FACTORY' : 'OFFICE',
        };
        break;
      }
      // ── VSM-specific drops ────────────────────────────────────────────────
      case 'vsm_cliente': {
        newCard = { id: newCardId, tipo: 'card_final', vsmIcon: 'externo', atividade: 'CLIENTE', status: 'ativo', extType: 'CLIENTE', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current, mapOrigin: 'FACTORY' };
        break;
      }
      case 'vsm_fornecedor': {
        newCard = { id: newCardId, tipo: 'card_inicial', vsmIcon: 'externo', atividade: 'FORNECEDOR', status: 'ativo', extType: 'FORNECEDOR', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current, mapOrigin: 'FACTORY' };
        break;
      }
      case 'vsm_externo': {
        newCard = { id: newCardId, tipo: 'card_inicial', vsmIcon: 'externo', atividade: 'FORNECEDOR', status: 'ativo', extType: 'FORNECEDOR', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current, mapOrigin: 'FACTORY' };
        break;
      }
      case 'vsm_processo': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'processo', atividade: 'Processo', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_operador': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'operador', atividade: 'Operador', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_databox': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'databox', atividade: 'Dados', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_comunicacao': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'comunicacao', atividade: 'Comunicação', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_info_manual': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'info_manual', atividade: 'Info Manual', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_info_eletro': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'info_eletro', atividade: 'Info Eletrôn.', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_empurrado': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'empurrado', atividade: 'Empurrado', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_puxado': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'puxado', atividade: 'Puxado', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_automatizado': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'automatizado', atividade: 'Auto', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_empilhador': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'empilhador', atividade: 'Empilhador', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_caminhao': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'caminhao', atividade: 'Caminhão', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_transport':
      case 'vsm_transporte': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'transporte', atividade: 'Transporte', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_aviao': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'aviao', atividade: 'Avião', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_barco': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'barco', atividade: 'Barco', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_trem': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'trem', atividade: 'Trem', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_kanban_prod': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kanban_prod', atividade: 'K. Produção', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_kanban_ret': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kanban_ret', atividade: 'K. Retirada', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_estoque': {
        newCard = { id: newCardId, tipo: 'inventory', vsmIcon: 'estoque', atividade: 'Estoque', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_supermercado': {
        newCard = { id: newCardId, tipo: 'inventory', vsmIcon: 'supermercado', atividade: 'Supermercado', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_fifo': {
        newCard = { id: newCardId, tipo: 'inventory', vsmIcon: 'fifo', atividade: 'FIFO', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_emergencia': {
        newCard = { id: newCardId, tipo: 'inventory', vsmIcon: 'emergencia', atividade: 'Emergência', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_entrega': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'entrega', atividade: 'Entrega', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_produto': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'produto', atividade: 'Produto', vsmOrientation: 'vertical', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_vertical_flow': {
        const anchors = findVerticalFlowAnchors(currentCards, snappedX, snappedY);
        const draftFlow = {
          id: newCardId,
          tipo: 'atividade',
          vsmIcon: 'vertical_flow',
          atividade: '',
          status: 'ativo',
          posicao: { x: snappedX, y: snappedY },
          conexoes_saida: [],
          vsmFlowSourceId: anchors.source?.id,
          vsmFlowTargetId: anchors.target?.id,
          vsmFlowHeight: VSM_VERTICAL_FLOW_DEFAULT_HEIGHT,
          layoutType: effectiveLayout,
          layoutColor: currentColorRef.current,
          mapOrigin: 'FACTORY',
        } as any;
        const geometry = buildVerticalFlowGeometry(draftFlow, currentCards.concat(draftFlow));
        newCard = { ...draftFlow, posicao: { x: geometry.x, y: geometry.y }, vsmFlowHeight: geometry.height };
        break;
      }
      case 'vsm_kanban_lote': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kanban_lote', atividade: 'K. Lote', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_kanban_posto': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kanban_posto', atividade: 'K. Posto', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_kanban_sinal': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kanban_sinal', atividade: 'K. Sinal', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_nivelamento': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'nivelamento', atividade: 'Nivelamento', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_vaver': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'vaver', atividade: 'Vá Ver', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
      case 'vsm_kaizen': {
        newCard = { id: newCardId, tipo: 'atividade', vsmIcon: 'kaizen', atividade: 'Kaizen', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], p: 100, tp: 0, lt: 0, tt: 0, layoutType: effectiveLayout, layoutColor: currentColorRef.current, vsmOptimizationOnly: viewMode === 'kaizen_work' } as any;
        break;
      }
      case 'vsm_pulmao': {
        newCard = { id: newCardId, tipo: 'inventory', vsmIcon: 'pulmao', atividade: 'Pulmão', status: 'ativo', posicao: { x: snappedX, y: snappedY }, conexoes_saida: [], layoutType: effectiveLayout, layoutColor: currentColorRef.current };
        break;
      }
    }
    
    if (!newCard) return;

    let inventoryAnchor: CardData | undefined;
    if (isVsmInventoryDrag(type)) {
      inventoryAnchor = targetCard && isVerticalLogisticsAnchor(targetCard)
        ? targetCard
        : findVerticalLogisticsAnchorAbove(currentCards, x, y);

      if (inventoryAnchor) {
        newCard = {
          ...newCard,
          posicao: buildCenteredBelowPosition(inventoryAnchor, newCard, snappedY),
        };
      }
    }

    if (mapType === 'FACTORY' && type === 'vsm_comunicacao') {
      const communicationDims = getCardDimensions(newCard, false, false);
      const nearest = findNearestConnectionPoint(processedConnections, x, y, 86);
      if (nearest) {
        newCard = {
          ...newCard,
          posicao: {
            x: Math.round(nearest.x - communicationDims.width / 2),
            y: Math.round(nearest.y - communicationDims.height / 2),
          },
          vsmBoundConnection: {
            sourceId: nearest.sourceId,
            targetId: nearest.targetId,
            connectionId: nearest.connectionId,
          },
        } as any;
      }
    }

    if (mapType === 'FACTORY' && type === 'vsm_nivelamento') {
      const nivDims = getCardDimensions(newCard, false, false);
      const infoConnections = processedConnections.filter(c => c.connectionType === 'vsm_info');
      const nearest = findNearestConnectionPoint(infoConnections, x, y, 86);
      if (nearest) {
        newCard = {
          ...newCard,
          posicao: {
            x: Math.round(nearest.x - nivDims.width / 2),
            y: Math.round(nearest.y - nivDims.height / 2),
          },
          vsmBoundConnection: {
            sourceId: nearest.sourceId,
            targetId: nearest.targetId,
            connectionId: nearest.connectionId,
          },
        } as any;
      }
    }

    // ── Edge Interception: dividir edge existente se o card foi solto sobre ela ──
    // Apenas para cards de fluxo (não post-its, não card_inicial/card_final)
    const canInterceptEdge = cardType !== 'card_inicial' && cardType !== 'card_final' && type !== 'vsm_comunicacao' && type !== 'vsm_nivelamento';
    let edgeIntercepted = false;

    if (canInterceptEdge && processedConnections.length > 0) {
      const HIT_THRESHOLD = 30; // px — tolerância de detecção
      // Usar o ponto de drop diretamente (centro aproximado)
      const cardCX = snappedX;
      const cardCY = snappedY;

      // Encontra edge mais próxima do centro do card colocado
      let bestEdge: (typeof processedConnections)[number] | null = null;
      let bestDist = HIT_THRESHOLD;

      for (const conn of processedConnections) {
        const { sourcePoint: sp, targetPoint: tp } = conn;
        // Distância ponto → segmento de linha reta SP→TP
        const dx = tp.x - sp.x;
        const dy = tp.y - sp.y;
        const lenSq = dx * dx + dy * dy;
        if (lenSq === 0) continue;
        const t = Math.max(0, Math.min(1, ((cardCX - sp.x) * dx + (cardCY - sp.y) * dy) / lenSq));
        const closestX = sp.x + t * dx;
        const closestY = sp.y + t * dy;
        const dist = Math.sqrt((cardCX - closestX) ** 2 + (cardCY - closestY) ** 2);
        if (dist < bestDist) {
          bestDist = dist;
          bestEdge = conn;
        }
      }

      if (bestEdge) {
        edgeIntercepted = true;
        addCard(newCard);

        setTimeout(() => {
          const currentCards = getCards();
          // 1. Remover conexão original do sourceId → targetId
          // 2. Adicionar sourceId → newCardId  e  newCardId → targetId
          const updatedCards = currentCards.map(c => {
            if (c.id === bestEdge!.sourceId) {
              const filtered = c.conexoes_saida.filter(conn => conn.id !== bestEdge!.connectionId);
              const newConn1 = { id: generateId(), targetId: newCardId, sourceHandle: bestEdge!.sourceHandle };
              return { ...c, conexoes_saida: [...filtered, newConn1] };
            }
            return c;
          });
          // Adicionar conexão newCard → original target
          const withNewOut = updatedCards.map(c => {
            if (c.id === newCardId) {
              const newConn2 = { id: generateId(), targetId: bestEdge!.targetId, sourceHandle: 'right' as const };
              return { ...c, conexoes_saida: [...c.conexoes_saida, newConn2] };
            }
            return c;
          });
          setCards(withNewOut);
        }, 50);
      }
    }

    if (!edgeIntercepted) {
      if (inventoryAnchor && isVsmVerticalFlowCard(inventoryAnchor)) {
        const flowWithTarget = { ...inventoryAnchor, vsmFlowTargetId: newCardId } as CardData;
        const flowGeometry = buildVerticalFlowGeometry(flowWithTarget, currentCards.concat(newCard));
        const updatedFlow = {
          ...flowWithTarget,
          posicao: { x: flowGeometry.x, y: flowGeometry.y },
          vsmFlowHeight: flowGeometry.height,
        } as CardData;
        const centeredInventory = {
          ...newCard,
          posicao: buildCenteredBelowPosition(updatedFlow, newCard, newCard.posicao.y),
        };
        setCards(currentCards.map(card => card.id === inventoryAnchor!.id ? updatedFlow : card).concat(centeredInventory));
      } else if (['vsm_externo', 'vsm_fornecedor', 'vsm_cliente'].includes(type)) {
        // Vice-versa: entity dropped → re-center any nearby inventory below it.
        const entityCenterX = newCard.posicao.x + VSM_ENTITY_LEFT_OFFSET + VSM_ENTITY_RENDERED_WIDTH / 2;
        const nearbyInventory = currentCards
          .filter(c => c.tipo === 'inventory' && c.posicao.y > newCard.posicao.y)
          .filter(c => Math.abs(getVsmCardBounds(c).centerX - entityCenterX) <= 400)
          .sort((a, b) => Math.abs(getVsmCardBounds(a).centerX - entityCenterX) - Math.abs(getVsmCardBounds(b).centerX - entityCenterX));
        if (nearbyInventory.length > 0) {
          const inv = nearbyInventory[0];
          const invBounds = getVsmCardBounds(inv);
          const targetX = Math.round(entityCenterX - invBounds.width / 2);
          const updatedInventory = { ...inv, posicao: { ...inv.posicao, x: targetX } };
          setCards([...currentCards.map(c => c.id === inv.id ? updatedInventory : c), newCard]);
        } else {
          addCard(newCard);
        }
      } else {
        addCard(newCard);
      }
    }
    
    // Auto-conexão após card inserido (Apenas se o modo não for FACTORY)
    // Suporta fluxo bidirecional: da esquerda para direita E da direita para esquerda.
    if (!edgeIntercepted && useProjectStore.getState().mapType !== 'FACTORY') {
      setTimeout(() => {
        const currentCards = getCards();
        const HORIZONTAL_RANGE = 800;
        const ROW_TOLERANCE = 80; // px — só conecta cards na mesma linha horizontal

        const sameRowCards = currentCards.filter(c =>
          c.status === 'ativo' &&
          c.id !== newCardId &&
          Math.abs(c.posicao.x - snappedX) < HORIZONTAL_RANGE &&
          Math.abs(c.posicao.y - snappedY) < ROW_TOLERANCE
        );

        // Card imediatamente à esquerda
        const leftCards = sameRowCards.filter(c => c.posicao.x < snappedX && c.tipo !== 'card_final');
        const nearestLeft = leftCards.sort((a, b) => b.posicao.x - a.posicao.x)[0];

        // Card imediatamente à direita
        const rightCards = sameRowCards.filter(c => c.posicao.x > snappedX && c.tipo !== 'card_inicial');
        const nearestRight = rightCards.sort((a, b) => a.posicao.x - b.posicao.x)[0];

        let updatedCards = currentCards;

        if (nearestLeft && cardType !== 'card_inicial') {
          const conn = { id: generateId(), targetId: newCardId, sourceHandle: 'right' as const };
          updatedCards = updatedCards.map(c =>
            c.id === nearestLeft.id ? { ...c, conexoes_saida: [...c.conexoes_saida, conn] } : c
          );
        }

        if (nearestRight && cardType !== 'card_final') {
          const conn = { id: generateId(), targetId: nearestRight.id, sourceHandle: 'right' as const };
          updatedCards = updatedCards.map(c =>
            c.id === newCardId ? { ...c, conexoes_saida: [...c.conexoes_saida, conn] } : c
          );
        }

        if (updatedCards !== currentCards) setCards(updatedCards);
      }, 50);
    }
  }, [getCards, addCard, setCards, setPostIts, setEditingPostItId, getPostItColor, processedConnections, viewMode]);

  const onUpdateConnectorPosition = useCallback((id: string, x: number, y: number) => {
    setPageConnectors(prev => prev.map(c => c.id === id ? { ...c, x, y } : c));
  }, []);

  const onUpdateConnectorLabel = useCallback((id: string, label: string) => {
    setPageConnectors(prev => prev.map(c => c.id === id ? { ...c, label } : c));
  }, []);

  const onDeleteConnector = useCallback((id: string) => {
    setPageConnectors(prev => prev.filter(c => c.id !== id));
  }, []);

  // ============================================================================
  // FILE OPERATION HANDLERS
  // ============================================================================
  const handleGoHome = useCallback(() => {
    navigate('/centro-operacional');
  }, [navigate]);

  // ── Gerar Plano de Ação a partir da camada Otimização ──────────────
  const handleGenerateActionPlan = useCallback(async () => {
    const orgId = (organization as any)?.id ?? user?.organizationId ?? user?.organization_id;
    if (!orgId) {
      showToast('error', 'Organização não encontrada. Faça login novamente.');
      return;
    }

    const storeState = useProjectStore.getState();
    let activeProjectId = selectedProject?.id ?? resolvedProjectId ?? zustandCurrentProjectId ?? null;
    const mapName = selectedProject?.nome_projeto || selectedProject?.name || storeState.currentMapDisplayName || 'Mapa VSM';
    const clientName = String((organization as any)?.name || user?.organizationName || selectedProject?.cliente || selectedProject?.clientName || mapName).trim();
    const optimizationSnapshot = viewMode === 'kaizen_work'
      ? { cards: getCards(), postIts }
      : storeState._modeSnapshots.kaizen;
    const futureSnapshot = storeState._modeSnapshots.future;
    const currentSnapshot = storeState._modeSnapshots.current;
    const optimizationCards = optimizationSnapshot?.cards ?? getCardsWithSimulation();
    const futureCards = futureSnapshot?.cards ?? [];
    const generationCards = Array.from(
      new Map(
        [
          ...(currentSnapshot?.cards ?? cards),
          ...optimizationCards,
          ...futureCards,
        ].map((card) => [card.id, card]),
      ).values(),
    );

    type OptimizationActionDraft = {
      sourceKey: string;
      title: string;
      description: string;
      priority: 'low' | 'medium' | 'high' | 'urgent';
      linkedNodeId?: string | null;
      impact?: number;
      effort?: number;
      metadata: Record<string, unknown>;
    };

    const getCardName = (card?: CardData | null) =>
      card?.atividade || card?.nome_fluxo || (card as any)?.vsmNome || card?.id || 'Processo';
    const normalizeText = (value: unknown) => String(value ?? '').trim().toLowerCase();
    const includesAny = (value: unknown, terms: string[]) => {
      const normalized = normalizeText(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
      return terms.some(term => normalized.includes(term));
    };
    const buildDraft = (
      kind: 'smed' | 'flow-cell' | 'supermarket' | 'buffer' | 'heijunka',
      componentId: string,
      componentLabel: string,
      linkedNodeId?: string,
    ): OptimizationActionDraft => {
      const configs: Record<typeof kind, { title: string; description: string; priority: OptimizationActionDraft['priority'] }> = {
        smed: {
          title: 'Executar Workshop de SMED para eliminação de Setup',
          description: 'Executar workshop SMED com vídeo-análise do setup atual, separar atividades internas/externas, padronizar sequência e validar redução de tempo no piloto.',
          priority: 'urgent',
        },
        'flow-cell': {
          title: 'Desenhar Trabalho Padronizado para a nova Célula de Fluxo Contínuo',
          description: 'Definir trabalho padronizado da célula, balancear operações por takt e registrar instruções operacionais para estabilidade do fluxo contínuo.',
          priority: 'high',
        },
        supermarket: {
          title: 'Dimensionar e demarcar fisicamente o Supermercado de Puxada',
          description: 'Dimensionar estoque-alvo, definir limites visuais e implantar regras de reposição para operação puxada do supermercado.',
          priority: 'high',
        },
        buffer: {
          title: 'Estabelecer nível de controle do Pulmão Intermediário (Buffer)',
          description: 'Definir parâmetros do pulmão intermediário, limites de controle e gatilhos de reposição para absorver variabilidade sem ruptura.',
          priority: 'medium',
        },
        heijunka: {
          title: 'Instalar e parametrizar o Quadro Físico/Visual Heijunka (OXOX)',
          description: 'Instalar quadro heijunka, parametrizar sequência OXOX e validar rotina de nivelamento para estabilizar mistura e cadência de produção.',
          priority: 'high',
        },
      };

      const config = configs[kind];
      const sourceComponentId = `${kind}:${componentId}`;

      return {
        sourceKey: sourceComponentId,
        title: config.title,
        description: [
          `Origem: geração automática do Mapa Futuro VSM no projeto "${mapName}".`,
          `Componente: ${componentLabel}.`,
          config.description,
          'Como: definir responsável, prazo e evidência antes da conclusão.',
        ].join('\n'),
        priority: config.priority,
        linkedNodeId: linkedNodeId ?? componentId,
        impact: 80,
        effort: 50,
        metadata: {
          source: 'vsm_dynamic_generation',
          sourceComponentId,
          mapName,
        },
      };
    };

    const buildMacroDraft = (
      sourceComponentId: 'vsm_cell_macro' | 'vsm_heijunka_box' | 'vsm_buffer_intermediate' | 'vsm_supermarket_input' | 'vsm_supermarket_output',
      title: string,
      componentLabel: string,
      linkedNodeId?: string,
    ): OptimizationActionDraft => ({
      sourceKey: sourceComponentId,
      title,
      description: [
        `Origem: transformação estrutural do Mapa Futuro VSM no projeto "${mapName}".`,
        `Componente: ${componentLabel}.`,
        'Como: definir responsável, prazo e evidência antes da conclusão.',
      ].join('\n'),
      priority: 'high',
      linkedNodeId,
      impact: 80,
      effort: 50,
      metadata: {
        source: 'vsm_dynamic_generation',
        sourceComponentId,
        mapName,
      },
    });

    const generateVsmActions = (cardsSnapshot: CardData[]): OptimizationActionDraft[] => {
      const drafts: OptimizationActionDraft[] = [];
      const supermarkets: CardData[] = [];
      const flowCellGroups = new Map<string, { anchor: CardData; hasSolda: boolean; hasMontagem: boolean; count: number }>();
      let hasCellMacro = false;
      let hasHeijunkaBox = false;
      let hasBufferIntermediate = false;
      let cellMacroAnchor: CardData | null = null;
      let heijunkaAnchor: CardData | null = null;
      let bufferAnchor: CardData | null = null;

      cardsSnapshot.forEach((card) => {
        const projection = (card as any).vsmKaizenProjection ?? {};
        const icon = normalizeText((card as any)?.vsmConvertedToIcon || (card as any)?.vsmIcon || card.tipo);
        const rootIcon = normalizeText((card as any)?.vsmIcon || card.tipo);
        const convertedToIcon = normalizeText((card as any)?.vsmConvertedToIcon);
        const cardName = getCardName(card);
        const titleText = `${projection?.title ?? ''} ${projection?.kaizenTitle ?? ''} ${projection?.improvementType ?? ''} ${cardName}`;
        const cardIdentity = `${card.id} ${cardName} ${(card as any)?.vsmFlowCellId ?? ''} ${(card as any)?.vsmFlowCellLabel ?? ''}`;
        const referencesSolda = includesAny(cardIdentity, ['solda', 'soldagem', 'welding']);
        const referencesMontagem = includesAny(cardIdentity, ['montagem', 'assembly']);
        const isUnifiedCell =
          icon === 'celula' ||
          Boolean((card as any)?.vsmFlowCellId) ||
          Boolean((card as any)?.vsmFlowCellKaizenLayout) ||
          Boolean((card as any)?.vsmFlowCellProjectedCycleSeconds);
        const flowCellId = String((card as any)?.vsmFlowCellId ?? '').trim();
        let temAcao = false;

        if (flowCellId) {
          const current = flowCellGroups.get(flowCellId) ?? { anchor: card, hasSolda: false, hasMontagem: false, count: 0 };
          flowCellGroups.set(flowCellId, {
            anchor: current.anchor,
            hasSolda: current.hasSolda || referencesSolda,
            hasMontagem: current.hasMontagem || referencesMontagem,
            count: current.count + 1,
          });
        }

        if (projection?.improvementType === 'setup' || includesAny(titleText, ['smed', 'setup'])) {
          drafts.push(buildDraft('smed', card.id, cardName, card.id));
          temAcao = true;
        }

        if (
          isUnifiedCell ||
          includesAny(titleText, ['celula', 'célula', 'flow cell', 'fluxo continuo', 'fluxo contínuo']) ||
          (referencesSolda && referencesMontagem)
        ) {
          hasCellMacro = true;
          cellMacroAnchor = cellMacroAnchor ?? card;
          temAcao = true;
        }

        if (icon === 'supermercado' || convertedToIcon === 'supermercado') {
          supermarkets.push(card);
          temAcao = true;
        }

        if (icon === 'pulmao' || convertedToIcon === 'pulmao') {
          hasBufferIntermediate = true;
          bufferAnchor = bufferAnchor ?? card;
          temAcao = true;
        }

        if (icon === 'nivelamento' || rootIcon === 'nivelamento' || includesAny(titleText, ['heijunka', 'oxox', 'nivelamento'])) {
          hasHeijunkaBox = true;
          heijunkaAnchor = heijunkaAnchor ?? card;
          temAcao = true;
        }

        if (isUnifiedCell && (referencesSolda || referencesMontagem || includesAny(titleText, ['celula', 'célula']))) {
          hasCellMacro = true;
          cellMacroAnchor = cellMacroAnchor ?? card;
          temAcao = true;
        }

        if (import.meta.env.DEV) {
          const nodeType = (card as any)?.vsmIcon || card.tipo;
          console.log(
            '🔍 [VSM Factory] Componente Lido: ' + nodeType + ' | ID: ' + card.id + ' | Gerou Ação: ' + temAcao,
          );
        }
      });

      flowCellGroups.forEach((group) => {
        if (!hasCellMacro && (group.count >= 2 || (group.hasSolda && group.hasMontagem))) {
          hasCellMacro = true;
          cellMacroAnchor = group.anchor;
        }
      });

      if (hasCellMacro) {
        drafts.push(
          buildMacroDraft(
            'vsm_cell_macro',
            'Desenhar Trabalho Padronizado para a nova Célula de Fluxo Contínuo',
            getCardName(cellMacroAnchor),
            cellMacroAnchor?.id,
          ),
        );
      }

      if (hasHeijunkaBox) {
        drafts.push(
          buildMacroDraft(
            'vsm_heijunka_box',
            'Instalar e parametrizar o Quadro Físico/Visual Heijunka (OXOX)',
            getCardName(heijunkaAnchor),
            heijunkaAnchor?.id,
          ),
        );
      }

      if (hasBufferIntermediate) {
        drafts.push(
          buildMacroDraft(
            'vsm_buffer_intermediate',
            'Estabelecer nível de controle do Pulmão Intermediário (Buffer)',
            getCardName(bufferAnchor),
            bufferAnchor?.id,
          ),
        );
      }

      const sortedSupermarkets = [...supermarkets].sort((left, right) => (left.posicao?.x ?? 0) - (right.posicao?.x ?? 0));
      const supermarketEntrada = sortedSupermarkets.find((card) =>
        includesAny(`${card.id} ${getCardName(card)}`, ['entrada', 'input', 'estamparia']),
      ) ?? sortedSupermarkets[0];
      const supermarketSaida = sortedSupermarkets.find((card) =>
        includesAny(`${card.id} ${getCardName(card)}`, ['saida', 'saída', 'output', 'expedicao', 'expedição', 'cliente']),
      ) ?? (sortedSupermarkets.length > 1 ? sortedSupermarkets[sortedSupermarkets.length - 1] : undefined);

      if (supermarketEntrada) {
        drafts.push(
          buildMacroDraft(
            'vsm_supermarket_input',
            'Dimensionar e demarcar fisicamente o Supermercado de Puxada',
            getCardName(supermarketEntrada),
            supermarketEntrada.id,
          ),
        );
      }

      if (supermarketSaida && supermarketSaida.id !== supermarketEntrada?.id) {
        drafts.push(
          buildMacroDraft(
            'vsm_supermarket_output',
            'Dimensionar e demarcar fisicamente o Supermercado de Puxada',
            getCardName(supermarketSaida),
            supermarketSaida.id,
          ),
        );
      }

      return drafts;
    };

    const uniqueDrafts = Array.from(
      new Map(generateVsmActions(generationCards).map((draft) => [draft.sourceKey, draft])).values(),
    ).filter((draft) => draft.title.trim());

    if (uniqueDrafts.length === 0) {
      showToast('warning', 'Nenhuma ideia de melhoria da Otimização encontrada para converter em ações.');
      return;
    }

    const ensureImplementationProject = async () => {
      if (!user?.id) {
        showToast('error', 'Usuário não identificado. Não foi possível criar o projeto de implementação.');
        return null;
      }

      const projectName = mapName.trim() || `VSM - ${clientName}`;
      const legacyProjectName = `Implementação VSM - ${clientName}`;
      const candidateProjects = [
        ...(await ProjectService.list(orgId, { search: projectName })),
        ...(await ProjectService.list(orgId, { search: legacyProjectName })),
      ];
      const existingProject = candidateProjects.find(project => {
        const normalized = project.name.trim().toLowerCase();
        return normalized === projectName.toLowerCase() || normalized === legacyProjectName.toLowerCase();
      });
      const project = existingProject ?? await ProjectService.create(orgId, user.id, {
        name: projectName,
        description: `Contêiner criado automaticamente para executar as ações do mapa VSM "${mapName}".`,
        status: 'active',
        visibility_scope: 'organization',
      });
      return project.id;
    };

    if (activeProjectId) {
      const enterpriseProject = await ProjectService.getById(orgId, activeProjectId).catch(() => null);
      if (!enterpriseProject) activeProjectId = null;
    }

    if (!activeProjectId) {
      activeProjectId = await ensureImplementationProject();
      if (!activeProjectId) return;
      setCurrentProjectId(activeProjectId);
    }

    const planName = `Plano de Ação - ${mapName}`;
    const existingPlans = activeProjectId
      ? await ProjectPlanService.listPlans(orgId, activeProjectId)
      : await ProjectPlanService.listStandalonePlans(orgId);
    let actionPlan = existingPlans.find(plan => plan.type === 'action' && plan.name.trim().toLowerCase() === planName.trim().toLowerCase()) ?? null;
    if (!actionPlan) {
      actionPlan = await ProjectPlanService.createPlan(orgId, {
        project_id: activeProjectId,
        name: planName,
        type: 'action',
        description: `Plano gerado automaticamente a partir das melhorias da etapa Otimização do mapa "${mapName}".`,
      });
    }

    const existingByProject = activeProjectId ? await ActionService.list(orgId, { project_id: activeProjectId }) : [];
    const isVsmAutoAction = (action: EnterpriseAction): boolean => {
      const metadata = action.metadata ?? {};
      const generatedFrom = String((metadata as any).generatedFrom ?? '').toLowerCase();
      const source = String((metadata as any).source ?? '').toLowerCase();
      const sourceComponentId = String((metadata as any).sourceComponentId ?? '').trim();
      const origin = String((metadata as any).origin ?? '').toLowerCase();
      return (
        origin === 'vsm' ||
        source.includes('vsm') ||
        generatedFrom.includes('vsm') ||
        sourceComponentId.length > 0
      );
    };

    const generatedSourceComponentIds = new Set(
      uniqueDrafts
        .map((draft) => String(draft.metadata.sourceComponentId ?? '').trim())
        .filter(Boolean),
    );

    const staleAutoActions = existingByProject.filter((action) => {
      if (!isVsmAutoAction(action)) return false;
      const sourceComponentId = String((action.metadata as any)?.sourceComponentId ?? '').trim();
      if (!sourceComponentId) return true;
      return !generatedSourceComponentIds.has(sourceComponentId);
    });
    if (staleAutoActions.length > 0) {
      await Promise.all(staleAutoActions.map((action) => ActionService.delete(action.id)));
    }

    const refreshedByProject = activeProjectId ? await ActionService.list(orgId, { project_id: activeProjectId }) : [];
    const existingByPlan = refreshedByProject.filter((action) => action.plan_id === actionPlan.id);
    const existingComponentIds = new Set(
      refreshedByProject
        .map((action) => String((action.metadata as any)?.sourceComponentId ?? '').trim())
        .filter(Boolean),
    );
    const toCreate = uniqueDrafts.filter(draft =>
      !existingComponentIds.has(String(draft.metadata.sourceComponentId ?? '').trim())
    );

    let created = 0;
    let createdInPlan = 0;
    const createdActions: EnterpriseAction[] = [];
    for (const draft of toCreate) {
      const result = await ActionService.create({
        organization_id: orgId,
        project_id: activeProjectId ?? undefined,
        plan_id: actionPlan.id,
        title: draft.title,
        description: draft.description,
        status: 'pending',
        priority: draft.priority,
        created_by: user?.id,
        effort: draft.effort,
        impact: draft.impact,
        metadata: {
          ...draft.metadata,
          mapId: activeProjectId,
          linkedNodeId: draft.linkedNodeId ?? null,
          generatedFrom: 'vsm_dynamic_generation',
          origin: 'VSM',
        },
      });
      if (result) {
        created++;
        if (result.plan_id === actionPlan.id) createdInPlan++;
        createdActions.push(result);
      }
    }

    if (toCreate.length > 0 && created === 0) {
      showToast('error', 'Não foi possível gravar as ações. Verifique as permissões do projeto ou as migrações da tabela actions.');
      return;
    }

    const skipped = uniqueDrafts.length - toCreate.length;
    const localPlanActions = Array.from(new Map([...refreshedByProject, ...createdActions].map(action => [action.id, action])).values())
      .map((action): Action => {
        const effort = action.effort ?? undefined;
        const impact = action.impact ?? undefined;
        const linkedNodeId = typeof action.metadata?.linkedNodeId === 'string' ? action.metadata.linkedNodeId : undefined;
        const sourceComponentId = typeof action.metadata?.sourceComponentId === 'string' ? action.metadata.sourceComponentId : undefined;
        return {
          id: action.id,
          title: action.title,
          description: action.description ?? undefined,
          status: action.status === 'completed' ? 'done' : action.status === 'in_progress' ? 'doing' : action.status === 'cancelled' ? 'canceled' : 'todo',
          projectId: action.project_id ?? activeProjectId ?? undefined,
          actionPlanId: action.plan_id ?? actionPlan.id,
          origin: 'canvas',
          linkedNodeId,
          sourceComponentId,
          responsible: action.assigned_name ?? undefined,
          responsavel: action.assigned_name ?? undefined,
          responsibleUserId: action.assigned_to ?? undefined,
          deadline: action.due_date ?? undefined,
          prazo: action.due_date ?? undefined,
          effort,
          impact,
          esforco: effort && effort >= 1 && effort <= 3 ? effort as 1 | 2 | 3 : undefined,
          impacto: impact && impact >= 1 && impact <= 3 ? impact as 1 | 2 | 3 : undefined,
          originSource: linkedNodeId ? 'Canvas' : 'Manual',
          createdAt: action.created_at,
          updatedAt: action.updated_at,
        };
      });
    if (localPlanActions.length > 0) {
      setActions(localPlanActions);
    }

    const msg = skipped > 0
      ? `Ideias de melhoria convertidas em ${created} ação(ões). ${skipped} já existia(m).`
      : `Ideias de melhoria convertidas em ${created} ação(ões).`;
    showToast('success', msg);
    void queryClient.invalidateQueries({ queryKey: ['step5-evidences-actions', orgId, activeProjectId] });
    void queryClient.invalidateQueries({ queryKey: ['projects'] });
    void queryClient.invalidateQueries({ queryKey: ['project-plan-summaries'] });

    const planScopedActionCount = existingByPlan.length + createdInPlan;
    const shouldOpenProjectActions = Boolean(activeProjectId) && planScopedActionCount === 0;
    navigate(shouldOpenProjectActions ? `/acoes?project_id=${activeProjectId}` : `/acoes?planId=${actionPlan.id}`);
  }, [cards, getCards, getCardsWithSimulation, navigate, organization, postIts, queryClient, resolvedProjectId, selectedProject, setActions, setCurrentProjectId, showToast, user?.id, user?.organizationId, user?.organizationName, user?.organization_id, viewMode, zustandCurrentProjectId]);

  const handleNewMap = useCallback(() => {
    // Clear canvas before showing modal so backdrop shows empty grid
    setCards([]);
    setPostIts([]);
    setActions([]);
    setShowNewMapTypeModal(true);
  }, [setCards, setPostIts, setActions]);

  const handleConfirmNewMap = useCallback((type: 'OFFICE' | 'FACTORY') => {
    setShowNewMapTypeModal(false);
    setMapType(type);
    setPostIts([]);
    setActions([]);
    if (type === 'OFFICE') {
      const startCard: CardData = {
        id: generateId(),
        tipo: 'card_inicial',
        atividade: 'Início',
        status: 'ativo',
        posicao: { x: 200, y: 300 },
        conexoes_saida: [],
        layoutType: currentLayoutRef.current,
        layoutColor: currentColorRef.current,
      };
      const endCard: CardData = {
        id: generateId(),
        tipo: 'card_final',
        atividade: 'Fim',
        status: 'ativo',
        posicao: { x: 800, y: 300 },
        conexoes_saida: [],
        layoutType: currentLayoutRef.current,
        layoutColor: currentColorRef.current,
      };
      setCards([startCard, endCard]);
    } else {
      // VSM/Factory: start with completely empty canvas
      setCards([]);
    }
  }, [setCards, setPostIts, setActions, setMapType]);

  const handleSaveLocal = useCallback(async (): Promise<boolean> => {
    const projectId = selectedProject?.id ?? resolvedProjectId;
    if (!projectId) {
      showToast('warning', 'Nenhum projeto selecionado para salvar.');
      return false;
    }
    try {
      const state = useProjectStore.getState();
      const storeCards = state.viewMode === 'kaizen_work' ? state.getCardsWithSimulation() : state.getCards();
      const currentCards = storeCards.length > 0 ? storeCards : activeCards;
      const activeSheetId = state.activeSheetId ?? state.sheets[0]?.id ?? generateId();
      const currentSheets = state.sheets.length
        ? state.sheets.map(sheet =>
            sheet.id === activeSheetId
              ? { ...sheet, cards: currentCards }
              : sheet
          )
        : [{ id: activeSheetId, name: 'MAPA 1', cards: currentCards }];
      const baseProject = selectedProject ?? {
        id: projectId,
        nome_projeto: state.currentMapDisplayName ?? 'Projeto sem nome',
        data_criacao: new Date().toISOString(),
        ultimo_salvamento: new Date().toISOString(),
        estado_atual: undefined,
        estado_futuro: null,
        activeState: 'atual',
      };
      const projectData = {
        ...baseProject,
        id: projectId,
        nome_projeto: baseProject.nome_projeto ?? state.currentMapDisplayName ?? 'Projeto sem nome',
        ...selectedProject,
        estado_atual: {
          ...(baseProject.estado_atual ?? {}),
          sheets: currentSheets,
          activeSheetId,
          cards: currentCards,
          postIts,
          actions,
          mapType: state.mapType,
          stage: state.stage,
          completedStages: state.completedStages,
          viewMode: state.viewMode,
          isKaizenMode: state.isKaizenMode,
          globalDemand: state.globalDemand,
          workTime: state.workTime,
          taktTime: state.taktTime,
          factoryWorkDays: state.factoryWorkDays,
          modeSnapshots: state._modeSnapshots,
        },
        globalDemand: state.globalDemand,
        workTime: state.workTime,
        taktTime: state.taktTime,
        optimizationSession: optimizationSession ?? (selectedProject as any)?.optimizationSession ?? null,
        optimizationLayer,
        ultimo_salvamento: new Date().toISOString(),
      } as Project;
      await saveProjectToDisk(projectData);
      const nextProjects = ctxProjects.some(item => item.id === projectData.id)
        ? ctxProjects.map(item => item.id === projectData.id ? projectData : item)
        : [...ctxProjects, projectData];
      setProjects(nextProjects);
      setSelectedProject(projectData);
      try {
        localStorage.setItem('lean_workflow_projects', JSON.stringify(nextProjects));
      } catch {
        // localStorage pode falhar em ambientes restritos; o arquivo .wf já foi gerado.
      }
      showToast('success', 'Projeto salvo localmente.');
      return true;
    } catch (error) {
      console.error('Erro ao salvar:', error);
      showToast('error', 'Erro ao salvar o projeto localmente.');
      return false;
    }
  }, [actions, activeCards, ctxProjects, optimizationLayer, optimizationSession, postIts, resolvedProjectId, selectedProject, setProjects, setSelectedProject, showToast]);

  const handleOpenLocal = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileInputChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const project = await loadProjectFromDisk(file);
      let persistedProject: Project | undefined = ctxProjects.find(item => item.id === project.id) as Project | undefined;
      if (!persistedProject) {
        persistedProject = useProjectStore.getState().projects.find(item => item.id === project.id) as Project | undefined;
      }
      if (!persistedProject) {
        try {
          const localProjects = JSON.parse(localStorage.getItem('lean_workflow_projects') ?? '[]') as Project[];
          persistedProject = localProjects.find(item => item.id === project.id);
        } catch {
          persistedProject = undefined;
        }
      }
      const currentStoreState = useProjectStore.getState();
      const canRecoverFromOpenProject = currentStoreState.currentProjectId === project.id && currentStoreState.sheets.length > 0;
      const recoveredState = canRecoverFromOpenProject
        ? {
            ...(project.estado_atual ?? persistedProject?.estado_atual ?? {}),
            sheets: currentStoreState.sheets,
            activeSheetId: currentStoreState.activeSheetId ?? currentStoreState.sheets[0]?.id,
            cards: currentStoreState.sheets.find(sheet => sheet.id === currentStoreState.activeSheetId)?.cards ?? currentStoreState.sheets[0]?.cards ?? [],
            postIts: currentStoreState.postIts,
            actions: currentStoreState.actions,
            mapType: currentStoreState.mapType,
            globalDemand: currentStoreState.globalDemand,
            workTime: currentStoreState.workTime,
            taktTime: currentStoreState.taktTime,
          }
        : undefined;
      const loadedState = project.estado_atual ?? persistedProject?.estado_atual ?? recoveredState ?? ({} as NonNullable<Project['estado_atual']>);
      const loadedCardCount = (loadedState.sheets ?? []).reduce((total, sheet) => total + (sheet.cards?.length ?? 0), 0) + (loadedState.cards?.length ?? 0);
      const loadedPostItCount = loadedState.postIts?.length ?? 0;
      const canLoadMap = loadedCardCount > 0 || loadedPostItCount > 0;
      if (!canLoadMap) {
        showToast('warning', 'Arquivo .wf sem mapa salvo. O mapa atual foi mantido.');
        return;
      }
      const fallbackSheetId = loadedState.activeSheetId || generateId();
      const hasMapContent = canLoadMap;
      const loadedSheets = loadedState.sheets?.length
        ? loadedState.sheets
        : loadedState.cards?.length
        ? [{ id: fallbackSheetId, name: 'MAPA 1', cards: loadedState.cards }]
        : [{ id: fallbackSheetId, name: 'MAPA 1', cards: [] }];
      const activeLoadedSheetId = loadedState.activeSheetId && loadedSheets.some(sheet => sheet.id === loadedState.activeSheetId)
        ? loadedState.activeSheetId
        : loadedSheets[0]?.id;
      const inferredMapType = loadedState.mapType
        ?? (/factory/i.test(file.name) || /factory/i.test(project.nome_projeto ?? '') ? 'FACTORY' : useProjectStore.getState().mapType);

      // Carregar sheets (formato atual) — tem precedência sobre cards legados
      {
        const { setSheets, setMapType } = useProjectStore.getState();
        setSheets(loadedSheets, activeLoadedSheetId);
        setMapType(inferredMapType);
        const demand = Number((loadedState as any).globalDemand ?? (project as any).globalDemand);
        const workTime = Number((loadedState as any).workTime ?? (project as any).workTime);
        if (demand && workTime) {
          useProjectStore.setState({
            globalDemand: demand,
            workTime,
            taktTime: Number((loadedState as any).taktTime ?? (project as any).taktTime) || workTime / demand,
          });
        }
      }

      setPostIts(loadedState.postIts ?? []);
      setActions(loadedState.actions ?? []);
      setOptimizationLayer((project as any).optimizationLayer ?? []);

      useProjectStore.setState({
        currentProjectId: project.id,
        stage: loadedState.stage ?? useProjectStore.getState().stage,
        completedStages: loadedState.completedStages ?? [],
        viewMode: loadedState.viewMode ?? 'current_clean',
        isKaizenMode: Boolean(loadedState.isKaizenMode),
        _modeSnapshots: (loadedState as any).modeSnapshots ?? (loadedState as any)._modeSnapshots ?? useProjectStore.getState()._modeSnapshots,
        factoryWorkDays: Number((loadedState as any).factoryWorkDays) || useProjectStore.getState().factoryWorkDays,
      } as any);
      setCurrentProjectId(project.id);

      const hydratedProject = {
        ...project,
        estado_atual: {
          ...loadedState,
          sheets: loadedSheets,
          activeSheetId: activeLoadedSheetId,
          cards: loadedSheets.find(sheet => sheet.id === activeLoadedSheetId)?.cards ?? loadedState.cards ?? [],
          mapType: inferredMapType,
          postIts: loadedState.postIts ?? [],
          actions: loadedState.actions ?? [],
        },
      } as Project;
      const nextProjects = ctxProjects.some(item => item.id === hydratedProject.id)
        ? ctxProjects.map(item => item.id === hydratedProject.id ? hydratedProject : item)
        : [...ctxProjects, hydratedProject];
      setProjects(nextProjects);
      setSelectedProject(hydratedProject);
      try {
        localStorage.setItem('lean_workflow_projects', JSON.stringify(nextProjects));
      } catch {
        // localStorage pode falhar em ambientes restritos; o store já foi hidratado.
      }

      // Sincronizar nome do projeto com o contexto global
      if (project.nome_projeto && selectedProject?.id) {
        updateProject(selectedProject.id, { nome_projeto: project.nome_projeto });
      }
      // Fallback: mesmo sem projeto selecionado no contexto, atualiza o display name no store
      if (project.nome_projeto) {
        useProjectStore.getState().setCurrentMapDisplayName(project.nome_projeto);
      }
      showToast(hasMapContent ? 'success' : 'warning', hasMapContent ? 'Projeto carregado.' : 'Projeto carregado sem mapa salvo. Uma aba vazia foi criada.');
    } catch (error) {
      console.error('Erro ao abrir arquivo:', error);
      alert(error instanceof Error ? error.message : 'Erro ao abrir o arquivo.');
    }
    // Reset input para permitir reabrir o mesmo arquivo
    e.target.value = '';
  }, [ctxProjects, selectedProject?.id, setActions, setCurrentProjectId, setOptimizationLayer, setPostIts, setProjects, setSelectedProject, showToast, updateProject]);

  const handleImportExcel = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      try {
        const importedCards = await importFromExcel(file);
        if (importedCards.length > 0) {
          setCards(importedCards);
          recalculateFlowVolumes();
        }
      } catch (error) {
        console.error('Erro ao importar Excel:', error);
        alert('Erro ao importar arquivo Excel.');
      }
    };
    input.click();
  }, [setCards, recalculateFlowVolumes]);

  const handleDownloadTemplate = useCallback(async () => {
    try {
      await downloadTemplate(mapType);
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      alert('Erro ao gerar template.');
    }
  }, [mapType]);

  const handleExportPDF = useCallback(async () => {
    // Use #canvas-content directly so getFullContentDimensions strips its translate3d+scale transform
    const el = canvasRef.current?.querySelector('#canvas-content') as HTMLElement;
    if (!el) return;
    try {
      const modeSuffix = mapType === 'FACTORY' ? ' [Factory]' : ' [Office]';
      const name = (selectedProject?.nome_projeto || 'Workflow') + modeSuffix;
      await exportService.toPDF(el, name);
    } catch (error) {
      console.error('Erro ao exportar PDF:', error);
      alert('Erro ao exportar PDF.');
    }
  }, [selectedProject]);

  const handleExportImage = useCallback(async () => {
    // Use #canvas-content directly so getFullContentDimensions strips its translate3d+scale transform
    const el = canvasRef.current?.querySelector('#canvas-content') as HTMLElement;
    if (!el) return;
    try {
      const name = selectedProject?.nome_projeto || 'Workflow';
      await exportService.toImage(el, name);
    } catch (error) {
      console.error('Erro ao exportar imagem:', error);
      alert('Erro ao exportar imagem.');
    }
  }, [selectedProject]);

  const handleExportExcel = useCallback(async () => {
    try {
      const currentCards = getCards();
      const name = selectedProject?.nome_projeto || 'Workflow';
      await exportService.toExcel(currentCards, name);
    } catch (error) {
      console.error('Erro ao exportar Excel:', error);
      alert('Erro ao exportar Excel.');
    }
  }, [getCards, selectedProject]);

  const handleExportA3 = useCallback(async () => {
    const el = canvasRef.current?.querySelector('#canvas-content') as HTMLElement;
    if (!el) return;
    try {
      const name = selectedProject?.nome_projeto || 'Workflow';
      await exportCanvasToA3(el, name, metrics, postIts);
    } catch (error) {
      console.error('Erro ao exportar A3:', error);
      alert('Erro ao exportar relatório A3.');
    }
  }, [selectedProject, metrics, postIts]);

  const handleExportEnterpriseReport = useCallback(async () => {
    const el = canvasRef.current?.querySelector('#canvas-content') as HTMLElement;
    if (!el) return;
    try {
      const currentCards = getCards();
      const name = selectedProject?.nome_projeto || 'Workflow';
      await exportService.toEnterpriseReport(el, { name }, currentCards, metrics);
    } catch (error) {
      console.error('Erro ao exportar relatório Enterprise:', error);
      alert('Erro ao exportar relatório Enterprise.');
    }
  }, [getCards, selectedProject, metrics]);

  const handleExportLeanReport = useCallback(async () => {
    try {
      if (!selectedProject) {
        alert('Nenhum projeto selecionado.');
        return;
      }
      await generateLeanReport(selectedProject as any, postIts);
    } catch (error) {
      console.error('Erro ao gerar relatório Lean:', error);
      alert('Erro ao gerar relatório Lean.');
    }
  }, [selectedProject, postIts]);

  const handleOpenReportStudio = useCallback(() => {
    const projectName = selectedProject?.nome_projeto || 'Mapa de Fluxo';
    const today = new Date().toLocaleDateString('pt-BR');
    const actCards = activeActivityCards;
    openReport({
      title: projectName,
      subtitle: `Relatório de Fluxo de Valor · ${today}`,
      organizationName: organization?.name,
      blocks: [
        { id: 'h1', type: 'subheading', content: 'Diagnóstico do Fluxo' },
        { id: 'p1', type: 'paragraph', content: 'Observações do consultor sobre o estado atual do mapa...' },
        { id: 'div1', type: 'divider' },
        { id: 'h2', type: 'subheading', content: 'Métricas do Mapa' },
        { id: 'p2', type: 'paragraph', content: `Atividades: ${actCards.length} · Lead Time total: ${metrics?.totalLeadTime ?? '—'}` },
        { id: 'div2', type: 'divider' },
        { id: 'h3', type: 'subheading', content: 'Problemas e Oportunidades' },
        { id: 'p3', type: 'paragraph', content: `Problemas identificados: ${postIts.length}` },
        { id: 'div3', type: 'divider' },
        { id: 'h4', type: 'subheading', content: 'Recomendações' },
        { id: 'p4', type: 'richtext', content: 'Insira aqui as recomendações de melhoria...' },
      ],
    });
  }, [openReport, selectedProject, activeActivityCards, metrics, postIts, organization]);

  const handleSaveCloud = useCallback(async () => {
    const projectId = selectedProject?.id ?? resolvedProjectId;
    console.log('[handleSaveCloud] projectId:', projectId, 'linkedBlueprintId:', linkedBlueprintId, 'zustandBlueprintId:', zustandBlueprintId, 'orgId:', user?.organizationId);
    if (!projectId && !linkedBlueprintId) {
      if (!selectedProject) {
        showToast('warning', 'Nenhum projeto selecionado para salvar.');
        return;
      }
      await handleSaveLocal();
      return;
    }

    try {
      const currentCards = getCards();
      const isOptimizationSave = viewMode === 'kaizen_work';

      if (isOptimizationSave) {
        await handleSaveLocal();
        return;
      }

      // Save cards to blueprint generated_map_data (primary persistence for blueprint-originated maps)
      if (linkedBlueprintId && user?.organizationId && !isOptimizationSave) {
        const cardsForBlueprint = currentCards.map(c => ({
          id: c.id,
          tipo: c.tipo,
          atividade: c.atividade,
          nome_fluxo: c.nome_fluxo,
          posicao: c.posicao,
          tp: c.tp,
          lt: c.lt,
          tt: c.tt,
          traUnit: c.traUnit,
          entrada: c.entrada,
          sector: c.sector || (c as any).setor,
          responsavel: c.responsavel || (c as any).quem,
          pessoasSimultaneas: c.pessoasSimultaneas,
          resources: (c as any).resources,
          quem: (c as any).quem,
          executionConfig: c.executionConfig,
        }));
        // Build connections from card conexoes_saida
        const connections: { sourceId: string; targetId: string }[] = [];
        for (const card of currentCards) {
          for (const conn of (card.conexoes_saida || [])) {
            connections.push({ sourceId: card.id, targetId: conn.targetId });
          }
        }
        await supabase
          .from('process_blueprints')
          .update({
            generated_map_data: { cards: cardsForBlueprint, connections },
          })
          .eq('id', linkedBlueprintId)
          .eq('organization_id', user.organizationId);
      }

      // Sync executionConfig from cards to workflow stages (if blueprint linked)
      if (linkedBlueprintId && user?.organizationId && !isOptimizationSave) {
        try {
          await syncCardConfigToStages(currentCards, linkedBlueprintId, user.organizationId);
          console.log('[MapaPage] Stage sync completed successfully');
        } catch (err) {
          console.warn('[MapaPage] Stage sync failed:', err);
        }
      }

      showToast('success', 'Projeto salvo na nuvem.');
    } catch (error) {
      console.error('Erro ao salvar na nuvem:', error);
      showToast('error', 'Erro ao salvar na nuvem.');
    }
  }, [selectedProject, resolvedProjectId, user, getCards, viewMode, postIts, actions, handleSaveLocal, showToast, linkedBlueprintId]);

  // Mapeamento de stage para texto de exibição
  const stageLabel = {
    'diagnosis': 'Etapa 1: Diagnóstico',
    'kaizen': 'Etapa 2: Oportunidades',
    'planning': 'Etapa 3: Planejamento',
    'execution': 'Etapa 4: Execução',
    'sustainment': 'Etapa 5: Sustentação'
  }[stage] || 'Etapa 1: Diagnóstico';

  const executiveSimulation = useMemo(() => simulateFutureState(), [simulateFutureState, cards, postIts, actions]);
  const strategyMetrics = useMemo(
    () =>
      mapStrategyEngine.getMetrics({
        cards: activeCards,
        postIts,
        actions,
        longestPathIds: metrics?.longestPathIds,
        bottleneckId: metrics?.realBottleneck?.id ?? null,
        futureCards: executiveSimulation.futureCards,
        simulationImprovements: executiveSimulation.improvements,
      }),
    [
      activeCards,
      postIts,
      actions,
      metrics?.longestPathIds,
      metrics?.realBottleneck?.id,
      executiveSimulation.futureCards,
      executiveSimulation.improvements,
    ],
  );
  const executiveStageSummaries = strategyMetrics.stageSummaries;
  const executiveFocusIds = useMemo(
    () => executiveStageSummaries.map((item) => item.cardId),
    [executiveStageSummaries],
  );
  const selectedExecutiveStage = executiveStageSummaries.find((item) => item.cardId === selectedCardId) ?? null;
  const executiveCriticalCardId = strategyMetrics.criticalCardId;
  const executivePendingActionCardIds = strategyMetrics.pendingActionCardIds;
  const executiveWasteCardIds = strategyMetrics.wasteCardIds;
  const comparisonAvailable = hasFutureMap || strategyMetrics.comparisonAvailable;

  const mapRibbon = useMemo(() => (
    <Ribbon
      contentInsetLeft={alignedRulerInset}
      goHome={handleGoHome}
      mapType={mapType}
      vsmDesignStyle={vsmDesignStyle}
      onVsmDesignStyleChange={setVsmDesignStyle}
      vsmCardColor={vsmCardColor}
      onVsmCardColorChange={setVsmCardColor}
      onNewMap={handleNewMap}
      onSave={handleSaveCloud}
      onSaveLocal={handleSaveLocal}
      onOpenLocal={handleOpenLocal}
      onImportExcel={handleImportExcel}
      onDownloadTemplate={handleDownloadTemplate}
      onExportPDF={handleExportPDF}
      onExportEnterpriseReport={handleExportEnterpriseReport}
      onExportExcel={handleExportExcel}
      onExportImage={handleExportImage}
      onExportA3={handleExportA3}
      onExportLeanReport={handleExportLeanReport}
      onOpenReportStudio={handleOpenReportStudio}
      onAddActivity={handleAddActivity}
      onAddDecision={handleAddDecision}
      onAddPostIt={handleAddPostIt}
      onDeleteSelected={handleDeleteSelected}
      onToggleGrid={handleToggleGrid}
      gridActive={showGrid}
      snapActive={snapToGridEnabled}
      onToggleSnap={handleToggleSnap}
      onToggleRulers={handleToggleRulers}
      rulersActive={showRulers}
      onToggleHeatmap={handleToggleHeatmap}
      heatmapActive={heatmapMode}
      onToggleLiveMode={handleToggleLiveMode}
      liveModeActive={liveMode}
      onShowComparePanel={() => setRightPanelMode(prev => prev === 'compare' ? 'none' : 'compare')}
      comparePanelActive={rightPanelMode === 'compare'}
      onTogglePageMode={handleTogglePageMode}
      pageModeActive={pageMode.enabled}
      pageFormat={pageMode.format}
      onContentScaleChange={setContentScale}
      contentScale={contentScale}
      onResetZoom={handleResetZoom}
      onZoomIn={handleZoomIn}
      onZoomOut={handleZoomOut}
      currentZoomLabel={`${Math.round(scale * 100)}%`}
      onTogglePanMode={handleTogglePanMode}
      panModeActive={panModeActive}
      onToggleMinimap={() => setShowMinimap(prev => !prev)}
      minimapActive={showMinimap}
      onLayoutChange={handleLayoutChange}
      currentLayout={currentLayout}
      onColorChange={handleColorChange}
      currentColor={currentColor}
      onVisualSettings={handleOpenVisualSettings}
      onProjectSettings={handleOpenProjectSettings}
      onOpenFactorySettings={() => setShowFactorySettingsModal(true)}
      onOrgManager={handleOpenOrgManager}
      onToggleManualArrow={handleToggleManualArrow}
      manualArrowMode={manualArrowMode}
      onToggleAnalysisMode={handleToggleAnalysisMode}
      analysisModeActive={analysisMode}
      selectedCardIds={selectedCardIds}
      selectedCardId={selectedCardId}
      onShowDashboard={() => setRightPanelMode(prev => prev === 'dashboard' ? 'none' : 'dashboard')}
      onShowKaizen={() => setRightPanelMode(prev => prev === 'kaizen' ? 'none' : 'kaizen')}
      onShowYamazumi={() => setRightPanelMode(prev => prev === 'yamazumi' ? 'none' : 'yamazumi')}
      onShowProperties={() => setRightPanelMode(prev => prev === 'properties' ? 'none' : 'properties')}
      onToggleSimulation={handleToggleSimulationPanel}
      onToggleExecutiveView={() => {
        setExecutiveMode((prev) => {
          if (prev) setShowExecutiveComparison(false);
          return !prev;
        });
      }}
      executiveViewActive={executiveMode}
      onCompareScenarios={() => setShowExecutiveComparison((prev) => !prev)}
      comparisonAvailable={comparisonAvailable}
      onToggleWidget={() => setRightPanelMode(prev => prev === 'dashboard' ? 'none' : 'dashboard')}
      widgetActive={rightPanelMode === 'dashboard'}
      yamazumiActive={rightPanelMode === 'yamazumi'}
      simulationActive={isSimulationMode}
      projectMode="full"
      currentProjectStep={stageLabel}
      currentViewMode={viewMode}
      isJourneySidebarOpen={showJourneySidebar}
      onToggleJourneySidebar={() => {
        const opening = !showJourneySidebar;
        setShowJourneySidebar(opening);
        if (opening) {
          setJourneyPanelExpanded(true); // sempre abre expandido
          setRightPanelMode('none'); // exclusão mútua com painéis direitos
        }
      }}
      consultingMode={consultingMode}
      onToggleConsultingMode={() => {
        setConsultingMode(!consultingMode);
        if (consultingMode) setShowJourneySidebar(false);
      }}
      journeyDeviationAlert={journeyDeviationAlert}
      onUndo={canUndo ? undo : undefined}
      onRedo={canRedo ? redo : undefined}
      onToggleSwimlanes={() => {
        if (swimLanes.length > 0) {
          setSwimLanesVisible(prev => !prev);
        } else {
          showToast('Primeiro aplique raias selecionando cards e usando "Organizar > Converter em Raias"', 'info');
        }
      }}
      swimlanesActive={swimLanesVisible && swimLanes.length > 0}
      cards={cards}
      linkedBlueprintId={linkedBlueprintId}
      onSearch={handleSearch}
      searchResultCount={searchResults.length}
      searchResultIndex={searchIndex}
      onSearchNext={handleSearchNext}
      onSearchPrev={handleSearchPrev}
      onSearchClear={handleSearchClear}
      readOnly={!canEdit}
    />
  ), [
    canEdit,
    handleGoHome,
    handleNewMap,
    handleSaveCloud,
    handleSaveLocal,
    handleOpenLocal,
    handleImportExcel,
    handleDownloadTemplate,
    handleExportPDF,
    handleExportEnterpriseReport,
    handleExportExcel,
    handleExportImage,
    handleExportA3,
    handleExportLeanReport,
    handleAddActivity,
    handleAddDecision,
    handleAddPostIt,
    handleDeleteSelected,
    handleToggleGrid,
    snapToGridEnabled,
    handleToggleSnap,
    handleToggleRulers,
    showRulers,
    handleToggleHeatmap,
    heatmapMode,
    handleTogglePageMode,
    pageMode.enabled,
    pageMode.format,
    setContentScale,
    contentScale,
    handleResetZoom,
    handleSearch,
    handleSearchNext,
    handleSearchPrev,
    handleSearchClear,
    computeSearchMatches,
    searchResults,
    searchIndex,
    searchGlowPhase,
    handleTogglePanMode,
    panModeActive,
    handleLayoutChange,
    currentLayout,
    handleColorChange,
    currentColor,
    handleOpenVisualSettings,
    handleOpenProjectSettings,
    handleOpenOrgManager,
    handleToggleManualArrow,
    manualArrowMode,
    handleToggleAnalysisMode,
    analysisMode,
    selectedCardIds,
    selectedCardId,
    stageLabel,
    viewMode,
    showJourneySidebar,
    handleToggleSimulationPanel,
    isSimulationMode,
    rightPanelMode,
    executiveMode,
    comparisonAvailable,
    alignedRulerInset,
    undo,
    redo,
    canUndo,
    canRedo,
    showRulers,
    swimLanes,
    swimLanesVisible,
    cards,
  ]);

  // ============================================================================
  // RENDER
  // ============================================================================
  if (isLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const selectedCard = activeCards.find((card) => card.id === selectedCardId) ?? null;
  const showSimulationSidebar = isSimulationMode;
  const showExecutivePanel = executiveMode && !showSimulationSidebar && selectedExecutiveStage !== null;
  const isYamazumiWorkspace = rightPanelMode === 'yamazumi' && !showSimulationSidebar && !showExecutivePanel;
  const isYamazumiFocusActive = isYamazumiWorkspace && yamazumiFocusMode;
  const journeyHudEnabled = !isYamazumiFocusActive;
  
  const showDockedRightPanel =
    !executiveMode &&
    !showSimulationSidebar &&
    rightPanelMode !== 'none' &&
    rightPanelMode !== 'compare' &&
    !(rightPanelMode === 'dashboard' && dashboardMode === 'fullscreen');
    
  const isCanvasEmpty = activeCards.length === 0 && visiblePostIts.length === 0;

  useEffect(() => {
    if (rightPanelMode === 'yamazumi') {
      setRightPanelExpanded(true);
    } else {
      setRightPanelExpanded(false);
    }
  }, [rightPanelMode]);

  useEffect(() => {
    if (!isYamazumiWorkspace) {
      const snapshot = yamazumiUiSnapshotRef.current;
      if (snapshot) {
        setShowRulers(snapshot.showRulers);
        setShowJourneySidebar(snapshot.showJourneySidebar);
        yamazumiUiSnapshotRef.current = null;
      }
      setYamazumiFocusMode(false);
      return;
    }

    if (!yamazumiUiSnapshotRef.current) {
      yamazumiUiSnapshotRef.current = { showRulers, showJourneySidebar };
    }

    setYamazumiFocusMode(true);
    setShowRulers(false);
    setShowJourneySidebar(false);
  }, [isYamazumiWorkspace]);

  const handleToggleYamazumiFocusMode = useCallback(() => {
    if (!isYamazumiWorkspace) return;

    if (!yamazumiUiSnapshotRef.current) {
      yamazumiUiSnapshotRef.current = { showRulers, showJourneySidebar };
    }

    if (yamazumiFocusMode) {
      const snapshot = yamazumiUiSnapshotRef.current;
      if (snapshot) {
        setShowRulers(snapshot.showRulers);
        setShowJourneySidebar(snapshot.showJourneySidebar);
      }
      setYamazumiFocusMode(false);
      return;
    }

    setShowRulers(false);
    setShowJourneySidebar(false);
    setYamazumiFocusMode(true);
  }, [isYamazumiWorkspace, yamazumiFocusMode, showRulers, showJourneySidebar]);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('yamazumi-focus-mode-change', { detail: { active: isYamazumiFocusActive } }));
    return () => {
      window.dispatchEvent(new CustomEvent('yamazumi-focus-mode-change', { detail: { active: false } }));
    };
  }, [isYamazumiFocusActive]);
  const blueprintRawStatus = blueprintHeaderSnapshot?.status ?? selectedProject?.status ?? 'draft';
  const blueprintIsStandard = blueprintHeaderSnapshot?.isCorporateStandard ?? false;
  const _isDbAuthoritative = blueprintIsStandard || ['published', 'standard', 'archived'].includes(blueprintRawStatus);
  const blueprintEffectiveStatus: VsmStatusValue = _isDbAuthoritative
    ? (blueprintRawStatus as VsmStatusValue)
    : mode === 'FUTURO' ? 'future'
    : mode === 'KAIZEN' ? 'kaizen'
    : 'current';
  const headerStageLabel = formatStageHeaderLabel(stageLabel);
  const headerStageBadge = headerStageLabel.replace(' - ', ' | ');
  const headerBadgeTone =
    'border-slate-300/75 bg-slate-100/88 text-slate-600 dark:border-slate-700/78 dark:bg-slate-900/82 dark:text-slate-300';
  const mapDisplayName = selectedProject?.nome_projeto || selectedProject?.name || 'Mapa VSM';
  // V27.0: Título editável inline
  const [editingTitle, setEditingTitle] = React.useState(false);
  const [titleDraft, setTitleDraft] = React.useState('');
  const titleInputRef = React.useRef<HTMLInputElement>(null);

  const handleTitleCommit = React.useCallback(async () => {
    const trimmed = titleDraft.trim();
    if (!trimmed || !selectedProject?.id || trimmed === mapDisplayName) {
      setEditingTitle(false);
      return;
    }
    updateProject(selectedProject.id, { nome_projeto: trimmed });
    setEditingTitle(false);
    // Persist to Supabase
    await supabase.from('projects').update({ nome_projeto: trimmed }).eq('id', selectedProject.id);
  }, [titleDraft, selectedProject?.id, mapDisplayName, updateProject]);

  const headerMeta = useMemo(
    () => (
      <div className="flex min-w-0 items-center gap-1.5">
        {editingTitle ? (
          <input
            ref={titleInputRef}
            value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={handleTitleCommit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleTitleCommit(); if (e.key === 'Escape') setEditingTitle(false); }}
            className="min-w-0 max-w-[200px] rounded border border-[var(--border-focus)] bg-[var(--bg-elevated)] px-1.5 py-0 text-[12px] font-medium text-[var(--text-primary)] outline-none"
            style={{ height: 20 }}
          />
        ) : (
          <span
            className="truncate text-[12px] font-medium text-[var(--text-primary)] cursor-text hover:underline hover:underline-offset-2"
            title="Clique para renomear"
            onClick={() => { setTitleDraft(mapDisplayName); setEditingTitle(true); setTimeout(() => titleInputRef.current?.select(), 10); }}
          >
            {mapDisplayName}
          </span>
        )}
        <span className={`inline-flex shrink-0 items-center rounded-[3px] border px-1.5 py-[2px] text-[10px] font-medium tracking-[0.03em] ${headerBadgeTone}`}>
          {headerStageBadge}
        </span>
        <VsmStatusBadge
          status={blueprintEffectiveStatus}
          isStandard={blueprintIsStandard}
          canEdit={!!linkedBlueprintId}
          onChangeStatus={handleSetBlueprintStatus}
        />
        {/* V28.0 — Toggle Blueprint / Sketch movido para menu Design */}
      </div>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, blueprintRawStatus, blueprintIsStandard, linkedBlueprintId, handleSetBlueprintStatus, headerBadgeTone, headerStageBadge, mapDisplayName, zustandCurrentProjectId, editingTitle, titleDraft, handleTitleCommit, mapType, vsmDesignStyle, setVsmDesignStyle],
  );
  const canvasViewportStyle = {
    background: 'var(--bg-elevated)',
    overflow: 'hidden' as const,
    boxSizing: 'border-box' as const,
    border: 'none',
    margin: 0,
    padding: 0,
    zIndex: 10,
    boxShadow: 'none',
  };
  // Keep header visible whenever consultingMode is active (unless in Yamazumi focus)
  const metricsHudRightMargin = showJourneySidebar ? (journeyPanelExpanded ? 320 : 240) : 0;
  const journeyPanelHud = (
    <JourneyPanel
      visible={journeyHudEnabled}
      expanded={journeyPanelExpanded}
      showDeviationAlert={journeyDeviationAlert}
      onGenerateActionPlan={handleGenerateActionPlan}
      onToggle={() => setJourneyPanelExpanded(prev => !prev)}
    />
  );
  const topCenterMetricsHud = null;

  return (
    <ModuleLayout ribbon={mapRibbon} headerMeta={headerMeta}>
      <SimulationProvider>
      <div
        className="page-container w-full h-full relative flex flex-col overflow-hidden"
        style={{ height: isYamazumiFocusActive ? 'calc(100vh - var(--shell-header-height, 32px) - var(--shell-statusbar-height, 22px))' : '100%' }}
      >
      {/* Hidden file input for "Abrir Local" (.wf) */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".wf"
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
      />
      <div className={`w-full flex-1 min-h-0 flex flex-row overflow-hidden ${isSimulationMode ? 'ring-2 ring-amber-500/20 ring-inset' : ''}`}>
        {!isYamazumiFocusActive && (
        <div
          className="shrink-0 border-r"
          style={{
            width: executiveMode ? '0px' : `${TOOL_RAIL_WIDTH}px`,
            pointerEvents: 'auto',
            opacity: 1,
            overflow: 'hidden',
            transition: 'opacity 0.2s ease, width 0.2s ease, background-color 0.15s ease',
            boxShadow: 'none',
            borderColor: 'var(--shell-divider)',
            backgroundColor: isDark ? 'var(--officebar-bg)' : '#f8fafc',
            color: isDark ? 'var(--ribbon-text-primary)' : '#1e293b',
          }}
        >
          <OfficeSidebar
            mapType={mapType}
            onMapTypeChange={setMapType}
            onAddActivity={handleAddActivity}
            onAddDecision={handleAddDecision}
            onAddStart={handleAddStart}
            onAddFinalCard={handleAddFinalCard}
            onAddInventory={handleAddInventory}
            onAddPostIt={handleAddPostIt}
            selectedCardId={selectedCardId}
            engineeringTool={engineeringTool}
            onEngineeringToolChange={setEngineeringTool}
            onConsolidateFuture={() => setConsolidateFutureNonce(value => value + 1)}
          />
        </div>
        )}
        <div className="flex-1 min-w-0 min-h-0 h-full flex flex-col relative overflow-hidden">
      {/* WORKSPACE CONTAINER - 100% width, elementos ajustam posição internamente */}
      <div 
        className="w-full flex-1 min-h-0 relative overflow-hidden isolate" 
        style={{ 
          boxSizing: 'border-box',
          paddingTop: isYamazumiFocusActive ? '0px' : '8px',
          margin: 0,
          zIndex: 1,
          backgroundColor: canvasBackgroundColor,
          boxShadow: 'inset 0 1px 0 rgba(15, 23, 42, 0.04)',
        }}
      >
          <CanvasModeBorderOverlay inset={alignedRulerInset} />
          {showRulers ? (
            <>              {/* CORNER BLOCK - Interseção das réguas (24x24px) */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: '0',
                  width: `${RULER_SIZE}px`,
                  height: `${RULER_SIZE}px`,
                  backgroundColor: rulerBg,
                  boxSizing: 'border-box',
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                  margin: 0,
                  padding: 0,
                  zIndex: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {/* Ícone de origem sutil */}
                <div 
                  style={{ 
                    width: '4px', 
                    height: '4px', 
                    borderRadius: '50%', 
                    backgroundColor: 'var(--text-muted)' 
                  }} 
                />
              </div>
              
              {/* HORIZONTAL RULER - position: absolute; top: 0; left: SIDEBAR + RULER_SIZE */}
              <div 
                style={{
                  position: 'absolute',
                  top: 0,
                  left: `${alignedRulerInset}px`,
                  right: 0,
                  height: `${RULER_SIZE}px`,
                  backgroundColor: rulerBg,
                  boxSizing: 'border-box',
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                  margin: 0,
                  padding: 0,
                  zIndex: 30
                }}
              >
                <CanvasRuler
                  orientation="horizontal"
                  scale={scale}
                  offset={pan.x}
                  length={viewportSize.width}
                />
              </div>

              {/* VERTICAL RULER - position: absolute; top: RULER_SIZE; left: SIDEBAR */}
              <div 
                style={{
                  position: 'absolute',
                  top: `${alignedRulerInset}px`,
                  left: '0',
                  bottom: 0,
                  width: `${RULER_SIZE}px`,
                  backgroundColor: rulerBg,
                  boxSizing: 'border-box',
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                  margin: 0,
                  padding: 0,
                  zIndex: 30
                }}
              >
                <CanvasRuler
                  orientation="vertical"
                  scale={scale}
                  offset={pan.y}
                  length={viewportSize.height}
                />
              </div>

              {/* CANVAS VIEWPORT - position: absolute; top: RULER_SIZE; left: SIDEBAR + RULER_SIZE; bottom: 0 */}
              <div 
                ref={viewportRef}
                style={{
                  position: 'absolute',
                  top: `${alignedRulerInset}px`,
                  left: `${alignedRulerInset}px`,
                  right: 0,
                  bottom: 0,
                  ...canvasViewportStyle,
                }}
              >
        {isSimulationMode && (
          <div className="absolute left-2 top-2 z-[70] flex items-center gap-2 border border-amber-500/40 bg-amber-50/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 shadow-sm dark:bg-amber-500/15 dark:text-amber-200">
            <span>MODO SIMULAÇÃO ATIVO</span>
            <button
              type="button"
              onClick={handleExitSimulationAndReset}
              className="pointer-events-auto border border-amber-600/50 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 hover:bg-amber-100 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-amber-500/20"
              title="Sair da simulação e resetar filtros"
            >
              Sair e Resetar
            </button>
          </div>
        )}
        {isCanvasEmpty && <CanvasEmptyState />}
        {executiveMode && showExecutiveComparison && comparisonAvailable && (
          <div className="absolute right-4 top-4 z-[65] w-[420px] max-w-[calc(100%-2rem)] border border-slate-300/90 bg-white/95 shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-950/92">
            <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-800">
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Overlay Executivo</div>
                <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">Comparativo Atual vs Futuro</div>
              </div>
              <button
                type="button"
                onClick={() => setShowExecutiveComparison(false)}
                className="border border-slate-300 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900"
              >
                Fechar
              </button>
            </div>
            <div className="grid grid-cols-3 gap-px bg-slate-200 dark:bg-slate-800">
              <div className="bg-white px-3 py-2 dark:bg-slate-950">
                <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Redução total</div>
                <div className="mt-1 text-[16px] font-bold text-emerald-700 dark:text-emerald-300">
                  {executiveSimulation.improvements.leadTimeReduction.toFixed(1)}%
                </div>
              </div>
              <div className="bg-white px-3 py-2 dark:bg-slate-950">
                <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Impacto consolidado</div>
                <div className="mt-1 text-[16px] font-bold text-slate-900 dark:text-slate-100">
                  {formatCompactCurrency(executiveSimulation.improvements.annualSaving)}
                </div>
              </div>
              <div className="bg-white px-3 py-2 dark:bg-slate-950">
                <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Ganho de eficiência</div>
                <div className="mt-1 text-[16px] font-bold text-blue-700 dark:text-blue-300">
                  {executiveSimulation.improvements.efficiencyGain.toFixed(1)} pts
                </div>
              </div>
            </div>
            <div className="max-h-[240px] overflow-auto">
              <table className="w-full text-[11px]">
                <thead className="sticky top-0 bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">Etapa</th>
                    <th className="px-3 py-2 text-right font-semibold">Redução</th>
                    <th className="px-3 py-2 text-right font-semibold">Impacto</th>
                  </tr>
                </thead>
                <tbody>
                  {executiveStageSummaries.map((item) => (
                    <tr key={`compare-rulers-${item.cardId}`} className="border-t border-slate-200 dark:border-slate-800">
                      <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{item.label}</td>
                      <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-300">
                        {item.projectedReduction > 0 ? formatCompactDuration(item.projectedReduction) : '-'}
                      </td>
                      <td className="px-3 py-2 text-right font-mono text-slate-700 dark:text-slate-200">
                        {item.financialImpact > 0 ? formatCompactCurrency(item.financialImpact) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* SNAP GUIDES - Linhas guias temporárias estilo Figma (ocultas em FACTORY) */}
        {mapType !== 'FACTORY' && snapGuides.x !== undefined && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none z-50"
            style={{
              left: `${snapGuides.x * scale + pan.x}px`,
              width: '1px',
              backgroundColor: '#3b82f6',
              boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)',
            }}
          />
        )}
        {mapType !== 'FACTORY' && snapGuides.y !== undefined && (
          <div
            className="absolute left-0 right-0 pointer-events-none z-50"
            style={{
              top: `${snapGuides.y * scale + pan.y}px`,
              height: '1px',
              backgroundColor: '#3b82f6',
              boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)',
            }}
          />
        )}
        
        {isYamazumiWorkspace ? (
          <div className="relative h-full overflow-hidden bg-[var(--bg-workspace)]">
            <div
              className="pointer-events-none absolute inset-0 opacity-60"
              style={{
                backgroundImage: 'linear-gradient(rgba(148,163,184,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.10) 1px, transparent 1px)',
                backgroundSize: '24px 24px',
              }}
            />
            <div className="relative z-10 h-full flex flex-col">
              {yamazumiViewMode === 'real' && (
                <KPIBar data={yamazumiKPIBarData} isLoading={yamazumiRealLoading} />
              )}
              <div className="min-h-0 flex-1">
              <YamazumiSimulator
                cards={cards}
                assignments={yamazumiAssignments}
                baseAssignments={yamazumiBaseAssignments}
                onAssignmentsChange={setYamazumiAssignments}
                shiftMinutes={yamazumiShiftMinutes}
                fteByRole={yamazumiFteByRole}
                focusMode={yamazumiFocusMode}
                onToggleFocusMode={handleToggleYamazumiFocusMode}
                showApplyButton={false}
                saveBaselineToken={yamazumiSaveBaselineToken}
                viewMode={yamazumiViewMode}
                onViewModeChange={setYamazumiViewMode}
                realLoadByRole={yamazumiRealLoadByRole}
                realKpis={yamazumiRealKpis}
                zoom={yamazumiZoom}
                onZoomChange={setYamazumiZoom}
                demandaPercent={yamazumiDemandaPercent}
                onDemandaPercentChange={setYamazumiDemandaPercent}
              />
              </div>
            </div>
          </div>
        ) : (
          <>
            <VsmCanvas
              activeCards={activeCards}
              postIts={visiblePostIts}
              readOnly={isCurrentSnapshotReadOnly}
              scale={scale}
              setScale={setScale}
              pan={pan}
              setPan={setPan}
              selectedPostItIds={selectedPostItIds}
              setSelectedPostItIds={setSelectedPostItIds}
              selectedCardIds={selectedCardIds}
              setSelectedCardIds={setSelectedCardIds}
              highlightedPath={highlightedPath}
              analysisMode={analysisMode}
              storeMetrics={metrics}
              isCreatingPostIt={isCreatingPostIt}
              creatingPostItType={creatingPostItType}
              manualArrowMode={manualArrowMode}
              manualArrowSource={manualArrowSource}
              manualArrowWaypoints={manualArrowWaypoints}
              connectingSource={connectingSource}
              selectedConnection={selectedConnection}
              boxSelection={boxSelection}
              setBoxSelection={setBoxSelection}
              processedConnections={processedConnections}
              handleCardSelect={handleCardSelect}
              onClearSelection={handleClearSelection}
              handleDeleteTotal={handleDeleteTotal}
              handleMarkX={handleMarkX}
              handleDuplicateCard={handleDuplicateCard}
              handleAddNext={handleAddNext}
              handleMove={handleMove}
              handleCardDragStart={handleCardDragStart}
              handleCardDragEnd={handleCardDragEnd}
              handleStartConnection={handleStartConnection}
              handleFinishConnection={handleFinishConnection}
              handleCreatePostItOnCard={handleCreatePostItOnCard}
              handleUpdateCardData={handleUpdateCardData}
              handlePostItClick={handlePostItClick}
              handlePostItStartDrag={handlePostItStartDrag}
              handlePostItDrag={handlePostItDrag}
              handlePostItEndDrag={handlePostItEndDrag}
              handleCancelDrag={handleCancelDrag}
              handlePostItTextChange={handlePostItTextChange}
              handleTogglePostItMinimize={handleTogglePostItMinimize}
              openContextMenu={openContextMenu}
              handleOpenInMemo={handleOpenInMemo}
              getActionsByNode={getActionsByNode}
              setRightPanelMode={handleRightPanelModeChange}
              setSelectedConnection={setSelectedConnection}
              setEditingPostItId={setEditingPostItId}
              canvasRef={canvasRef}
              currentLayout={effectiveLayout}
              draggingPostIt={draggingPostIt}
              postItSnapTargetId={postItSnapTargetId}
              editingPostItId={editingPostItId}
              linkingPostItId={linkingPostItId}
              setPostItSnapTargetId={setPostItSnapTargetId}
              heatmapMode={heatmapMode}
              showGrid={showGrid && gridType !== 'none'}
              gridType={gridType}
              snapToGridEnabled={snapToGridEnabled}
              pageMode={pageMode}
              setManualArrowWaypoints={setManualArrowWaypoints}
              setManualArrowMode={setManualArrowMode}
              setManualArrowSource={setManualArrowSource}
              onCancelManualArrowCreation={handleCancelManualArrowCreation}
              contentScale={contentScale}
              pageConnectors={pageConnectors}
              onUpdateConnectorPosition={onUpdateConnectorPosition}
              onUpdateConnectorLabel={onUpdateConnectorLabel}
              onDeleteConnector={onDeleteConnector}
              onDropCardOnTarget={onDropCardOnTarget}
              onDropCardOnCanvas={onDropCardOnCanvas}
              onDropCardBeforeTarget={onDropCardBeforeTarget}
              onModifyConnection={handleModifyConnection}
              gridColor={gridColor}
              gridIntensity={18}
              canvasBackgroundColor={canvasBackgroundColor}
              canvasGridColor={gridColor}
              defaultArrowColor={arrowColor}
              executiveMode={executiveMode}
              executiveFocusIds={executiveFocusIds}
              executiveStageSummaries={executiveStageSummaries}
              executiveBottleneckId={executiveCriticalCardId}
              swimLanes={swimLanes}
              swimLanesVisible={swimLanesVisible}
              onSwimlanesApplied={handleSwimlanesApplied}
              liveMetricsMap={liveMode ? liveMetricsMap : undefined}
              searchHighlightId={searchHighlightId}
              searchGlowPhase={searchGlowPhase}
              engineeringTool={engineeringTool}
              onEngineeringToolChange={setEngineeringTool}
              consolidateFutureNonce={consolidateFutureNonce}
              canvasSvgChildren={undefined}
            />
          </>
        )}

              </div>{/* /viewportRef */}
            </>
          ) : (
            /* SEM RÉGUAS - Canvas ocupa todo o espaço exceto BottomBar */
            <div 
              ref={viewportRef}
              className="absolute overflow-hidden"
              style={{ 
                top: 0,
                left: '0',
                right: 0,
                bottom: 0,
                ...canvasViewportStyle,
              }}
            >
              {isSimulationMode && (
                <div className="absolute left-2 top-2 z-[70] flex items-center gap-2 border border-amber-500/40 bg-amber-50/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700 shadow-sm dark:bg-amber-500/15 dark:text-amber-200">
                  <span>MODO SIMULAÇÃO ATIVO</span>
                  <button
                    type="button"
                    onClick={handleExitSimulationAndReset}
                    className="pointer-events-auto border border-amber-600/50 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700 hover:bg-amber-100 dark:bg-slate-900 dark:text-amber-200 dark:hover:bg-amber-500/20"
                    title="Sair da simulação e resetar filtros"
                  >
                    Sair e Resetar
                  </button>
                </div>
              )}
              {isCanvasEmpty && <CanvasEmptyState />}
              {executiveMode && showExecutiveComparison && comparisonAvailable && (
                <div className="absolute right-4 top-4 z-[65] w-[420px] max-w-[calc(100%-2rem)] border border-slate-300/90 bg-white/95 shadow-xl backdrop-blur-sm dark:border-slate-700 dark:bg-slate-950/92">
                  <div className="flex items-center justify-between border-b border-slate-200 px-3 py-2 dark:border-slate-800">
                    <div>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Overlay Executivo</div>
                      <div className="text-[12px] font-semibold text-slate-800 dark:text-slate-100">Comparativo Atual vs Futuro</div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowExecutiveComparison(false)}
                      className="border border-slate-300 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-600 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-900"
                    >
                      Fechar
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-slate-200 dark:bg-slate-800">
                    <div className="bg-white px-3 py-2 dark:bg-slate-950">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Redução total</div>
                      <div className="mt-1 text-[16px] font-bold text-emerald-700 dark:text-emerald-300">
                        {executiveSimulation.improvements.leadTimeReduction.toFixed(1)}%
                      </div>
                    </div>
                    <div className="bg-white px-3 py-2 dark:bg-slate-950">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Impacto consolidado</div>
                      <div className="mt-1 text-[16px] font-bold text-slate-900 dark:text-slate-100">
                        {formatCompactCurrency(executiveSimulation.improvements.annualSaving)}
                      </div>
                    </div>
                    <div className="bg-white px-3 py-2 dark:bg-slate-950">
                      <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Ganho de eficiência</div>
                      <div className="mt-1 text-[16px] font-bold text-blue-700 dark:text-blue-300">
                        {executiveSimulation.improvements.efficiencyGain.toFixed(1)} pts
                      </div>
                    </div>
                  </div>
                  <div className="max-h-[240px] overflow-auto">
                    <table className="w-full text-[11px]">
                      <thead className="sticky top-0 bg-slate-100 text-slate-600 dark:bg-slate-900 dark:text-slate-300">
                        <tr>
                          <th className="px-3 py-2 text-left font-semibold">Etapa</th>
                          <th className="px-3 py-2 text-right font-semibold">Redução</th>
                          <th className="px-3 py-2 text-right font-semibold">Impacto</th>
                        </tr>
                      </thead>
                      <tbody>
                        {executiveStageSummaries.map((item) => (
                          <tr key={`compare-flat-${item.cardId}`} className="border-t border-slate-200 dark:border-slate-800">
                            <td className="px-3 py-2 text-slate-700 dark:text-slate-200">{item.label}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700 dark:text-emerald-300">
                              {item.projectedReduction > 0 ? formatCompactDuration(item.projectedReduction) : '-'}
                            </td>
                            <td className="px-3 py-2 text-right font-mono text-slate-700 dark:text-slate-200">
                              {item.financialImpact > 0 ? formatCompactCurrency(item.financialImpact) : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
              {/* SNAP GUIDES (ocultas em FACTORY) */}
              {mapType !== 'FACTORY' && snapGuides.x !== undefined && (
                <div
                  className="absolute top-0 bottom-0 pointer-events-none z-50"
                  style={{
                    left: `${snapGuides.x * scale + pan.x}px`,
                    width: '1px',
                    backgroundColor: '#3b82f6',
                    boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)',
                  }}
                />
              )}
              {mapType !== 'FACTORY' && snapGuides.y !== undefined && (
                <div
                  className="absolute left-0 right-0 pointer-events-none z-50"
                  style={{
                    top: `${snapGuides.y * scale + pan.y}px`,
                    height: '1px',
                    backgroundColor: '#3b82f6',
                    boxShadow: '0 0 4px rgba(59, 130, 246, 0.5)',
                  }}
                />
              )}
              
              {isYamazumiWorkspace ? (
                <div className="relative h-full overflow-hidden bg-[var(--bg-workspace)]">
                  <div
                    className="pointer-events-none absolute inset-0 opacity-60"
                    style={{
                      backgroundImage: 'linear-gradient(rgba(148,163,184,0.10) 1px, transparent 1px), linear-gradient(90deg, rgba(148,163,184,0.10) 1px, transparent 1px)',
                      backgroundSize: '24px 24px',
                    }}
                  />
                  <div className="relative z-10 h-full flex flex-col">
                    {yamazumiViewMode === 'real' && (
                      <KPIBar data={yamazumiKPIBarData} isLoading={yamazumiRealLoading} />
                    )}
                    <div className="min-h-0 flex-1">
                    <YamazumiSimulator
                      cards={cards}
                      assignments={yamazumiAssignments}
                      baseAssignments={yamazumiBaseAssignments}
                      onAssignmentsChange={setYamazumiAssignments}
                      shiftMinutes={yamazumiShiftMinutes}
                      fteByRole={yamazumiFteByRole}
                      focusMode={yamazumiFocusMode}
                      onToggleFocusMode={handleToggleYamazumiFocusMode}
                      showApplyButton={false}
                      saveBaselineToken={yamazumiSaveBaselineToken}
                      viewMode={yamazumiViewMode}
                      onViewModeChange={setYamazumiViewMode}
                      realLoadByRole={yamazumiRealLoadByRole}
                      realKpis={yamazumiRealKpis}
                      zoom={yamazumiZoom}
                      onZoomChange={setYamazumiZoom}
                      demandaPercent={yamazumiDemandaPercent}
                      onDemandaPercentChange={setYamazumiDemandaPercent}
                    />
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <VsmCanvas
                    activeCards={activeCards}
                    postIts={visiblePostIts}
                    readOnly={isCurrentSnapshotReadOnly}
                    scale={scale}
                    setScale={setScale}
                    pan={pan}
                    setPan={setPan}
                    selectedPostItIds={selectedPostItIds}
                    setSelectedPostItIds={setSelectedPostItIds}
                    selectedCardIds={selectedCardIds}
                    setSelectedCardIds={setSelectedCardIds}
                    highlightedPath={highlightedPath}
                    analysisMode={analysisMode}
                    storeMetrics={metrics}
                    isCreatingPostIt={isCreatingPostIt}
                    creatingPostItType={creatingPostItType}
                    manualArrowMode={manualArrowMode}
                    manualArrowSource={manualArrowSource}
                    manualArrowWaypoints={manualArrowWaypoints}
                    connectingSource={connectingSource}
                    selectedConnection={selectedConnection}
                    boxSelection={boxSelection}
                    setBoxSelection={setBoxSelection}
                    processedConnections={processedConnections}
                    handleCardSelect={handleCardSelect}
                    onClearSelection={handleClearSelection}
                    handleDeleteTotal={handleDeleteTotal}
                    handleMarkX={handleMarkX}
                    handleDuplicateCard={handleDuplicateCard}
                    handleAddNext={handleAddNext}
                    handleMove={handleMove}
                    handleCardDragStart={handleCardDragStart}
                    handleCardDragEnd={handleCardDragEnd}
                    handleStartConnection={handleStartConnection}
                    handleFinishConnection={handleFinishConnection}
                    handleCreatePostItOnCard={handleCreatePostItOnCard}
                    handleUpdateCardData={handleUpdateCardData}
                    handlePostItClick={handlePostItClick}
                    handlePostItStartDrag={handlePostItStartDrag}
                    handlePostItDrag={handlePostItDrag}
                    handlePostItEndDrag={handlePostItEndDrag}
                    handleCancelDrag={handleCancelDrag}
                    handlePostItTextChange={handlePostItTextChange}
                    handleTogglePostItMinimize={handleTogglePostItMinimize}
                    openContextMenu={openContextMenu}
                    handleOpenInMemo={handleOpenInMemo}
                    getActionsByNode={getActionsByNode}
                    setRightPanelMode={handleRightPanelModeChange}
                    setSelectedConnection={setSelectedConnection}
                    setEditingPostItId={setEditingPostItId}
                    canvasRef={canvasRef}
                    currentLayout={effectiveLayout}
                    draggingPostIt={draggingPostIt}
                    postItSnapTargetId={postItSnapTargetId}
                    editingPostItId={editingPostItId}
                    linkingPostItId={linkingPostItId}
                    setPostItSnapTargetId={setPostItSnapTargetId}
                    heatmapMode={heatmapMode}
                    showGrid={showGrid && gridType !== 'none'}
                    gridType={gridType}
                    snapToGridEnabled={snapToGridEnabled}
                    pageMode={pageMode}
                    setManualArrowWaypoints={setManualArrowWaypoints}
                    setManualArrowMode={setManualArrowMode}
                    setManualArrowSource={setManualArrowSource}
                    onCancelManualArrowCreation={handleCancelManualArrowCreation}
                    contentScale={contentScale}
                    pageConnectors={pageConnectors}
                    onUpdateConnectorPosition={onUpdateConnectorPosition}
                    onUpdateConnectorLabel={onUpdateConnectorLabel}
                    onDeleteConnector={onDeleteConnector}
                    onDropCardOnTarget={onDropCardOnTarget}
                    onDropCardOnCanvas={onDropCardOnCanvas}
                    onDropCardBeforeTarget={onDropCardBeforeTarget}
                    onModifyConnection={handleModifyConnection}
                    gridColor={gridColor}
                    gridIntensity={18}
                    canvasBackgroundColor={canvasBackgroundColor}
                    canvasGridColor={gridColor}
                    defaultArrowColor={arrowColor}
                    executiveMode={executiveMode}
                    executiveFocusIds={executiveFocusIds}
                    executiveStageSummaries={executiveStageSummaries}
                    executiveBottleneckId={executiveCriticalCardId}
                    swimLanes={swimLanes}
                    swimLanesVisible={swimLanesVisible}
                    onSwimlanesApplied={handleSwimlanesApplied}
                    liveMetricsMap={liveMode ? liveMetricsMap : undefined}
                    engineeringTool={engineeringTool}
                    onEngineeringToolChange={setEngineeringTool}
                    consolidateFutureNonce={consolidateFutureNonce}
                  />
                </>
              )}

            </div>
          )}
        </div>

        </div>

        <div className={`${showSimulationSidebar ? 'w-80' : showExecutivePanel ? 'w-80' : rightPanelMode === 'compare' ? 'w-80' : showJourneySidebar ? (journeyPanelExpanded ? 'w-80' : 'w-11') : showDockedRightPanel ? (rightPanelMode === 'yamazumi' ? (rightPanelExpanded ? 'w-80' : 'w-[68px]') : (rightPanelExpanded ? 'w-[520px]' : 'w-80')) : 'w-0'} shrink-0 min-h-0 transition-all duration-200 flex flex-col items-stretch overflow-hidden`} style={{ zIndex: 100 }}>
          {showJourneySidebar && !showSimulationSidebar && !showExecutivePanel && journeyPanelHud}
          {rightPanelMode === 'compare' && !showSimulationSidebar && !showExecutivePanel && (
            <MapWorkflowComparePanel
              blueprintId={linkedBlueprintId}
              organizationId={user?.organizationId || ''}
              cards={cards}
              onClose={() => setRightPanelMode('none')}
            />
          )}
          {showSimulationSidebar && (
            <SimulationSidebar
              cards={cards}
              selectedCard={selectedCard}
              metrics={metrics}
              insightSuggestion={simulationSuggestion}
              onClearSelection={handleClearSelection}
              onUpdateCardData={handleUpdateCardData}
              onFocusBottleneck={handleFocusBottleneck}
              onScenarioSaved={(scenarioName) => showToast('success', `${scenarioName} salvo com sucesso.`)}
              onOptimizationFeedback={(kind, message) => showToast(kind, message)}
              onClose={() => {
                handleExitSimulationAndReset();
              }}
            />
          )}
          {showExecutivePanel && selectedExecutiveStage && (
            <aside className="h-full overflow-y-auto border-l border-slate-200 bg-slate-50/96 px-4 py-4 dark:border-slate-800 dark:bg-[var(--bg-surface)]">
              <div className="border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
                <div className="border-b border-slate-200 px-4 py-3 dark:border-slate-800">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:text-slate-400">Painel Estrategico</div>
                  <div className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">{selectedExecutiveStage.label}</div>
                </div>
                <div className="grid grid-cols-2 gap-px bg-slate-200 dark:bg-slate-800">
                  <div className="bg-white px-4 py-3 dark:bg-slate-950">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Tempo acumulado</div>
                    <div className="mt-1 font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">{formatCompactDuration(selectedExecutiveStage.cumulativeLeadTime)}</div>
                  </div>
                  <div className="bg-white px-4 py-3 dark:bg-slate-950">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">% desperdicio</div>
                    <div className="mt-1 font-mono text-lg font-semibold text-amber-700 dark:text-amber-300">{selectedExecutiveStage.wastePercent.toFixed(1)}%</div>
                  </div>
                  <div className="bg-white px-4 py-3 dark:bg-slate-950">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Reducao projetada</div>
                    <div className="mt-1 font-mono text-lg font-semibold text-emerald-700 dark:text-emerald-300">
                      {selectedExecutiveStage.projectedReduction > 0 ? formatCompactDuration(selectedExecutiveStage.projectedReduction) : 'Sem reducao'}
                    </div>
                  </div>
                  <div className="bg-white px-4 py-3 dark:bg-slate-950">
                    <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Impacto financeiro</div>
                    <div className="mt-1 font-mono text-lg font-semibold text-slate-900 dark:text-slate-100">{formatCompactCurrency(selectedExecutiveStage.financialImpact)}</div>
                  </div>
                </div>
                <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Status de acoes vinculadas</div>
                  <div className="mt-2 flex items-center justify-between border border-slate-200 px-3 py-2 text-[11px] dark:border-slate-800">
                    <span className="text-slate-600 dark:text-slate-300">{selectedExecutiveStage.actionStatusLabel}</span>
                    <span className={`font-semibold ${selectedExecutiveStage.pendingActions > 0 ? 'text-amber-700 dark:text-amber-300' : 'text-emerald-700 dark:text-emerald-300'}`}>
                      {selectedExecutiveStage.pendingActions > 0 ? 'Aberto' : 'Resolvido'}
                    </span>
                  </div>
                </div>
                <div className="border-t border-slate-200 px-4 py-3 dark:border-slate-800">
                  <div className="text-[10px] uppercase tracking-[0.12em] text-slate-500">Leitura operacional</div>
                  <div className="mt-2 space-y-2 text-[11px] text-slate-600 dark:text-slate-300">
                    <div className="flex items-center justify-between">
                      <span>Lead time da etapa</span>
                      <span className="font-mono text-slate-900 dark:text-slate-100">{formatCompactDuration(selectedExecutiveStage.leadTime)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>TP medio</span>
                      <span className="font-mono text-slate-900 dark:text-slate-100">{formatCompactDuration(selectedExecutiveStage.leadTime)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>% valor agregado</span>
                      <span className="font-mono text-slate-900 dark:text-slate-100">{selectedExecutiveStage.valueAddedPercent.toFixed(1)}%</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span>Status gargalo</span>
                      <span className={`font-semibold ${selectedExecutiveStage.isBottleneck ? 'text-red-700 dark:text-red-300' : 'text-slate-500 dark:text-slate-400'}`}>
                        {selectedExecutiveStage.isBottleneck ? 'Critico' : 'Normal'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </aside>
          )}
          {showDockedRightPanel && (
            <RightPanel
              docked
              rightPanelMode={rightPanelMode as any}
              dashboardMode={dashboardMode}
              simulationMode="sidebar"
              onClose={handleCloseRightPanel}
              onToggleDashboardMode={handleToggleDashboardMode}
              onToggleSimulationMode={() => {}}
              onTogglePathHighlight={handleTogglePathHighlight}
              highlightedPath={highlightedPath}
              onUpdateCardData={handleUpdateCardData}
              postIts={postIts}
              cards={cards}
              selectedCardId={selectedCardId}
              selectedPostItId={selectedPostItId}
              kaizenFilter={kaizenFilter}
              onKaizenFilterChange={handleKaizenFilterChange}
              isKaizenCollapsed={isKaizenCollapsed}
              onToggleKaizenCollapsed={handleToggleKaizenCollapsed}
              onSelectPostIt={handleSelectPostIt}
              onReorderPostIts={handleReorderPostIts}
              onClearCardFilter={handleClearCardFilter}
              onUpdatePostIt={handleUpdatePostItData}
              onOpenActionsKaizen={handleOpenActionsKaizen}
              onCreateAction={handleCreateActionFromPostIt}
              onOpenActionDetail={handleOpenActionDetail}
              onGenerateFutureMap={handleGenerateFutureMap}
              onClearMapFilters={clearMapFilters}
              dashboardFilterResetToken={dashboardFilterResetToken}
              onSimulateSolution={handleSimulateInsightSolution}
              linkedBlueprintId={linkedBlueprintId}
              metrics={metrics}
              isExpanded={rightPanelExpanded}
              onExpandedChange={setRightPanelExpanded}
              yamazumiAssignments={yamazumiAssignments}
              yamazumiShiftMinutes={yamazumiShiftMinutes}
              yamazumiFteByRole={yamazumiFteByRole}
              yamazumiPendingChanges={yamazumiPendingChanges}
              onYamazumiShiftMinutesChange={setYamazumiShiftMinutes}
              onYamazumiFteChange={(roleKey, nextValue) => setYamazumiFteByRole(prev => ({ ...prev, [roleKey]: nextValue }))}
              onYamazumiSync={handleYamazumiSync}
              onYamazumiAutoBalance={handleYamazumiAutoBalance}
              onYamazumiSaveBaseline={handleYamazumiSaveBaseline}
              yamazumiSavedBaseline={yamazumiHasSavedBaseline}
              onYamazumiSyncToExecution={handleYamazumiSyncToExecution}
              syncingYamazumiToExecution={syncingYamazumiToExecution}
              yamazumiZoom={yamazumiZoom}
              onYamazumiZoomChange={setYamazumiZoom}
              yamazumiDemandaPercent={yamazumiDemandaPercent}
              onYamazumiDemandaPercentChange={setYamazumiDemandaPercent}
              yamazumiSavedBaselineKpis={yamazumiSavedBaselineKpis}
            />
          )}
        </div>
      </div>

      

      {/* LEAD TIME LADDER FOOTER — escada VA/NVA + KPIs fixos no rodapé, não segue pan */}
      {showLeadTimeLadder && (
        <LeadTimeLadderFooter pan={pan} scale={scale} contentScale={contentScale} sidebarWidth={executiveMode ? 0 : TOOL_RAIL_WIDTH} />
      )}

      {showMinimap && !isYamazumiWorkspace && (
        <div
          style={{
            position: 'fixed',
            right: 12,
            bottom: `calc(var(--shell-statusbar-height, 24px) + 12px)`,
            zIndex: 60,
          }}
        >
          <MiniMapHUD
            cards={activeCards}
            pan={pan}
            setPan={setPan}
            scale={scale}
            setScale={setScale}
            canvasRef={canvasRef}
            isDark={isDark}
            width={180}
            height={130}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
          />
        </div>
      )}

      {!executiveMode && !showSimulationSidebar && !showDockedRightPanel && (
        <RightPanel
          rightPanelMode={rightPanelMode as any}
          dashboardMode={dashboardMode}
          simulationMode="sidebar"
          onClose={handleCloseRightPanel}
          onToggleDashboardMode={handleToggleDashboardMode}
          onToggleSimulationMode={() => {}}
          onTogglePathHighlight={handleTogglePathHighlight}
          highlightedPath={highlightedPath}
          onUpdateCardData={handleUpdateCardData}
          postIts={postIts}
          cards={cards}
          selectedCardId={selectedCardId}
          selectedPostItId={selectedPostItId}
          kaizenFilter={kaizenFilter}
          onKaizenFilterChange={handleKaizenFilterChange}
          isKaizenCollapsed={isKaizenCollapsed}
          onToggleKaizenCollapsed={handleToggleKaizenCollapsed}
          onSelectPostIt={handleSelectPostIt}
          onReorderPostIts={handleReorderPostIts}
          onClearCardFilter={handleClearCardFilter}
          onUpdatePostIt={handleUpdatePostItData}
          onOpenActionsKaizen={handleOpenActionsKaizen}
          onCreateAction={handleCreateActionFromPostIt}
          onOpenActionDetail={handleOpenActionDetail}
          onGenerateFutureMap={handleGenerateFutureMap}
          onClearMapFilters={clearMapFilters}
          dashboardFilterResetToken={dashboardFilterResetToken}
          onSimulateSolution={handleSimulateInsightSolution}
          linkedBlueprintId={linkedBlueprintId}
          metrics={metrics}
          yamazumiAssignments={yamazumiAssignments}
          yamazumiShiftMinutes={yamazumiShiftMinutes}
          yamazumiFteByRole={yamazumiFteByRole}
          yamazumiPendingChanges={yamazumiPendingChanges}
          onYamazumiShiftMinutesChange={setYamazumiShiftMinutes}
          onYamazumiFteChange={(roleKey, nextValue) => setYamazumiFteByRole(prev => ({ ...prev, [roleKey]: nextValue }))}
          onYamazumiSync={handleYamazumiSync}
          onYamazumiAutoBalance={handleYamazumiAutoBalance}
          onYamazumiSaveBaseline={handleYamazumiSaveBaseline}
          yamazumiSavedBaseline={yamazumiHasSavedBaseline}
          onYamazumiSyncToExecution={handleYamazumiSyncToExecution}
          syncingYamazumiToExecution={syncingYamazumiToExecution}
          yamazumiZoom={yamazumiZoom}
          onYamazumiZoomChange={setYamazumiZoom}
          yamazumiDemandaPercent={yamazumiDemandaPercent}
          onYamazumiDemandaPercentChange={setYamazumiDemandaPercent}
          yamazumiSavedBaselineKpis={yamazumiSavedBaselineKpis}
        />
      )}

      <VisualSettingsModal
        isOpen={showVisualSettingsModal}
        onClose={() => setShowVisualSettingsModal(false)}
        gridType={gridType}
        onGridTypeChange={setGridType}
        gridColor={gridColor}
        onGridColorChange={setGridColor}
        backgroundColor={canvasBackgroundColor}
        onBackgroundColorChange={setCanvasBackgroundColor}
        arrowColor={arrowColor}
        onArrowColorChange={setArrowColor}
        currentLayout={currentLayout}
        onLayoutChange={handleLayoutChange}
        currentColor={currentColor}
        onColorChange={handleColorChange}
        mapType={mapType}
        vsmDesignStyle={vsmDesignStyle}
        onVsmDesignStyleChange={setVsmDesignStyle}
        vsmCardColor={vsmCardColor}
        onVsmCardColorChange={setVsmCardColor}
      />

      <ProjectSettingsModal
        isOpen={showProjectSettingsModal}
        onClose={() => setShowProjectSettingsModal(false)}
        gridType={gridType}
        onGridTypeChange={setGridType}
        gridColor={gridColor}
        onGridColorChange={setGridColor}
        currentLayout={currentLayout}
        onLayoutChange={handleLayoutChange}
        currentColor={currentColor}
        onColorChange={handleColorChange}
        project={selectedProject}
        onUpdateProject={selectedProject?.id ? (updates) => updateProject(selectedProject.id, updates) : undefined}
      />

      <OrgManager
        isOpen={showOrgManagerModal}
        onClose={() => setShowOrgManagerModal(false)}
      />

      {/* V229: Modal de Par\u00e2metros da F\u00e1brica */}
      <FactorySettingsModal
        isOpen={showFactorySettingsModal}
        onClose={() => setShowFactorySettingsModal(false)}
      />

      {/* New Map Type Selection Modal — V19.9.4 */}
      {showNewMapTypeModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{ backdropFilter: 'blur(8px)', backgroundColor: 'rgba(10,14,20,0.45)' }}
        >
          <div
            className="relative bg-white dark:bg-neutral-900 rounded-2xl w-[500px] p-8 flex flex-col gap-7"
            style={{ border: '1px solid rgba(200,205,215,0.25)', boxShadow: '0 20px 60px rgba(0,0,0,0.18)' }}
          >
            {/* Close button */}
            <button
              className="absolute top-4 right-4 text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
              onClick={() => setShowNewMapTypeModal(false)}
            >
              <X size={16} />
            </button>

            {/* Header */}
            <div className="flex flex-col gap-2">
              <h2 className="text-base font-semibold tracking-tight text-neutral-800 dark:text-neutral-100">
                Selecione o Contexto de Trabalho
              </h2>
              <p className="text-sm text-neutral-400 dark:text-neutral-500 leading-relaxed">
                Defina o tipo de mapeamento para iniciar um novo canvas.
              </p>
            </div>

            {/* Cards */}
            <div className="flex gap-4">
              {/* Administrativo */}
              <button
                className="group flex-1 flex flex-col items-center gap-4 p-6 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-[1px] hover:border-blue-500 hover:shadow-sm active:scale-[0.99] transition-all duration-150 text-left"
                onClick={() => handleConfirmNewMap('OFFICE')}
              >
                <Building2
                  size={28}
                  strokeWidth={1.4}
                  className="text-neutral-500 dark:text-neutral-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                />
                <div className="flex flex-col gap-1.5 items-center text-center">
                  <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">Administrativo</span>
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 leading-relaxed">
                    Mapeamento de processos administrativos, fluxos de escritório e serviços.
                  </span>
                </div>
              </button>

              {/* Produtivo (VSM) */}
              <button
                className="group flex-1 flex flex-col items-center gap-4 p-6 rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 hover:border-[1px] hover:border-blue-500 hover:shadow-sm active:scale-[0.99] transition-all duration-150 text-left"
                onClick={() => handleConfirmNewMap('FACTORY')}
              >
                <Factory
                  size={28}
                  strokeWidth={1.4}
                  className="text-neutral-500 dark:text-neutral-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors"
                />
                <div className="flex flex-col gap-1.5 items-center text-center">
                  <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">Produtivo (VSM)</span>
                  <span className="text-xs text-neutral-400 dark:text-neutral-500 leading-relaxed">
                    Value Stream Mapping para chão de fábrica, manufatura e logística industrial.
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <ToastContainer />

      {/* Post-it Context Menu */}
      {postItContextMenu && (() => {
        const targetPostIt = postIts.find(p => p.id === postItContextMenu.id);
        const typeLabels: Record<string, string> = {
          idea: 'Ideia', problem: 'Problema', action: 'Ação', data: 'Dados', risk: 'Risco'
        };
        const typeColors: Record<string, string> = {
          idea: '#fcd34d', problem: '#fca5a5', action: '#86efac', data: '#93c5fd', risk: '#c4b5fd'
        };
        return (
          <div
            style={{ position: 'fixed', inset: 0, zIndex: 9999 }}
            onClick={() => setPostItContextMenu(null)}
            onContextMenu={(e) => { e.preventDefault(); setPostItContextMenu(null); }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: postItContextMenu.x,
                top: postItContextMenu.y,
                backgroundColor: '#fff',
                borderRadius: '8px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)',
                padding: '4px 0',
                minWidth: '180px',
                fontSize: '12px',
                zIndex: 10000,
              }}
            >
              {/* Editar */}
              <div
                style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => {
                  setEditingPostItId(postItContextMenu.id);
                  setPostItContextMenu(null);
                }}
              >
                <span style={{ width: 16, textAlign: 'center' }}>✏️</span>
                <span>Editar</span>
              </div>

              {/* Alterar Tipo - submenu inline */}
              <div style={{ padding: '4px 12px', color: '#6b7280', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' as const }}>
                Alterar Tipo
              </div>
              {(['idea', 'problem', 'action', 'data', 'risk'] as PostItType[]).map(t => (
                <div
                  key={t}
                  style={{
                    padding: '5px 12px 5px 20px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    fontWeight: targetPostIt?.type === t ? 700 : 400,
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  onClick={() => handleChangePostItType(postItContextMenu.id, t)}
                >
                  <div style={{
                    width: 12, height: 12, borderRadius: 2,
                    backgroundColor: typeColors[t],
                    border: '1px solid rgba(0,0,0,0.15)',
                  }} />
                  <span>{typeLabels[t]}</span>
                  {targetPostIt?.type === t && <span style={{ marginLeft: 'auto', color: '#2563eb' }}>✓</span>}
                </div>
              ))}

              <div style={{ height: 1, backgroundColor: '#e5e7eb', margin: '4px 0' }} />

              {/* Desancorar */}
              {targetPostIt?.fixedToNodeId && (
                <div
                  style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                  onClick={() => handleDetachPostIt(postItContextMenu.id)}
                >
                  <span style={{ width: 16, textAlign: 'center' }}>🔗</span>
                  <span>Desancorar do Card</span>
                </div>
              )}

              {/* Excluir */}
              <div
                style={{ padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', color: '#dc2626' }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#fef2f2')}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                onClick={() => handleDeletePostIt(postItContextMenu.id)}
              >
                <span style={{ width: 16, textAlign: 'center' }}>🗑️</span>
                <span>Excluir</span>
              </div>
            </div>
          </div>
        );
      })()}
      </div>
      </SimulationProvider>
    </ModuleLayout>
  );
};

export const MapaPage: React.FC = () => (
  <WorkspaceModeProvider>
    <MapaPageInner />
  </WorkspaceModeProvider>
);

